import * as i0 from '@angular/core';
import { Injectable, Component, ViewEncapsulation, Pipe, Inject, ViewChild, Directive, Input, EventEmitter, Output, HostListener, NgModule } from '@angular/core';
import VFConfigManager from 'vuframe-web-player/dist/util/vf-config-manager';
import Bugsnag from '@bugsnag/js';
import { BehaviorSubject, Subject, interval, throwError, fromEvent } from 'rxjs';
import * as i1 from '@angular/router';
import { __awaiter } from 'tslib';
import KeenTracking from 'keen-tracking';
import * as MobileDetect from 'mobile-detect';
import * as i2 from '@angular/common/http';
import { HttpHeaders, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { map, catchError, take, switchMap, debounceTime, filter } from 'rxjs/operators';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$2 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i1$4 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Callbacks } from 'vuframe-web-player/dist/util/callbacks';
import * as i3 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i1$3 from '@angular/platform-browser';
import * as i4 from 'ng2-pdfjs-viewer';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import * as i3$1 from '@angular/material/grid-list';
import { MatGridListModule } from '@angular/material/grid-list';
import * as i3$2 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as i1$5 from '@angular/material/snack-bar';
import { MAT_SNACK_BAR_DATA, MatSnackBarModule } from '@angular/material/snack-bar';
import * as shajs from 'sha.js';
import * as i5 from '@angular/forms';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i4$1 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i6 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import VFProgress from 'vuframe-web-player/dist/util/vf-progress';
import { VFWebPlayer } from 'vuframe-web-player';
import Constants from 'vuframe-web-player/dist/util/const';
import { VFTriggerType } from 'vuframe-web-player/dist/interfaces/vf-trigger-meta';
import * as i1$6 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

const libEnv = {
    production: true,
    applicationName: '@vuframe/vf-webviewer',
    version: '1.9.30',
    stage: 'live',
    API_TOKEN: 'Yzg3NDJkMjRlOGU0MmU5ZTg5MDYzMWFhMmY4NWI1NDk2NDVmYTg3YmQxOWQ2Yzk4NmE=',
    API_SECRET: 'OTRlZDI3MmY2YTU3NTJiMmNiM2ZjNGM5ODgyZThiNmNkNTUyMWIwMDhkNzgxZGI0NGU=',
    CLOUDFRONT_CDN: true
};

const ColorHelper = require('color');
/**
 * Adds additional GET params to an URL string
 * @param url the URL string
 * @param params the new params as a key-value mapping
 * @returns the extended URL string
 */
function addURLParams(url, params) {
    const urlObject = new URL(url);
    for (const key of Object.keys(params)) {
        urlObject.searchParams.set(key, params[key]);
    }
    return urlObject.href;
}
/**
 * Attempts to sanitize a string into a six-digit hex color code starting with a hash symbol
 * @param colorString the input string that should be converted
 * @throws error if colorstring is not a valid hex color code
 * @returns the color as a six-digit hex code
 */
function hexColorFromString(colorString) {
    if (colorString === "transparent") {
        return colorString;
    }
    colorString = colorString.toUpperCase();
    // check if colorstring is a valid hex code
    if (new RegExp(/^#?[ABCDEF\d]{6,8}$/).test(colorString)) {
        // ensure leading hash symbol
        return `#${colorString}`.replace("##", "#");
    }
    else {
        throw `${colorString} is not a valid hex color code`;
    }
}
/**
 * Returns the API authentication string for the current application environment.
 * @returns the auth string used for API authentication
 */
function getVuframeAuthHash() {
    return btoa(`${atob(libEnv.API_TOKEN)}:${atob(libEnv.API_SECRET)}`);
}
/**
 * Evaluates usage of cloudfront CDN parameter.
 * @returns 1 or 0 depending on CLOUDFRONT_CDN env variable
 */
function useCloudFrontCDN() {
    return libEnv.CLOUDFRONT_CDN ? 1 : 0;
}
/**
 * Function which checks if the color is dark or white
 */
function colorIsDark(colorString) {
    if (colorString === "transparent") {
        return false;
    }
    if (colorString === undefined) {
        return false;
    }
    const colorHelper = ColorHelper(colorString);
    const r = colorHelper.red();
    const g = colorHelper.green();
    const b = colorHelper.blue();
    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 127.5) {
        return false;
    }
    else {
        return true;
    }
}
/**
 * This functions checks if the current browser is Safari.
 */
function detectSafari() {
    // Get the user-agent string
    const userAgentString = navigator.userAgent;
    // Detect Chrome
    const chromeAgent = userAgentString.indexOf("Chrome") > -1;
    // Detect Safari
    let safariAgent = userAgentString.indexOf("Safari") > -1;
    // Discard Safari since it also matches Chrome
    if ((chromeAgent) && (safariAgent))
        safariAgent = false;
    return safariAgent;
}
const version = libEnv.version;

class Logger {
    static warn(...args) {
        if (VFConfigManager.enableDebug) {
            var args2 = Array.prototype.slice.call(args);
            args2.unshift(Logger.LOG_PREFIX);
            console.warn.apply(console, args2);
        }
    }
    static debug(...args) {
        if (VFConfigManager.enableDebug) {
            var args2 = Array.prototype.slice.call(args);
            args2.unshift(Logger.LOG_PREFIX);
            console.debug.apply(console, args2);
        }
    }
    static info(...args) {
        var args2 = Array.prototype.slice.call(args);
        args2.unshift(Logger.LOG_PREFIX);
        console.log.apply(console, args2);
    }
    static error(...args) {
        var args2 = Array.prototype.slice.call(args);
        args2.unshift(Logger.LOG_PREFIX);
        console.error.apply(console, args2);
    }
    static reportError(error) {
        Logger.error(error);
        Bugsnag.notify(error);
    }
}
Logger.LOG_PREFIX = '[VFViewer]';

var VFViewerMode;
(function (VFViewerMode) {
    VFViewerMode["Standalone"] = "standalone";
    VFViewerMode["Embed"] = "embed";
    VFViewerMode["EmbedToken"] = "embedToken";
})(VFViewerMode || (VFViewerMode = {}));
class BaseViewerConfig {
    /**
     * Returns default config object (necessary to allow overriding in child classes)
     * @returns the default viewer configuration for this component class
     */
    getDefaultConfig() {
        return BaseViewerConfig.DEFAULT_CONFIG;
    }
    /**
     * Sets UI configuration flags depending on the GET params passed to the application
     */
    readConfigParams() {
        this.config = this.getDefaultConfig();
        const params = new URL(window.location.href).searchParams;
        if (params.has('openinapp')) {
            this.config.enableDeepLinking = params.get('openinapp') !== '0';
        }
        else if (params.has('hide_applink')) {
            this.config.enableDeepLinking = params.get('hide_applink') === '0';
        }
        if (params.has('bg_color')) {
            try {
                this.config.backgroundColor = hexColorFromString(params.get('bg_color'));
            }
            catch (error) {
                Logger.warn(`invalid value for background color: ${params.get('bg_color')} is not a valid hex color code`);
            }
        }
        if (params.has('plane')) {
            try {
                this.config.planeColor = hexColorFromString(params.get('plane'));
            }
            catch (error) {
                Logger.warn(`invalid value for plane color: ${params.get('plane')} is not a valid hex color code`);
            }
        }
        if (params.has('brand_color')) {
            try {
                this.config.brandColor = hexColorFromString(params.get('brand_color'));
            }
            catch (error) {
                Logger.warn(`invalid value for brand color: ${params.get('brand_color')} is not a valid hex color code`);
            }
        }
        if (params.has('brand_bg')) {
            try {
                this.config.brandBackground = hexColorFromString(params.get('brand_bg'));
            }
            catch (error) {
                Logger.warn(`invalid value for brand background color: ${params.get('brand_bg')} is not a valid hex color code`);
            }
        }
        if (params.has('hide_bg')) {
            this.config.enableEnvironment = params.get('hide_bg') === '0';
        }
        if (params.has('initial_hotspot')) {
            this.config.initialHotspot = params.get('initial_hotspot');
        }
        if (params.has('hide_sidebar')) {
            this.config.enableSidebar = params.get('hide_sidebar') === '0';
        }
        if (params.has('force_sidebar')) {
            this.config.forceSidebar = params.get('force_sidebar') === '1';
        }
        if (params.has('hide_navigation')) {
            this.config.disableNavigation = params.get('hide_navigation') === '1';
        }
        if (params.has('autoplay')) {
            this.config.autoplay = params.get('autoplay') !== '0';
        }
        if (params.has('autopause')) {
            this.config.autopause = params.get('autopause') !== '0';
        }
        if (params.has('autorotate')) {
            this.config.autorotate = params.get('autorotate') !== '0';
        }
        if (params.has('enable_ao')) {
            this.config.enableAmbientOcclusion = params.get('enable_ao') !== '0';
        }
        if (params.has('lm')) {
            this.config.skipLightmaps = params.get('lm') !== '1';
        }
        if (params.has('format')) {
            this.config.playerFormat = params.get('format');
            if (this.config.playerFormat !== 'aura' && this.config.playerFormat !== 'glb') {
                Logger.info('invalid player format: ' + this.config.playerFormat + ', switching to default: glb');
                this.config.playerFormat = 'glb';
            }
        }
        if (params.has('resolution')) {
            this.config.playerResolution = params.get('resolution');
            if (this.config.playerResolution !== '512' && this.config.playerResolution !== '1024' && this.config.playerResolution !== '2048') {
                Logger.info('invalid player resolution: ' + this.config.playerResolution + ', switching to default: 1024');
                this.config.playerResolution = '1024';
            }
        }
        if (params.has('aa')) {
            this.config.antiAliasSamples = parseInt(params.get('aa'));
        }
        if (params.has('exposure')) {
            this.config.postProcessExposure = parseFloat(params.get('exposure'));
        }
        if (params.has('contrast')) {
            this.config.postProcessContrast = parseFloat(params.get('contrast'));
        }
        if (params.has('enable_mirror')) {
            this.config.enableGroundMirror = params.get('enable_mirror') !== '0';
        }
        if (params.has('enable_xr')) {
            if (params.get('enable_xr') === 'arkit') {
                this.config.forceXR = 'arkit';
                this.config.suppressXR = false;
            }
            else if (params.get('enable_xr') === 'arcore') {
                this.config.forceXR = 'arcore';
                this.config.suppressXR = false;
            }
            else {
                this.config.forceXR = null;
                this.config.suppressXR = params.get('enable_xr') === '0';
            }
        }
        if (params.has('enable_shadows')) {
            this.config.enableShadows = params.get('enable_shadows') !== '0';
        }
        if (params.has('debug')) {
            this.config.enableDebug = params.get('debug') === '1';
        }
        if (params.has('dimensioning')) {
            this.config.enableDimensioning = params.get('dimensioning') === '1';
        }
        if (params.has('disable_watermark')) {
            this.config.disableWatermark = params.get('disable_watermark') === '1';
        }
        if (params.has('screen_resolution')) {
            this.config.screenResolution = parseFloat(params.get('screen_resolution'));
        }
    }
    applyBrand() {
        if (this.config.brandColor) {
            document.body.parentElement.style.setProperty(`--brand`, this.config.brandColor);
        }
        if (this.config.brandBackground) {
            document.body.parentElement.style.setProperty(`--brand-bg`, this.config.brandBackground);
        }
    }
}
/**
 * Default configuration object
 */
BaseViewerConfig.DEFAULT_CONFIG = {
    autoplay: true,
    autopause: true,
    autorotate: false,
    enableSidebar: true,
    disableNavigation: false,
    enableEnvironment: true,
    enableDebug: false,
    enableDeepLinking: true,
    playerFormat: 'aura',
    playerResolution: '1024',
    antiAliasSamples: 2,
    postProcessExposure: 1.0,
    postProcessContrast: 1.0,
    //backgroundColor: ''
};

class OptionalComponentsService {
    constructor() {
        // Setting true or false for the optional components
        this._overlayUiComponent = new BehaviorSubject(false);
        this.overlayUiComponent$ = this._overlayUiComponent.asObservable();
        this._vuframeWatermark = new BehaviorSubject(false);
        this.vuframeWatermark$ = this._vuframeWatermark.asObservable();
        this._controlsMenu = new BehaviorSubject(false);
        this.controlsMenu$ = this._controlsMenu.asObservable();
        this._backButton = new BehaviorSubject(false);
        this.backButton$ = this._backButton.asObservable();
    }
    set overlayUiComponent(value) {
        this._overlayUiComponent.next(value);
    }
    set vuframeWatermark(value) {
        this._vuframeWatermark.next(value);
    }
    set controlsMenu(value) {
        this._controlsMenu.next(value);
    }
    set backButton(value) {
        this._backButton.next(value);
    }
    set stage(value) {
        this._stage = value;
    }
    get stage() {
        return this._stage;
    }
}
OptionalComponentsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: OptionalComponentsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
OptionalComponentsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: OptionalComponentsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: OptionalComponentsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Service which helps for the workaround of the Circular Dependency.
 * Initializes and stores variables and methods which are used by more than one component.
 */
class SharedService {
    constructor(router, route) {
        this.router = router;
        this.route = route;
        this._smartVuType = new BehaviorSubject(null);
        this.smartVuType$ = this._smartVuType.asObservable();
        this._smartVuInfo = new BehaviorSubject(null);
        this.smartVuInfo$ = this._smartVuInfo.asObservable();
        this._collectionSmartVus = new BehaviorSubject(null);
        this.collectionSmartVus$ = this._collectionSmartVus.asObservable();
    }
    set collectionSmartVus(collectionSmartVus) {
        this._collectionSmartVus.next(collectionSmartVus);
    }
    set smartVuInfo(smartVuInfo) {
        this._smartVuInfo.next(smartVuInfo);
    }
    set playerStyle(value) {
        this._playerStyle = value;
    }
    get playerStyle() {
        return this._playerStyle;
    }
    set playerConfig(value) {
        this._playerConfig = value;
    }
    get playerConfig() {
        return this._playerConfig;
    }
    set smartVuType(value) {
        this._smartVuType.next(value);
    }
    set inputConfig(value) {
        this._inputConfig = value;
    }
    get inputConfig() {
        return this._inputConfig;
    }
    setResolution(resolution) {
        let res = 512;
        if (resolution === 'uhd') {
            res = 2048;
        }
        else if (resolution === 'hd') {
            res = 1024;
        }
        if (this.playerConfig.playerResolution === res.toString()) {
            Logger.debug('same solution, not changing');
            return;
        }
        const queryParams = { resolution: res };
        Logger.info(`change resolution to: ${resolution} (${res})`);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            queryParamsHandling: 'merge' // remove to replace all query params by provided
        }).then(r => window.location.reload());
    }
}
SharedService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SharedService, deps: [{ token: i1.Router }, { token: i1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable });
SharedService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SharedService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SharedService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i1.ActivatedRoute }]; } });

/**
 * Helper class for creating new event objects that are sent to Keen.io
 */
class AnalyticsEventFactory {
    /**
     * Creates a new AnalyticsEventsFactory
     * @param platformService an instance of PlatformService (must be passed manually, no dependency injection)
     */
    constructor(platformService) {
        this.platformService = platformService;
    }
    /**
     * Creates a new hit-type event for a SmartVu object
     * @param smartVu the subject smartVu of the event
     */
    createSmartVuHitEvent(smartVu) {
        const event = this.createBaseEvent();
        event.source = {
            type: 'feature',
            token: smartVu.identifier,
        };
        event.feature_token = smartVu.identifier;
        return event;
    }
    /**
     * Creates a new hit-type event for a Share object
     * @param share the subject Share of the event
     */
    createShareHitEvent(smartVuInfo) {
        const event = this.createBaseEvent();
        event.source = {
            type: smartVuInfo.type,
            token: smartVuInfo.token,
        };
        event.share_code = smartVuInfo.code;
        event.feature_token = smartVuInfo.token;
        event.project_token = smartVuInfo.projectToken;
        return event;
    }
    /**
     * Creates a new hit-type event for an Item object
     * @param item the subject Item of the event
     * @param smartVu optional parent SmartVu of the item
     */
    createItemHitEvent(item, smartVu = null) {
        const event = this.createBaseEvent();
        event.source = {
            type: 'item',
            token: item.identifier,
        };
        if (smartVu) {
            event.feature_token = smartVu.identifier;
            event.parent_feature_token = smartVu.identifier;
        }
        return event;
    }
    /**
     * Creates an empty event object that can then be filled with the subject data
     */
    createBaseEvent() {
        return {
            source: {
                token: null,
                type: null,
            },
            platform: {
                version: libEnv.version,
                type: 'web',
            },
            device: {
                uuid: '${keen.ip}',
                name: null,
                type_id: this.platformService.isMobile() ? 'mobile' : 'desktop',
            },
            client: {
                language: navigator.language.slice(0, 2),
                id: libEnv.applicationName,
                version: libEnv.version,
                cms_type: 'live',
                type: 'viewer',
                build_type: AnalyticsEventFactory.getBuildTypeForEnvironment(),
            },
            parent_feature_token: null,
            feature_token: null,
            share_code: null,
            project_token: null,
            company_token: null,
            app_token: null,
            distribution_token: null,
            cms_user_id: null,
            company_user_id: null,
            keen: {
                addons: [
                // disable IP tracking
                /*{
                  name: 'keen:ip_to_geo',
                  input: {
                    ip: 'device.uuid',
                  },
                  output: 'location',
                },*/
                ],
            },
        };
    }
    /**
     * Maps the current environment to a build type string as used by the mobile apps
     */
    static getBuildTypeForEnvironment() {
        if (libEnv.stage === 'stage') {
            return 'live';
        }
        else if (libEnv.stage === 'experimental') {
            return 'experimental';
        }
        else if (libEnv.stage === 'beta') {
            return 'beta';
        }
        else if (libEnv.stage === 'staging') {
            return 'staging';
        }
        else {
            return 'dev';
        }
    }
}

/**
 * Enum listing the different supported operating systems
 */
var DevicePlatform;
(function (DevicePlatform) {
    DevicePlatform[DevicePlatform["iOS"] = 0] = "iOS";
    DevicePlatform[DevicePlatform["Android"] = 1] = "Android";
    DevicePlatform[DevicePlatform["Other"] = 2] = "Other";
})(DevicePlatform || (DevicePlatform = {}));
/**
 * Helper service that provides information about the type of the current device and operating system. Uses the mobile-detect npm package.
 */
class PlatformService {
    /**
     * Constructs a new PlatformService
     */
    constructor() {
        // initialize MobileDetect with the browser's user agent string
        this.deviceInfo = new MobileDetect(navigator.userAgent);
    }
    /**
     * Checks if the device is running iOS
     * @returns true for iOS devices
     */
    isIOS() {
        return this.deviceInfo.os() == 'iOS' || this.isIpadOS();
    }
    /**
     * Checks if the current device is an iPad running iOS. The user agent of new versions of iPadOS is indistinguishable from macOS, so this method checks if the device uses a macOS user agent string and supports multi-touch.
     * @returns true if the device is running iPadOS
     */
    isIpadOS() {
        /* */
        return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    }
    /**
     * Checks if the current device is a mobile device (smartphone or tabled)
     * @returns true if the current device is a mobile device
     */
    isMobile() {
        return this.deviceInfo.mobile() !== null || this.isIpadOS();
    }
    /**
     * Checks if the current device is an Android device
     * @returns true if the device is running Android
     */
    isAndroid() {
        return this.deviceInfo.os() == 'AndroidOS';
    }
    /**
     * Checks if the current device can run ARCore applications
     * @returns true if the device supports ARCore
     */
    supportsARCore() {
        // ARCore support requires Android 7 or above
        return this.isAndroid() && this.deviceInfo.version('Android') > 7;
    }
    /**
     * Checks if the current device can run iOS QuickLook
     * @returns true if the device supports QuickLook
     */
    supportsQuickLook() {
        // device needs to run iOS/iPadOS
        if (!this.isIOS) {
            return false;
        }
        // validate presence of QuickLook using temporary link element
        const tempLink = document.createElement('a');
        return Boolean(tempLink.relList &&
            tempLink.relList.supports &&
            tempLink.relList.supports('ar'));
    }
    /**
     * Gets the current devices platform/OS
     * @returns the device's platform as a DevicePlatform value
     */
    getCurrentPlatform() {
        if (this.isIOS()) {
            return DevicePlatform.iOS;
        }
        else if (this.isAndroid()) {
            return DevicePlatform.Android;
        }
        else {
            return DevicePlatform.Other;
        }
    }
}
PlatformService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlatformService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PlatformService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlatformService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlatformService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Service class that sends tracking events to Keen.io for analytics.
 */
class AnalyticsService {
    /**
     * Creates a new AnalyticsService instance and initializes the Keen client
     * @param platformService
     * @param optionalComponentsService
     * @param sharedService
     */
    constructor(platformService, optionalComponentsService, sharedService) {
        this.platformService = platformService;
        this.optionalComponentsService = optionalComponentsService;
        this.sharedService = sharedService;
        this.eventFactory = new AnalyticsEventFactory(this.platformService);
    }
    registerKeenClient(projectId, writeKey) {
        this.keenClient = new KeenTracking({
            projectId: projectId,
            writeKey: writeKey,
        });
    }
    /**
     * Registers a new hit event for the given SmartVu object
     * @param smartVu the subject of the hit event
     */
    registerSmartVuHit(smartVu) {
        return __awaiter(this, void 0, void 0, function* () {
            const event = this.eventFactory.createSmartVuHitEvent(smartVu);
            this.registerHitEvent(event);
        });
    }
    /**
     * Registers a new hit event for the given Item object
     * @param item the subject of the hit event
     * @param smartVu optional parent SmartVu of the item
     */
    registerItemHit(item, smartVu = null) {
        return __awaiter(this, void 0, void 0, function* () {
            const event = this.eventFactory.createItemHitEvent(item, smartVu);
            this.registerHitEvent(event);
        });
    }
    /**
     * Registers a new hit event for the given Share object
     * @param smartVuInfo the subject of the hit event
     */
    registerShareHit(smartVuInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            const event = this.eventFactory.createShareHitEvent(smartVuInfo);
            this.registerHitEvent(event);
        });
    }
    /**
     * Registers a new hit event with Keen.io
     * @param event the hit event containing all relevant information
     */
    registerHitEvent(event) {
        if (this.optionalComponentsService.stage === 'production' && event) {
            this.keenClient.recordEvent('hits', event).catch(error => {
            });
        }
    }
}
AnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AnalyticsService, deps: [{ token: PlatformService }, { token: OptionalComponentsService }, { token: SharedService }], target: i0.ɵɵFactoryTarget.Injectable });
AnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PlatformService }, { type: OptionalComponentsService }, { type: SharedService }]; } });

class GlobalStylingComponent {
    constructor() { }
    ngOnInit() {
    }
}
GlobalStylingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
GlobalStylingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: GlobalStylingComponent, selector: "vf-global-styling", ngImport: i0, template: "", styles: ["a.mat-button-base,button.mat-button-base{border-radius:100px}.viewer-popup-dialog-cms{min-height:50vh}.viewer-popup-dialog{width:700px}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-content{max-height:78vh;padding:15px 25px 0}.viewer-popup-fullscreen{width:calc(100vw - 40px);height:calc(100vh - 40px);overflow:hidden}.viewer-popup-fullscreen mat-dialog-container{overflow:hidden}.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-content{max-height:100vh;padding:0}.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-content ng2-pdfjs-viewer>iframe{border:none;margin-bottom:-6px}.viewer-popup-dialog mat-dialog-container,.viewer-popup-fullscreen mat-dialog-container{padding:0;will-change:transform,opacity}.viewer-popup-dialog mat-dialog-container>*,.viewer-popup-fullscreen mat-dialog-container>*{display:flex;flex-direction:column;align-items:center}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-title,.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-title{position:relative;width:100%;text-align:center;background:var(--brand-bg);color:var(--brand);padding:15px;margin:0;box-shadow:0 3px 5px #0000001a;-webkit-user-select:none;user-select:none}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-title a,.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-title a{position:absolute;right:20px;top:17px;color:var(--brand);cursor:pointer}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-title a>*,.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-title a>*{font-size:1.5em}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-content,.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-content{text-align:center;margin:0}.viewer-popup-dialog mat-dialog-container>* .mat-dialog-content video,.viewer-popup-fullscreen mat-dialog-container>* .mat-dialog-content video{display:block;margin:auto;max-width:100%;max-height:60vh}.viewer-popup-dialog .mat-dialog-actions,.viewer-popup-fullscreen .mat-dialog-actions{padding:15px;margin-bottom:0;justify-content:center}.viewer-popup-dialog.viewer-popup-dialog-borderless .mat-dialog-content,.viewer-popup-fullscreen.viewer-popup-dialog-borderless .mat-dialog-content{padding:10px}.viewer-popup-dialog.viewer-popup-dialog-borderless .mat-dialog-actions,.viewer-popup-fullscreen.viewer-popup-dialog-borderless .mat-dialog-actions{display:none}.viewer-popup-dialog.viewer-popup-pdf .mat-dialog-content,.viewer-popup-fullscreen.viewer-popup-pdf .mat-dialog-content{padding:0;position:absolute;width:100%;height:calc(100vh - 89px);background-color:#505050}.viewer-popup-dialog.viewer-popup-pdf .mat-dialog-actions,.viewer-popup-fullscreen.viewer-popup-pdf .mat-dialog-actions{padding:15px;margin-bottom:0;justify-content:flex-end;position:absolute;width:100%;right:40px;top:-9px}.viewer-popup-dialog.viewer-popup-fullscreen .mat-dialog-content,.viewer-popup-fullscreen.viewer-popup-fullscreen .mat-dialog-content{padding:0;position:absolute;width:100%;height:calc(100vh - 89px);top:49px;bottom:0px;left:0px;right:0px}.viewer-popup-dialog.viewer-popup-fullscreen .mat-dialog-actions,.viewer-popup-fullscreen.viewer-popup-fullscreen .mat-dialog-actions{padding:15px;margin-bottom:0;justify-content:flex-end;position:absolute;width:100%;right:40px;top:-9px}.viewer-popup-dialog.viewer-popup-media .mat-dialog-content,.viewer-popup-fullscreen.viewer-popup-media .mat-dialog-content{background-color:#000;display:grid;align-items:center}*:focus{outline:none!important}a,button{-webkit-tap-highlight-color:rgba(0,0,0,0)}body{background-color:transparent}.page-container{width:100%;height:100%;display:flex;flex-direction:column;justify-content:center;align-items:center;background-color:transparent}.page-container .page-content{width:420px;max-width:95%;padding:25px;text-align:center;border-radius:20px;background:#fff;box-shadow:0 3px 10px #0003}.page-container .page-content h1{font-size:1.5em;margin-top:5px}.img-responsive{max-width:100%}.center{display:flex;justify-content:center}.left{float:left}.right{float:right}.clear{overflow:hidden}input[disabled=true],button[disabled=true],a[disabled=true]{cursor:not-allowed;pointer-events:none}.mat-ripple-element{background-color:#0000001a}.mat-option{color:#000000de}.mat-option:hover:not(.mat-option-disabled),.mat-option:focus:not(.mat-option-disabled){background:rgba(0,0,0,.04)}.mat-option.mat-selected:not(.mat-option-multiple):not(.mat-option-disabled){background:rgba(0,0,0,.04)}.mat-option.mat-active{background:rgba(0,0,0,.04);color:#000000de}.mat-option.mat-option-disabled{color:#00000061}.mat-primary .mat-option.mat-selected:not(.mat-option-disabled){color:#121a27}.mat-accent .mat-option.mat-selected:not(.mat-option-disabled){color:#ff5252}.mat-warn .mat-option.mat-selected:not(.mat-option-disabled){color:#f44336}.mat-optgroup-label{color:#0000008a}.mat-optgroup-disabled .mat-optgroup-label{color:#00000061}.mat-pseudo-checkbox{color:#0000008a}.mat-pseudo-checkbox:after{color:#fafafa}.mat-pseudo-checkbox-disabled{color:#b0b0b0}.mat-primary .mat-pseudo-checkbox-checked,.mat-primary .mat-pseudo-checkbox-indeterminate{background:#121a27}.mat-pseudo-checkbox-checked,.mat-pseudo-checkbox-indeterminate,.mat-accent .mat-pseudo-checkbox-checked,.mat-accent .mat-pseudo-checkbox-indeterminate{background:#ff5252}.mat-warn .mat-pseudo-checkbox-checked,.mat-warn .mat-pseudo-checkbox-indeterminate{background:#f44336}.mat-pseudo-checkbox-checked.mat-pseudo-checkbox-disabled,.mat-pseudo-checkbox-indeterminate.mat-pseudo-checkbox-disabled{background:#b0b0b0}.mat-app-background{background-color:#fafafa;color:#000000de}.mat-elevation-z0{box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-elevation-z1{box-shadow:0 2px 1px -1px #0003,0 1px 1px #00000024,0 1px 3px #0000001f}.mat-elevation-z2{box-shadow:0 3px 1px -2px #0003,0 2px 2px #00000024,0 1px 5px #0000001f}.mat-elevation-z3{box-shadow:0 3px 3px -2px #0003,0 3px 4px #00000024,0 1px 8px #0000001f}.mat-elevation-z4{box-shadow:0 2px 4px -1px #0003,0 4px 5px #00000024,0 1px 10px #0000001f}.mat-elevation-z5{box-shadow:0 3px 5px -1px #0003,0 5px 8px #00000024,0 1px 14px #0000001f}.mat-elevation-z6{box-shadow:0 3px 5px -1px #0003,0 6px 10px #00000024,0 1px 18px #0000001f}.mat-elevation-z7{box-shadow:0 4px 5px -2px #0003,0 7px 10px 1px #00000024,0 2px 16px 1px #0000001f}.mat-elevation-z8{box-shadow:0 5px 5px -3px #0003,0 8px 10px 1px #00000024,0 3px 14px 2px #0000001f}.mat-elevation-z9{box-shadow:0 5px 6px -3px #0003,0 9px 12px 1px #00000024,0 3px 16px 2px #0000001f}.mat-elevation-z10{box-shadow:0 6px 6px -3px #0003,0 10px 14px 1px #00000024,0 4px 18px 3px #0000001f}.mat-elevation-z11{box-shadow:0 6px 7px -4px #0003,0 11px 15px 1px #00000024,0 4px 20px 3px #0000001f}.mat-elevation-z12{box-shadow:0 7px 8px -4px #0003,0 12px 17px 2px #00000024,0 5px 22px 4px #0000001f}.mat-elevation-z13{box-shadow:0 7px 8px -4px #0003,0 13px 19px 2px #00000024,0 5px 24px 4px #0000001f}.mat-elevation-z14{box-shadow:0 7px 9px -4px #0003,0 14px 21px 2px #00000024,0 5px 26px 4px #0000001f}.mat-elevation-z15{box-shadow:0 8px 9px -5px #0003,0 15px 22px 2px #00000024,0 6px 28px 5px #0000001f}.mat-elevation-z16{box-shadow:0 8px 10px -5px #0003,0 16px 24px 2px #00000024,0 6px 30px 5px #0000001f}.mat-elevation-z17{box-shadow:0 8px 11px -5px #0003,0 17px 26px 2px #00000024,0 6px 32px 5px #0000001f}.mat-elevation-z18{box-shadow:0 9px 11px -5px #0003,0 18px 28px 2px #00000024,0 7px 34px 6px #0000001f}.mat-elevation-z19{box-shadow:0 9px 12px -6px #0003,0 19px 29px 2px #00000024,0 7px 36px 6px #0000001f}.mat-elevation-z20{box-shadow:0 10px 13px -6px #0003,0 20px 31px 3px #00000024,0 8px 38px 7px #0000001f}.mat-elevation-z21{box-shadow:0 10px 13px -6px #0003,0 21px 33px 3px #00000024,0 8px 40px 7px #0000001f}.mat-elevation-z22{box-shadow:0 10px 14px -6px #0003,0 22px 35px 3px #00000024,0 8px 42px 7px #0000001f}.mat-elevation-z23{box-shadow:0 11px 14px -7px #0003,0 23px 36px 3px #00000024,0 9px 44px 8px #0000001f}.mat-elevation-z24{box-shadow:0 11px 15px -7px #0003,0 24px 38px 3px #00000024,0 9px 46px 8px #0000001f}.mat-theme-loaded-marker{display:none}.mat-autocomplete-panel{background:white;color:#000000de}.mat-autocomplete-panel:not([class*=mat-elevation-z]){box-shadow:0 2px 4px -1px #0003,0 4px 5px #00000024,0 1px 10px #0000001f}.mat-autocomplete-panel .mat-option.mat-selected:not(.mat-active):not(:hover){background:white}.mat-autocomplete-panel .mat-option.mat-selected:not(.mat-active):not(:hover):not(.mat-option-disabled){color:#000000de}.mat-badge-content{color:#fff;background:#121a27}.cdk-high-contrast-active .mat-badge-content{outline:solid 1px;border-radius:0}.mat-badge-accent .mat-badge-content{background:#ff5252;color:#fff}.mat-badge-warn .mat-badge-content{color:#fff;background:#f44336}.mat-badge{position:relative}.mat-badge-hidden .mat-badge-content{display:none}.mat-badge-disabled .mat-badge-content{background:#b9b9b9;color:#00000061}.mat-badge-content{position:absolute;text-align:center;display:inline-block;border-radius:50%;transition:transform .2s ease-in-out;transform:scale(.6);overflow:hidden;white-space:nowrap;text-overflow:ellipsis;pointer-events:none}.ng-animate-disabled .mat-badge-content,.mat-badge-content._mat-animation-noopable{transition:none}.mat-badge-content.mat-badge-active{transform:none}.mat-badge-small .mat-badge-content{width:16px;height:16px;line-height:16px}.mat-badge-small.mat-badge-above .mat-badge-content{top:-8px}.mat-badge-small.mat-badge-below .mat-badge-content{bottom:-8px}.mat-badge-small.mat-badge-before .mat-badge-content{left:-16px}[dir=rtl] .mat-badge-small.mat-badge-before .mat-badge-content{left:auto;right:-16px}.mat-badge-small.mat-badge-after .mat-badge-content{right:-16px}[dir=rtl] .mat-badge-small.mat-badge-after .mat-badge-content{right:auto;left:-16px}.mat-badge-small.mat-badge-overlap.mat-badge-before .mat-badge-content{left:-8px}[dir=rtl] .mat-badge-small.mat-badge-overlap.mat-badge-before .mat-badge-content{left:auto;right:-8px}.mat-badge-small.mat-badge-overlap.mat-badge-after .mat-badge-content{right:-8px}[dir=rtl] .mat-badge-small.mat-badge-overlap.mat-badge-after .mat-badge-content{right:auto;left:-8px}.mat-badge-medium .mat-badge-content{width:22px;height:22px;line-height:22px}.mat-badge-medium.mat-badge-above .mat-badge-content{top:-11px}.mat-badge-medium.mat-badge-below .mat-badge-content{bottom:-11px}.mat-badge-medium.mat-badge-before .mat-badge-content{left:-22px}[dir=rtl] .mat-badge-medium.mat-badge-before .mat-badge-content{left:auto;right:-22px}.mat-badge-medium.mat-badge-after .mat-badge-content{right:-22px}[dir=rtl] .mat-badge-medium.mat-badge-after .mat-badge-content{right:auto;left:-22px}.mat-badge-medium.mat-badge-overlap.mat-badge-before .mat-badge-content{left:-11px}[dir=rtl] .mat-badge-medium.mat-badge-overlap.mat-badge-before .mat-badge-content{left:auto;right:-11px}.mat-badge-medium.mat-badge-overlap.mat-badge-after .mat-badge-content{right:-11px}[dir=rtl] .mat-badge-medium.mat-badge-overlap.mat-badge-after .mat-badge-content{right:auto;left:-11px}.mat-badge-large .mat-badge-content{width:28px;height:28px;line-height:28px}.mat-badge-large.mat-badge-above .mat-badge-content{top:-14px}.mat-badge-large.mat-badge-below .mat-badge-content{bottom:-14px}.mat-badge-large.mat-badge-before .mat-badge-content{left:-28px}[dir=rtl] .mat-badge-large.mat-badge-before .mat-badge-content{left:auto;right:-28px}.mat-badge-large.mat-badge-after .mat-badge-content{right:-28px}[dir=rtl] .mat-badge-large.mat-badge-after .mat-badge-content{right:auto;left:-28px}.mat-badge-large.mat-badge-overlap.mat-badge-before .mat-badge-content{left:-14px}[dir=rtl] .mat-badge-large.mat-badge-overlap.mat-badge-before .mat-badge-content{left:auto;right:-14px}.mat-badge-large.mat-badge-overlap.mat-badge-after .mat-badge-content{right:-14px}[dir=rtl] .mat-badge-large.mat-badge-overlap.mat-badge-after .mat-badge-content{right:auto;left:-14px}.mat-bottom-sheet-container{box-shadow:0 8px 10px -5px #0003,0 16px 24px 2px #00000024,0 6px 30px 5px #0000001f;background:white;color:#000000de}.mat-button,.mat-icon-button,.mat-stroked-button{color:inherit;background:transparent}.mat-button.mat-primary,.mat-icon-button.mat-primary,.mat-stroked-button.mat-primary{color:#121a27}.mat-button.mat-accent,.mat-icon-button.mat-accent,.mat-stroked-button.mat-accent{color:#ff5252}.mat-button.mat-warn,.mat-icon-button.mat-warn,.mat-stroked-button.mat-warn{color:#f44336}.mat-button.mat-primary[disabled],.mat-button.mat-accent[disabled],.mat-button.mat-warn[disabled],.mat-button[disabled][disabled],.mat-icon-button.mat-primary[disabled],.mat-icon-button.mat-accent[disabled],.mat-icon-button.mat-warn[disabled],.mat-icon-button[disabled][disabled],.mat-stroked-button.mat-primary[disabled],.mat-stroked-button.mat-accent[disabled],.mat-stroked-button.mat-warn[disabled],.mat-stroked-button[disabled][disabled]{color:#00000042}.mat-button.mat-primary .mat-button-focus-overlay,.mat-icon-button.mat-primary .mat-button-focus-overlay,.mat-stroked-button.mat-primary .mat-button-focus-overlay{background-color:#121a27}.mat-button.mat-accent .mat-button-focus-overlay,.mat-icon-button.mat-accent .mat-button-focus-overlay,.mat-stroked-button.mat-accent .mat-button-focus-overlay{background-color:#ff5252}.mat-button.mat-warn .mat-button-focus-overlay,.mat-icon-button.mat-warn .mat-button-focus-overlay,.mat-stroked-button.mat-warn .mat-button-focus-overlay{background-color:#f44336}.mat-button[disabled] .mat-button-focus-overlay,.mat-icon-button[disabled] .mat-button-focus-overlay,.mat-stroked-button[disabled] .mat-button-focus-overlay{background-color:transparent}.mat-button .mat-ripple-element,.mat-icon-button .mat-ripple-element,.mat-stroked-button .mat-ripple-element{opacity:.1;background-color:currentColor}.mat-button-focus-overlay{background:black}.mat-stroked-button:not([disabled]){border-color:#0000001f}.mat-flat-button,.mat-raised-button,.mat-fab,.mat-mini-fab{color:#000000de;background-color:#fff}.mat-flat-button.mat-primary,.mat-raised-button.mat-primary,.mat-fab.mat-primary,.mat-mini-fab.mat-primary{color:#fff}.mat-flat-button.mat-accent,.mat-raised-button.mat-accent,.mat-fab.mat-accent,.mat-mini-fab.mat-accent{color:#fff}.mat-flat-button.mat-warn,.mat-raised-button.mat-warn,.mat-fab.mat-warn,.mat-mini-fab.mat-warn{color:#fff}.mat-flat-button.mat-primary[disabled],.mat-flat-button.mat-accent[disabled],.mat-flat-button.mat-warn[disabled],.mat-flat-button[disabled][disabled],.mat-raised-button.mat-primary[disabled],.mat-raised-button.mat-accent[disabled],.mat-raised-button.mat-warn[disabled],.mat-raised-button[disabled][disabled],.mat-fab.mat-primary[disabled],.mat-fab.mat-accent[disabled],.mat-fab.mat-warn[disabled],.mat-fab[disabled][disabled],.mat-mini-fab.mat-primary[disabled],.mat-mini-fab.mat-accent[disabled],.mat-mini-fab.mat-warn[disabled],.mat-mini-fab[disabled][disabled]{color:#00000042}.mat-flat-button.mat-primary,.mat-raised-button.mat-primary,.mat-fab.mat-primary,.mat-mini-fab.mat-primary{background-color:#121a27}.mat-flat-button.mat-accent,.mat-raised-button.mat-accent,.mat-fab.mat-accent,.mat-mini-fab.mat-accent{background-color:#ff5252}.mat-flat-button.mat-warn,.mat-raised-button.mat-warn,.mat-fab.mat-warn,.mat-mini-fab.mat-warn{background-color:#f44336}.mat-flat-button.mat-primary[disabled],.mat-flat-button.mat-accent[disabled],.mat-flat-button.mat-warn[disabled],.mat-flat-button[disabled][disabled],.mat-raised-button.mat-primary[disabled],.mat-raised-button.mat-accent[disabled],.mat-raised-button.mat-warn[disabled],.mat-raised-button[disabled][disabled],.mat-fab.mat-primary[disabled],.mat-fab.mat-accent[disabled],.mat-fab.mat-warn[disabled],.mat-fab[disabled][disabled],.mat-mini-fab.mat-primary[disabled],.mat-mini-fab.mat-accent[disabled],.mat-mini-fab.mat-warn[disabled],.mat-mini-fab[disabled][disabled]{background-color:#0000001f}.mat-flat-button.mat-primary .mat-ripple-element,.mat-raised-button.mat-primary .mat-ripple-element,.mat-fab.mat-primary .mat-ripple-element,.mat-mini-fab.mat-primary .mat-ripple-element{background-color:#ffffff1a}.mat-flat-button.mat-accent .mat-ripple-element,.mat-raised-button.mat-accent .mat-ripple-element,.mat-fab.mat-accent .mat-ripple-element,.mat-mini-fab.mat-accent .mat-ripple-element{background-color:#ffffff1a}.mat-flat-button.mat-warn .mat-ripple-element,.mat-raised-button.mat-warn .mat-ripple-element,.mat-fab.mat-warn .mat-ripple-element,.mat-mini-fab.mat-warn .mat-ripple-element{background-color:#ffffff1a}.mat-stroked-button:not([class*=mat-elevation-z]),.mat-flat-button:not([class*=mat-elevation-z]){box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-raised-button:not([class*=mat-elevation-z]){box-shadow:0 3px 1px -2px #0003,0 2px 2px #00000024,0 1px 5px #0000001f}.mat-raised-button:not([disabled]):active:not([class*=mat-elevation-z]){box-shadow:0 5px 5px -3px #0003,0 8px 10px 1px #00000024,0 3px 14px 2px #0000001f}.mat-raised-button[disabled]:not([class*=mat-elevation-z]){box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-fab:not([class*=mat-elevation-z]),.mat-mini-fab:not([class*=mat-elevation-z]){box-shadow:0 3px 5px -1px #0003,0 6px 10px #00000024,0 1px 18px #0000001f}.mat-fab:not([disabled]):active:not([class*=mat-elevation-z]),.mat-mini-fab:not([disabled]):active:not([class*=mat-elevation-z]){box-shadow:0 7px 8px -4px #0003,0 12px 17px 2px #00000024,0 5px 22px 4px #0000001f}.mat-fab[disabled]:not([class*=mat-elevation-z]),.mat-mini-fab[disabled]:not([class*=mat-elevation-z]){box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-button-toggle-standalone,.mat-button-toggle-group{box-shadow:0 3px 1px -2px #0003,0 2px 2px #00000024,0 1px 5px #0000001f}.mat-button-toggle-standalone.mat-button-toggle-appearance-standard,.mat-button-toggle-group-appearance-standard{box-shadow:none}.mat-button-toggle{color:#00000061}.mat-button-toggle .mat-button-toggle-focus-overlay{background-color:#0000001f}.mat-button-toggle-appearance-standard{color:#000000de;background:white}.mat-button-toggle-appearance-standard .mat-button-toggle-focus-overlay{background-color:#000}.mat-button-toggle-group-appearance-standard .mat-button-toggle+.mat-button-toggle{border-left:solid 1px rgba(0,0,0,.12)}[dir=rtl] .mat-button-toggle-group-appearance-standard .mat-button-toggle+.mat-button-toggle{border-left:none;border-right:solid 1px rgba(0,0,0,.12)}.mat-button-toggle-group-appearance-standard.mat-button-toggle-vertical .mat-button-toggle+.mat-button-toggle{border-left:none;border-right:none;border-top:solid 1px rgba(0,0,0,.12)}.mat-button-toggle-checked{background-color:#e0e0e0;color:#0000008a}.mat-button-toggle-checked.mat-button-toggle-appearance-standard{color:#000000de}.mat-button-toggle-disabled{color:#00000042;background-color:#eee}.mat-button-toggle-disabled.mat-button-toggle-appearance-standard{background:white}.mat-button-toggle-disabled.mat-button-toggle-checked{background-color:#bdbdbd}.mat-button-toggle-standalone.mat-button-toggle-appearance-standard,.mat-button-toggle-group-appearance-standard{border:solid 1px rgba(0,0,0,.12)}.mat-button-toggle-appearance-standard .mat-button-toggle-label-content{line-height:48px}.mat-card{background:white;color:#000000de}.mat-card:not([class*=mat-elevation-z]){box-shadow:0 2px 1px -1px #0003,0 1px 1px #00000024,0 1px 3px #0000001f}.mat-card.mat-card-flat:not([class*=mat-elevation-z]){box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-card-subtitle{color:#0000008a}.mat-checkbox-frame{border-color:#0000008a}.mat-checkbox-checkmark{fill:#fafafa}.mat-checkbox-checkmark-path{stroke:#fafafa!important}.mat-checkbox-mixedmark{background-color:#fafafa}.mat-checkbox-indeterminate.mat-primary .mat-checkbox-background,.mat-checkbox-checked.mat-primary .mat-checkbox-background{background-color:#121a27}.mat-checkbox-indeterminate.mat-accent .mat-checkbox-background,.mat-checkbox-checked.mat-accent .mat-checkbox-background{background-color:#ff5252}.mat-checkbox-indeterminate.mat-warn .mat-checkbox-background,.mat-checkbox-checked.mat-warn .mat-checkbox-background{background-color:#f44336}.mat-checkbox-disabled.mat-checkbox-checked .mat-checkbox-background,.mat-checkbox-disabled.mat-checkbox-indeterminate .mat-checkbox-background{background-color:#b0b0b0}.mat-checkbox-disabled:not(.mat-checkbox-checked) .mat-checkbox-frame{border-color:#b0b0b0}.mat-checkbox-disabled .mat-checkbox-label{color:#0000008a}.mat-checkbox .mat-ripple-element{background-color:#000}.mat-checkbox-checked:not(.mat-checkbox-disabled).mat-primary .mat-ripple-element,.mat-checkbox:active:not(.mat-checkbox-disabled).mat-primary .mat-ripple-element{background:#121a27}.mat-checkbox-checked:not(.mat-checkbox-disabled).mat-accent .mat-ripple-element,.mat-checkbox:active:not(.mat-checkbox-disabled).mat-accent .mat-ripple-element{background:#ff5252}.mat-checkbox-checked:not(.mat-checkbox-disabled).mat-warn .mat-ripple-element,.mat-checkbox:active:not(.mat-checkbox-disabled).mat-warn .mat-ripple-element{background:#f44336}.mat-chip.mat-standard-chip{background-color:#e0e0e0;color:#000000de}.mat-chip.mat-standard-chip .mat-chip-remove{color:#000000de;opacity:.4}.mat-chip.mat-standard-chip:not(.mat-chip-disabled):active{box-shadow:0 3px 3px -2px #0003,0 3px 4px #00000024,0 1px 8px #0000001f}.mat-chip.mat-standard-chip:not(.mat-chip-disabled) .mat-chip-remove:hover{opacity:.54}.mat-chip.mat-standard-chip.mat-chip-disabled{opacity:.4}.mat-chip.mat-standard-chip:after{background:black}.mat-chip.mat-standard-chip.mat-chip-selected.mat-primary{background-color:#121a27;color:#fff}.mat-chip.mat-standard-chip.mat-chip-selected.mat-primary .mat-chip-remove{color:#fff;opacity:.4}.mat-chip.mat-standard-chip.mat-chip-selected.mat-primary .mat-ripple-element{background-color:#ffffff1a}.mat-chip.mat-standard-chip.mat-chip-selected.mat-warn{background-color:#f44336;color:#fff}.mat-chip.mat-standard-chip.mat-chip-selected.mat-warn .mat-chip-remove{color:#fff;opacity:.4}.mat-chip.mat-standard-chip.mat-chip-selected.mat-warn .mat-ripple-element{background-color:#ffffff1a}.mat-chip.mat-standard-chip.mat-chip-selected.mat-accent{background-color:#ff5252;color:#fff}.mat-chip.mat-standard-chip.mat-chip-selected.mat-accent .mat-chip-remove{color:#fff;opacity:.4}.mat-chip.mat-standard-chip.mat-chip-selected.mat-accent .mat-ripple-element{background-color:#ffffff1a}.mat-table{background:white}.mat-table thead,.mat-table tbody,.mat-table tfoot,mat-header-row,mat-row,mat-footer-row,[mat-header-row],[mat-row],[mat-footer-row],.mat-table-sticky{background:inherit}mat-row,mat-header-row,mat-footer-row,th.mat-header-cell,td.mat-cell,td.mat-footer-cell{border-bottom-color:#0000001f}.mat-header-cell{color:#0000008a}.mat-cell,.mat-footer-cell{color:#000000de}.mat-calendar-arrow{border-top-color:#0000008a}.mat-datepicker-toggle,.mat-datepicker-content .mat-calendar-next-button,.mat-datepicker-content .mat-calendar-previous-button{color:#0000008a}.mat-calendar-table-header{color:#00000061}.mat-calendar-table-header-divider:after{background:rgba(0,0,0,.12)}.mat-calendar-body-label{color:#0000008a}.mat-calendar-body-cell-content,.mat-date-range-input-separator{color:#000000de;border-color:transparent}.mat-calendar-body-disabled>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected),.mat-form-field-disabled .mat-date-range-input-separator{color:#00000061}.mat-calendar-body-cell:not(.mat-calendar-body-disabled):hover>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected),.cdk-keyboard-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected),.cdk-program-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected){background-color:#0000000a}.mat-calendar-body-in-preview{color:#0000003d}.mat-calendar-body-today:not(.mat-calendar-body-selected){border-color:#00000061}.mat-calendar-body-disabled>.mat-calendar-body-today:not(.mat-calendar-body-selected){border-color:#0000002e}.mat-calendar-body-in-range:before{background:rgba(18,26,39,.2)}.mat-calendar-body-in-comparison-range:before{background:rgba(249,171,0,.2)}.mat-calendar-body-comparison-bridge-start:before,[dir=rtl] .mat-calendar-body-comparison-bridge-end:before{background:linear-gradient(to right,rgba(18,26,39,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-calendar-body-comparison-bridge-end:before,[dir=rtl] .mat-calendar-body-comparison-bridge-start:before{background:linear-gradient(to left,rgba(18,26,39,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-calendar-body-in-comparison-range.mat-calendar-body-in-range:after{background:#a8dab5}.mat-calendar-body-in-comparison-range>.mat-calendar-body-selected{background:rgb(69.5241935484,163.4758064516,93.9516129032)}.mat-calendar-body-selected{background-color:#121a27;color:#fff}.mat-calendar-body-disabled>.mat-calendar-body-selected{background-color:#121a2766}.mat-calendar-body-today.mat-calendar-body-selected{box-shadow:inset 0 0 0 1px #fff}.mat-datepicker-content{box-shadow:0 2px 4px -1px #0003,0 4px 5px #00000024,0 1px 10px #0000001f;background-color:#fff;color:#000000de}.mat-datepicker-content.mat-accent .mat-calendar-body-in-range:before{background:rgba(255,82,82,.2)}.mat-datepicker-content.mat-accent .mat-calendar-body-in-comparison-range:before{background:rgba(249,171,0,.2)}.mat-datepicker-content.mat-accent .mat-calendar-body-comparison-bridge-start:before,.mat-datepicker-content.mat-accent [dir=rtl] .mat-calendar-body-comparison-bridge-end:before{background:linear-gradient(to right,rgba(255,82,82,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-datepicker-content.mat-accent .mat-calendar-body-comparison-bridge-end:before,.mat-datepicker-content.mat-accent [dir=rtl] .mat-calendar-body-comparison-bridge-start:before{background:linear-gradient(to left,rgba(255,82,82,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-datepicker-content.mat-accent .mat-calendar-body-in-comparison-range.mat-calendar-body-in-range:after{background:#a8dab5}.mat-datepicker-content.mat-accent .mat-calendar-body-in-comparison-range>.mat-calendar-body-selected{background:rgb(69.5241935484,163.4758064516,93.9516129032)}.mat-datepicker-content.mat-accent .mat-calendar-body-selected{background-color:#ff5252;color:#fff}.mat-datepicker-content.mat-accent .mat-calendar-body-disabled>.mat-calendar-body-selected{background-color:#ff525266}.mat-datepicker-content.mat-accent .mat-calendar-body-today.mat-calendar-body-selected{box-shadow:inset 0 0 0 1px #fff}.mat-datepicker-content.mat-warn .mat-calendar-body-in-range:before{background:rgba(244,67,54,.2)}.mat-datepicker-content.mat-warn .mat-calendar-body-in-comparison-range:before{background:rgba(249,171,0,.2)}.mat-datepicker-content.mat-warn .mat-calendar-body-comparison-bridge-start:before,.mat-datepicker-content.mat-warn [dir=rtl] .mat-calendar-body-comparison-bridge-end:before{background:linear-gradient(to right,rgba(244,67,54,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-datepicker-content.mat-warn .mat-calendar-body-comparison-bridge-end:before,.mat-datepicker-content.mat-warn [dir=rtl] .mat-calendar-body-comparison-bridge-start:before{background:linear-gradient(to left,rgba(244,67,54,.2) 50%,rgba(249,171,0,.2) 50%)}.mat-datepicker-content.mat-warn .mat-calendar-body-in-comparison-range.mat-calendar-body-in-range:after{background:#a8dab5}.mat-datepicker-content.mat-warn .mat-calendar-body-in-comparison-range>.mat-calendar-body-selected{background:rgb(69.5241935484,163.4758064516,93.9516129032)}.mat-datepicker-content.mat-warn .mat-calendar-body-selected{background-color:#f44336;color:#fff}.mat-datepicker-content.mat-warn .mat-calendar-body-disabled>.mat-calendar-body-selected{background-color:#f4433666}.mat-datepicker-content.mat-warn .mat-calendar-body-today.mat-calendar-body-selected{box-shadow:inset 0 0 0 1px #fff}.mat-datepicker-content-touch{box-shadow:0 0 #0003,0 0 #00000024,0 0 #0000001f}.mat-datepicker-toggle-active{color:#121a27}.mat-datepicker-toggle-active.mat-accent{color:#ff5252}.mat-datepicker-toggle-active.mat-warn{color:#f44336}.mat-date-range-input-inner[disabled]{color:#00000061}.mat-dialog-container{box-shadow:0 11px 15px -7px #0003,0 24px 38px 3px #00000024,0 9px 46px 8px #0000001f;background:white;color:#000000de}.mat-divider{border-top-color:#0000001f}.mat-divider-vertical{border-right-color:#0000001f}.mat-expansion-panel{background:white;color:#000000de}.mat-expansion-panel:not([class*=mat-elevation-z]){box-shadow:0 3px 1px -2px #0003,0 2px 2px #00000024,0 1px 5px #0000001f}.mat-action-row{border-top-color:#0000001f}.mat-expansion-panel .mat-expansion-panel-header.cdk-keyboard-focused:not([aria-disabled=true]),.mat-expansion-panel .mat-expansion-panel-header.cdk-program-focused:not([aria-disabled=true]),.mat-expansion-panel:not(.mat-expanded) .mat-expansion-panel-header:hover:not([aria-disabled=true]){background:rgba(0,0,0,.04)}@media (hover: none){.mat-expansion-panel:not(.mat-expanded):not([aria-disabled=true]) .mat-expansion-panel-header:hover{background:white}}.mat-expansion-panel-header-title{color:#000000de}.mat-expansion-panel-header-description,.mat-expansion-indicator:after{color:#0000008a}.mat-expansion-panel-header[aria-disabled=true]{color:#00000042}.mat-expansion-panel-header[aria-disabled=true] .mat-expansion-panel-header-title,.mat-expansion-panel-header[aria-disabled=true] .mat-expansion-panel-header-description{color:inherit}.mat-expansion-panel-header{height:48px}.mat-expansion-panel-header.mat-expanded{height:64px}.mat-form-field-label{color:#0009}.mat-hint{color:#0009}.mat-form-field.mat-focused .mat-form-field-label{color:#121a27}.mat-form-field.mat-focused .mat-form-field-label.mat-accent{color:#ff5252}.mat-form-field.mat-focused .mat-form-field-label.mat-warn{color:#f44336}.mat-focused .mat-form-field-required-marker{color:#ff5252}.mat-form-field-ripple{background-color:#000000de}.mat-form-field.mat-focused .mat-form-field-ripple{background-color:#121a27}.mat-form-field.mat-focused .mat-form-field-ripple.mat-accent{background-color:#ff5252}.mat-form-field.mat-focused .mat-form-field-ripple.mat-warn{background-color:#f44336}.mat-form-field-type-mat-native-select.mat-focused:not(.mat-form-field-invalid) .mat-form-field-infix:after{color:#121a27}.mat-form-field-type-mat-native-select.mat-focused:not(.mat-form-field-invalid).mat-accent .mat-form-field-infix:after{color:#ff5252}.mat-form-field-type-mat-native-select.mat-focused:not(.mat-form-field-invalid).mat-warn .mat-form-field-infix:after{color:#f44336}.mat-form-field.mat-form-field-invalid .mat-form-field-label{color:#f44336}.mat-form-field.mat-form-field-invalid .mat-form-field-label.mat-accent,.mat-form-field.mat-form-field-invalid .mat-form-field-label .mat-form-field-required-marker{color:#f44336}.mat-form-field.mat-form-field-invalid .mat-form-field-ripple,.mat-form-field.mat-form-field-invalid .mat-form-field-ripple.mat-accent{background-color:#f44336}.mat-error{color:#f44336}.mat-form-field-appearance-legacy .mat-form-field-label{color:#0000008a}.mat-form-field-appearance-legacy .mat-hint{color:#0000008a}.mat-form-field-appearance-legacy .mat-form-field-underline{background-color:#0000006b}.mat-form-field-appearance-legacy.mat-form-field-disabled .mat-form-field-underline{background-image:linear-gradient(to right,rgba(0,0,0,.42) 0%,rgba(0,0,0,.42) 33%,transparent 0%);background-size:4px 100%;background-repeat:repeat-x}.mat-form-field-appearance-standard .mat-form-field-underline{background-color:#0000006b}.mat-form-field-appearance-standard.mat-form-field-disabled .mat-form-field-underline{background-image:linear-gradient(to right,rgba(0,0,0,.42) 0%,rgba(0,0,0,.42) 33%,transparent 0%);background-size:4px 100%;background-repeat:repeat-x}.mat-form-field-appearance-fill .mat-form-field-flex{background-color:#0000000a}.mat-form-field-appearance-fill.mat-form-field-disabled .mat-form-field-flex{background-color:#00000005}.mat-form-field-appearance-fill .mat-form-field-underline:before{background-color:#0000006b}.mat-form-field-appearance-fill.mat-form-field-disabled .mat-form-field-label{color:#00000061}.mat-form-field-appearance-fill.mat-form-field-disabled .mat-form-field-underline:before{background-color:transparent}.mat-form-field-appearance-outline .mat-form-field-outline{color:#0000001f}.mat-form-field-appearance-outline .mat-form-field-outline-thick{color:#000000de}.mat-form-field-appearance-outline.mat-focused .mat-form-field-outline-thick{color:#121a27}.mat-form-field-appearance-outline.mat-focused.mat-accent .mat-form-field-outline-thick{color:#ff5252}.mat-form-field-appearance-outline.mat-focused.mat-warn .mat-form-field-outline-thick{color:#f44336}.mat-form-field-appearance-outline.mat-form-field-invalid.mat-form-field-invalid .mat-form-field-outline-thick{color:#f44336}.mat-form-field-appearance-outline.mat-form-field-disabled .mat-form-field-label{color:#00000061}.mat-form-field-appearance-outline.mat-form-field-disabled .mat-form-field-outline{color:#0000000f}.mat-icon.mat-primary{color:#121a27}.mat-icon.mat-accent{color:#ff5252}.mat-icon.mat-warn{color:#f44336}.mat-form-field-type-mat-native-select .mat-form-field-infix:after{color:#0000008a}.mat-input-element:disabled,.mat-form-field-type-mat-native-select.mat-form-field-disabled .mat-form-field-infix:after{color:#00000061}.mat-input-element{caret-color:#121a27}.mat-input-element::placeholder{color:#0000006b}.mat-input-element::-moz-placeholder{color:#0000006b}.mat-input-element::-webkit-input-placeholder{color:#0000006b}.mat-input-element:-ms-input-placeholder{color:#0000006b}.mat-form-field.mat-accent .mat-input-element{caret-color:#ff5252}.mat-form-field.mat-warn .mat-input-element,.mat-form-field-invalid .mat-input-element{caret-color:#f44336}.mat-form-field-type-mat-native-select.mat-form-field-invalid .mat-form-field-infix:after{color:#f44336}.mat-list-base .mat-list-item{color:#000000de}.mat-list-base .mat-list-option{color:#000000de}.mat-list-base .mat-subheader{color:#0000008a}.mat-list-item-disabled{background-color:#eee}.mat-list-option:hover,.mat-list-option:focus,.mat-nav-list .mat-list-item:hover,.mat-nav-list .mat-list-item:focus,.mat-action-list .mat-list-item:hover,.mat-action-list .mat-list-item:focus{background:rgba(0,0,0,.04)}.mat-list-single-selected-option,.mat-list-single-selected-option:hover,.mat-list-single-selected-option:focus{background:rgba(0,0,0,.12)}.mat-menu-panel{background:white}.mat-menu-panel:not([class*=mat-elevation-z]){box-shadow:0 2px 4px -1px #0003,0 4px 5px #00000024,0 1px 10px #0000001f}.mat-menu-item{background:transparent;color:#000000de}.mat-menu-item[disabled],.mat-menu-item[disabled]:after{color:#00000061}.mat-menu-item .mat-icon-no-color,.mat-menu-item-submenu-trigger:after{color:#0000008a}.mat-menu-item:hover:not([disabled]),.mat-menu-item.cdk-program-focused:not([disabled]),.mat-menu-item.cdk-keyboard-focused:not([disabled]),.mat-menu-item-highlighted:not([disabled]){background:rgba(0,0,0,.04)}.mat-paginator{background:white}.mat-paginator,.mat-paginator-page-size .mat-select-trigger{color:#0000008a}.mat-paginator-decrement,.mat-paginator-increment{border-top:2px solid rgba(0,0,0,.54);border-right:2px solid rgba(0,0,0,.54)}.mat-paginator-first,.mat-paginator-last{border-top:2px solid rgba(0,0,0,.54)}.mat-icon-button[disabled] .mat-paginator-decrement,.mat-icon-button[disabled] .mat-paginator-increment,.mat-icon-button[disabled] .mat-paginator-first,.mat-icon-button[disabled] .mat-paginator-last{border-color:#00000061}.mat-paginator-container{min-height:56px}.mat-progress-bar-background{fill:#595f68}.mat-progress-bar-buffer{background-color:#595f68}.mat-progress-bar-fill:after{background-color:#121a27}.mat-progress-bar.mat-accent .mat-progress-bar-background{fill:#ff6c6c}.mat-progress-bar.mat-accent .mat-progress-bar-buffer{background-color:#ff6c6c}.mat-progress-bar.mat-accent .mat-progress-bar-fill:after{background-color:#ff5252}.mat-progress-bar.mat-warn .mat-progress-bar-background{fill:#ffcdd2}.mat-progress-bar.mat-warn .mat-progress-bar-buffer{background-color:#ffcdd2}.mat-progress-bar.mat-warn .mat-progress-bar-fill:after{background-color:#f44336}.mat-progress-spinner circle,.mat-spinner circle{stroke:#121a27}.mat-progress-spinner.mat-accent circle,.mat-spinner.mat-accent circle{stroke:#ff5252}.mat-progress-spinner.mat-warn circle,.mat-spinner.mat-warn circle{stroke:#f44336}.mat-radio-outer-circle{border-color:#0000008a}.mat-radio-button.mat-primary.mat-radio-checked .mat-radio-outer-circle{border-color:#121a27}.mat-radio-button.mat-primary .mat-radio-inner-circle,.mat-radio-button.mat-primary .mat-radio-ripple .mat-ripple-element:not(.mat-radio-persistent-ripple),.mat-radio-button.mat-primary.mat-radio-checked .mat-radio-persistent-ripple,.mat-radio-button.mat-primary:active .mat-radio-persistent-ripple{background-color:#121a27}.mat-radio-button.mat-accent.mat-radio-checked .mat-radio-outer-circle{border-color:#ff5252}.mat-radio-button.mat-accent .mat-radio-inner-circle,.mat-radio-button.mat-accent .mat-radio-ripple .mat-ripple-element:not(.mat-radio-persistent-ripple),.mat-radio-button.mat-accent.mat-radio-checked .mat-radio-persistent-ripple,.mat-radio-button.mat-accent:active .mat-radio-persistent-ripple{background-color:#ff5252}.mat-radio-button.mat-warn.mat-radio-checked .mat-radio-outer-circle{border-color:#f44336}.mat-radio-button.mat-warn .mat-radio-inner-circle,.mat-radio-button.mat-warn .mat-radio-ripple .mat-ripple-element:not(.mat-radio-persistent-ripple),.mat-radio-button.mat-warn.mat-radio-checked .mat-radio-persistent-ripple,.mat-radio-button.mat-warn:active .mat-radio-persistent-ripple{background-color:#f44336}.mat-radio-button.mat-radio-disabled.mat-radio-checked .mat-radio-outer-circle,.mat-radio-button.mat-radio-disabled .mat-radio-outer-circle{border-color:#00000061}.mat-radio-button.mat-radio-disabled .mat-radio-ripple .mat-ripple-element,.mat-radio-button.mat-radio-disabled .mat-radio-inner-circle{background-color:#00000061}.mat-radio-button.mat-radio-disabled .mat-radio-label-content{color:#00000061}.mat-radio-button .mat-ripple-element{background-color:#000}.mat-select-value{color:#000000de}.mat-select-placeholder{color:#0000006b}.mat-select-disabled .mat-select-value{color:#00000061}.mat-select-arrow{color:#0000008a}.mat-select-panel{background:white}.mat-select-panel:not([class*=mat-elevation-z]){box-shadow:0 2px 4px -1px #0003,0 4px 5px #00000024,0 1px 10px #0000001f}.mat-select-panel .mat-option.mat-selected:not(.mat-option-multiple){background:rgba(0,0,0,.12)}.mat-form-field.mat-focused.mat-primary .mat-select-arrow{color:#121a27}.mat-form-field.mat-focused.mat-accent .mat-select-arrow{color:#ff5252}.mat-form-field.mat-focused.mat-warn .mat-select-arrow{color:#f44336}.mat-form-field .mat-select.mat-select-invalid .mat-select-arrow{color:#f44336}.mat-form-field .mat-select.mat-select-disabled .mat-select-arrow{color:#00000061}.mat-drawer-container{background-color:#fafafa;color:#000000de}.mat-drawer{background-color:#fff;color:#000000de}.mat-drawer.mat-drawer-push{background-color:#fff}.mat-drawer:not(.mat-drawer-side){box-shadow:0 8px 10px -5px #0003,0 16px 24px 2px #00000024,0 6px 30px 5px #0000001f}.mat-drawer-side{border-right:solid 1px rgba(0,0,0,.12)}.mat-drawer-side.mat-drawer-end{border-left:solid 1px rgba(0,0,0,.12);border-right:none}[dir=rtl] .mat-drawer-side{border-left:solid 1px rgba(0,0,0,.12);border-right:none}[dir=rtl] .mat-drawer-side.mat-drawer-end{border-left:none;border-right:solid 1px rgba(0,0,0,.12)}.mat-drawer-backdrop.mat-drawer-shown{background-color:#0009}.mat-slide-toggle.mat-checked .mat-slide-toggle-thumb{background-color:#ff5252}.mat-slide-toggle.mat-checked .mat-slide-toggle-bar{background-color:#ff52528a}.mat-slide-toggle.mat-checked .mat-ripple-element{background-color:#ff5252}.mat-slide-toggle.mat-primary.mat-checked .mat-slide-toggle-thumb{background-color:#121a27}.mat-slide-toggle.mat-primary.mat-checked .mat-slide-toggle-bar{background-color:#121a278a}.mat-slide-toggle.mat-primary.mat-checked .mat-ripple-element{background-color:#121a27}.mat-slide-toggle.mat-warn.mat-checked .mat-slide-toggle-thumb{background-color:#f44336}.mat-slide-toggle.mat-warn.mat-checked .mat-slide-toggle-bar{background-color:#f443368a}.mat-slide-toggle.mat-warn.mat-checked .mat-ripple-element{background-color:#f44336}.mat-slide-toggle:not(.mat-checked) .mat-ripple-element{background-color:#000}.mat-slide-toggle-thumb{box-shadow:0 2px 1px -1px #0003,0 1px 1px #00000024,0 1px 3px #0000001f;background-color:#fafafa}.mat-slide-toggle-bar{background-color:#00000061}.mat-slider-track-background{background-color:#00000042}.mat-primary .mat-slider-track-fill,.mat-primary .mat-slider-thumb,.mat-primary .mat-slider-thumb-label{background-color:#121a27}.mat-primary .mat-slider-thumb-label-text{color:#fff}.mat-primary .mat-slider-focus-ring{background-color:#121a2733}.mat-accent .mat-slider-track-fill,.mat-accent .mat-slider-thumb,.mat-accent .mat-slider-thumb-label{background-color:#ff5252}.mat-accent .mat-slider-thumb-label-text{color:#fff}.mat-accent .mat-slider-focus-ring{background-color:#ff525233}.mat-warn .mat-slider-track-fill,.mat-warn .mat-slider-thumb,.mat-warn .mat-slider-thumb-label{background-color:#f44336}.mat-warn .mat-slider-thumb-label-text{color:#fff}.mat-warn .mat-slider-focus-ring{background-color:#f4433633}.mat-slider:hover .mat-slider-track-background,.cdk-focused .mat-slider-track-background{background-color:#00000061}.mat-slider-disabled .mat-slider-track-background,.mat-slider-disabled .mat-slider-track-fill,.mat-slider-disabled .mat-slider-thumb{background-color:#00000042}.mat-slider-disabled:hover .mat-slider-track-background{background-color:#00000042}.mat-slider-min-value .mat-slider-focus-ring{background-color:#0000001f}.mat-slider-min-value.mat-slider-thumb-label-showing .mat-slider-thumb,.mat-slider-min-value.mat-slider-thumb-label-showing .mat-slider-thumb-label{background-color:#000000de}.mat-slider-min-value.mat-slider-thumb-label-showing.cdk-focused .mat-slider-thumb,.mat-slider-min-value.mat-slider-thumb-label-showing.cdk-focused .mat-slider-thumb-label{background-color:#00000042}.mat-slider-min-value:not(.mat-slider-thumb-label-showing) .mat-slider-thumb{border-color:#00000042;background-color:transparent}.mat-slider-min-value:not(.mat-slider-thumb-label-showing):hover .mat-slider-thumb,.mat-slider-min-value:not(.mat-slider-thumb-label-showing).cdk-focused .mat-slider-thumb{border-color:#00000061}.mat-slider-min-value:not(.mat-slider-thumb-label-showing):hover.mat-slider-disabled .mat-slider-thumb,.mat-slider-min-value:not(.mat-slider-thumb-label-showing).cdk-focused.mat-slider-disabled .mat-slider-thumb{border-color:#00000042}.mat-slider-has-ticks .mat-slider-wrapper:after{border-color:#000000b3}.mat-slider-horizontal .mat-slider-ticks{background-image:repeating-linear-gradient(to right,rgba(0,0,0,.7),rgba(0,0,0,.7) 2px,transparent 0,transparent);background-image:-moz-repeating-linear-gradient(.0001deg,rgba(0,0,0,.7),rgba(0,0,0,.7) 2px,transparent 0,transparent)}.mat-slider-vertical .mat-slider-ticks{background-image:repeating-linear-gradient(to bottom,rgba(0,0,0,.7),rgba(0,0,0,.7) 2px,transparent 0,transparent)}.mat-step-header.cdk-keyboard-focused,.mat-step-header.cdk-program-focused,.mat-step-header:hover{background-color:#0000000a}@media (hover: none){.mat-step-header:hover{background:none}}.mat-step-header .mat-step-label,.mat-step-header .mat-step-optional{color:#0000008a}.mat-step-header .mat-step-icon{background-color:#0000008a;color:#fff}.mat-step-header .mat-step-icon-selected,.mat-step-header .mat-step-icon-state-done,.mat-step-header .mat-step-icon-state-edit{background-color:#121a27;color:#fff}.mat-step-header .mat-step-icon-state-error{background-color:transparent;color:#f44336}.mat-step-header .mat-step-label.mat-step-label-active{color:#000000de}.mat-step-header .mat-step-label.mat-step-label-error{color:#f44336}.mat-stepper-horizontal,.mat-stepper-vertical{background-color:#fff}.mat-stepper-vertical-line:before{border-left-color:#0000001f}.mat-horizontal-stepper-header:before,.mat-horizontal-stepper-header:after,.mat-stepper-horizontal-line{border-top-color:#0000001f}.mat-horizontal-stepper-header{height:72px}.mat-stepper-label-position-bottom .mat-horizontal-stepper-header,.mat-vertical-stepper-header{padding:24px}.mat-stepper-vertical-line:before{top:-16px;bottom:-16px}.mat-stepper-label-position-bottom .mat-horizontal-stepper-header:after,.mat-stepper-label-position-bottom .mat-horizontal-stepper-header:before{top:36px}.mat-stepper-label-position-bottom .mat-stepper-horizontal-line{top:36px}.mat-sort-header-arrow{color:#757575}.mat-tab-nav-bar,.mat-tab-header{border-bottom:1px solid rgba(0,0,0,.12)}.mat-tab-group-inverted-header .mat-tab-nav-bar,.mat-tab-group-inverted-header .mat-tab-header{border-top:1px solid rgba(0,0,0,.12);border-bottom:none}.mat-tab-label,.mat-tab-link{color:#000000de}.mat-tab-label.mat-tab-disabled,.mat-tab-link.mat-tab-disabled{color:#00000061}.mat-tab-header-pagination-chevron{border-color:#000000de}.mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron{border-color:#00000061}.mat-tab-group[class*=mat-background-] .mat-tab-header,.mat-tab-nav-bar[class*=mat-background-]{border-bottom:none;border-top:none}.mat-tab-group.mat-primary .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-primary .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-primary .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-primary .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-primary .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-primary .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-primary .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-primary .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#595f684d}.mat-tab-group.mat-primary .mat-ink-bar,.mat-tab-nav-bar.mat-primary .mat-ink-bar{background-color:#121a27}.mat-tab-group.mat-primary.mat-background-primary .mat-ink-bar,.mat-tab-nav-bar.mat-primary.mat-background-primary .mat-ink-bar{background-color:#fff}.mat-tab-group.mat-accent .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-accent .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-accent .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-accent .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-accent .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-accent .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-accent .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-accent .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#ff6c6c4d}.mat-tab-group.mat-accent .mat-ink-bar,.mat-tab-nav-bar.mat-accent .mat-ink-bar{background-color:#ff5252}.mat-tab-group.mat-accent.mat-background-accent .mat-ink-bar,.mat-tab-nav-bar.mat-accent.mat-background-accent .mat-ink-bar{background-color:#fff}.mat-tab-group.mat-warn .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-warn .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-warn .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-warn .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-warn .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-warn .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-warn .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-warn .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#ffcdd24d}.mat-tab-group.mat-warn .mat-ink-bar,.mat-tab-nav-bar.mat-warn .mat-ink-bar{background-color:#f44336}.mat-tab-group.mat-warn.mat-background-warn .mat-ink-bar,.mat-tab-nav-bar.mat-warn.mat-background-warn .mat-ink-bar{background-color:#fff}.mat-tab-group.mat-background-primary .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-primary .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-primary .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-primary .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-primary .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-primary .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-primary .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-primary .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#595f684d}.mat-tab-group.mat-background-primary .mat-tab-header,.mat-tab-group.mat-background-primary .mat-tab-links,.mat-tab-group.mat-background-primary .mat-tab-header-pagination,.mat-tab-nav-bar.mat-background-primary .mat-tab-header,.mat-tab-nav-bar.mat-background-primary .mat-tab-links,.mat-tab-nav-bar.mat-background-primary .mat-tab-header-pagination{background-color:#121a27}.mat-tab-group.mat-background-primary .mat-tab-label,.mat-tab-group.mat-background-primary .mat-tab-link,.mat-tab-nav-bar.mat-background-primary .mat-tab-label,.mat-tab-nav-bar.mat-background-primary .mat-tab-link{color:#fff}.mat-tab-group.mat-background-primary .mat-tab-label.mat-tab-disabled,.mat-tab-group.mat-background-primary .mat-tab-link.mat-tab-disabled,.mat-tab-nav-bar.mat-background-primary .mat-tab-label.mat-tab-disabled,.mat-tab-nav-bar.mat-background-primary .mat-tab-link.mat-tab-disabled{color:#fff6}.mat-tab-group.mat-background-primary .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-primary .mat-tab-header-pagination-chevron{border-color:#fff}.mat-tab-group.mat-background-primary .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-primary .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron{border-color:#fff6}.mat-tab-group.mat-background-primary .mat-ripple-element,.mat-tab-nav-bar.mat-background-primary .mat-ripple-element{background-color:#ffffff1f}.mat-tab-group.mat-background-accent .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-accent .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-accent .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-accent .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-accent .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-accent .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-accent .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-accent .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#ff6c6c4d}.mat-tab-group.mat-background-accent .mat-tab-header,.mat-tab-group.mat-background-accent .mat-tab-links,.mat-tab-group.mat-background-accent .mat-tab-header-pagination,.mat-tab-nav-bar.mat-background-accent .mat-tab-header,.mat-tab-nav-bar.mat-background-accent .mat-tab-links,.mat-tab-nav-bar.mat-background-accent .mat-tab-header-pagination{background-color:#ff5252}.mat-tab-group.mat-background-accent .mat-tab-label,.mat-tab-group.mat-background-accent .mat-tab-link,.mat-tab-nav-bar.mat-background-accent .mat-tab-label,.mat-tab-nav-bar.mat-background-accent .mat-tab-link{color:#fff}.mat-tab-group.mat-background-accent .mat-tab-label.mat-tab-disabled,.mat-tab-group.mat-background-accent .mat-tab-link.mat-tab-disabled,.mat-tab-nav-bar.mat-background-accent .mat-tab-label.mat-tab-disabled,.mat-tab-nav-bar.mat-background-accent .mat-tab-link.mat-tab-disabled{color:#fff6}.mat-tab-group.mat-background-accent .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-accent .mat-tab-header-pagination-chevron{border-color:#fff}.mat-tab-group.mat-background-accent .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-accent .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron{border-color:#fff6}.mat-tab-group.mat-background-accent .mat-ripple-element,.mat-tab-nav-bar.mat-background-accent .mat-ripple-element{background-color:#ffffff1f}.mat-tab-group.mat-background-warn .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-warn .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-warn .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-group.mat-background-warn .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-warn .mat-tab-label.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-warn .mat-tab-label.cdk-program-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-warn .mat-tab-link.cdk-keyboard-focused:not(.mat-tab-disabled),.mat-tab-nav-bar.mat-background-warn .mat-tab-link.cdk-program-focused:not(.mat-tab-disabled){background-color:#ffcdd24d}.mat-tab-group.mat-background-warn .mat-tab-header,.mat-tab-group.mat-background-warn .mat-tab-links,.mat-tab-group.mat-background-warn .mat-tab-header-pagination,.mat-tab-nav-bar.mat-background-warn .mat-tab-header,.mat-tab-nav-bar.mat-background-warn .mat-tab-links,.mat-tab-nav-bar.mat-background-warn .mat-tab-header-pagination{background-color:#f44336}.mat-tab-group.mat-background-warn .mat-tab-label,.mat-tab-group.mat-background-warn .mat-tab-link,.mat-tab-nav-bar.mat-background-warn .mat-tab-label,.mat-tab-nav-bar.mat-background-warn .mat-tab-link{color:#fff}.mat-tab-group.mat-background-warn .mat-tab-label.mat-tab-disabled,.mat-tab-group.mat-background-warn .mat-tab-link.mat-tab-disabled,.mat-tab-nav-bar.mat-background-warn .mat-tab-label.mat-tab-disabled,.mat-tab-nav-bar.mat-background-warn .mat-tab-link.mat-tab-disabled{color:#fff6}.mat-tab-group.mat-background-warn .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-warn .mat-tab-header-pagination-chevron{border-color:#fff}.mat-tab-group.mat-background-warn .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron,.mat-tab-nav-bar.mat-background-warn .mat-tab-header-pagination-disabled .mat-tab-header-pagination-chevron{border-color:#fff6}.mat-tab-group.mat-background-warn .mat-ripple-element,.mat-tab-nav-bar.mat-background-warn .mat-ripple-element{background-color:#ffffff1f}.mat-toolbar{background:whitesmoke;color:#000000de}.mat-toolbar.mat-primary{background:#121a27;color:#fff}.mat-toolbar.mat-accent{background:#ff5252;color:#fff}.mat-toolbar.mat-warn{background:#f44336;color:#fff}.mat-toolbar .mat-form-field-underline,.mat-toolbar .mat-form-field-ripple,.mat-toolbar .mat-focused .mat-form-field-ripple{background-color:currentColor}.mat-toolbar .mat-form-field-label,.mat-toolbar .mat-focused .mat-form-field-label,.mat-toolbar .mat-select-value,.mat-toolbar .mat-select-arrow,.mat-toolbar .mat-form-field.mat-focused .mat-select-arrow{color:inherit}.mat-toolbar .mat-input-element{caret-color:currentColor}.mat-toolbar-multiple-rows{min-height:64px}.mat-toolbar-row,.mat-toolbar-single-row{height:64px}@media (max-width: 599px){.mat-toolbar-multiple-rows{min-height:56px}.mat-toolbar-row,.mat-toolbar-single-row{height:56px}}.mat-tooltip{background:rgba(97,97,97,.9)}.mat-tree{background:white}.mat-tree-node,.mat-nested-tree-node{color:#000000de}.mat-tree-node{min-height:48px}.mat-snack-bar-container{color:#ffffffb3;background:#323232;box-shadow:0 3px 5px -1px #0003,0 6px 10px #00000024,0 1px 18px #0000001f}.mat-simple-snackbar-action{color:#ff5252}body{font-family:\"Helvetica Neue\",sans-serif;text-rendering:optimizeLegibility;-webkit-font-smoothing:auto;color:#000}a{color:#000}.error{color:#ff5252}:root{--brand: #FFFFFF;--brand-bg: #121a27}h2{font-size:1rem}*{box-sizing:border-box}body,html{padding:0;margin:0;height:100%;background-color:transparent!important}.homepage{background-color:#f4f7fa}.badge{font-weight:normal}#content{width:100%;height:100%;overflow-y:scroll;-webkit-overflow-scrolling:touch}.load-progress{text-align:center;margin-top:10px;font-size:12px;font-weight:bold}.tooltip-inner{white-space:nowrap!important;max-width:none!important}.dark-modal{text-align:center}.dark-modal .modal-content{background-color:#000000d9;color:#fff}.dark-modal .modal-header{border-bottom-color:#ffffff0f}.dark-modal .modal-footer{border-top-color:#ffffff0f!important}.dark-modal .close{color:#fff}.modal-bg{background-color:#00000080}.blurred{filter:blur(10px)}.player-light>.controls>.btn-dark,.player-light>.controls>.dropdown>button{background-color:#ffffff80!important}.player-light>.controls>.dropdown-menu{background-color:#ffffffd9!important}.player-light .ar-button{background-color:#ffffff80}.player-light .ar-button:hover{background-color:#fff9}.player-dark .ar-button{background-color:#00000080}.player-dark .ar-button:hover{background-color:#0009}[type=button],[type=reset],[type=submit],button{-webkit-appearance:initial}.windowCentered{position:fixed;text-align:center;top:50%;left:50%;transform:translate(-50%,-50%)}.dialogCentered{position:fixed;text-align:center;top:calc(50% + 25px);left:50%;transform:translate(-50%,-50%)}.material-icons.mat-icon-64{font-size:64px;height:64px;width:64px;position:relative}.material-icons.mat-icon-128{font-size:128px;height:128px;width:128px;position:relative}#webPlayerContainer,.viewer{position:fixed;width:100vw;height:100vh;max-height:-webkit-fill-available}.toast{flex-basis:350px;max-width:350px;font-size:.875rem;background-color:#ffffffd9;background-clip:padding-box;border:1px solid rgba(0,0,0,.1);box-shadow:0 .25rem .75rem #0000001a;opacity:0;border-radius:.25rem}.toast-body{padding:.75rem}.bg-black{background-color:#000000d9}.mat-raised-button.mat-primary{background-color:var(--brand-bg);color:var(--brand)}.password-dialog{width:450px}.password-dialog .mat-dialog-container{background-color:#f6f6f7;border-radius:15px;position:relative;overflow:visible}.password-dialog-background{background-color:#f1f5f9}.smartvu-dialog-container .mat-dialog-container{width:400px;padding:0;border-radius:10px}@media only screen and (max-height: 768px){.smartvu-dialog-container .mat-dialog-container{width:300px;max-height:600px}}@media only screen and (max-height: 414px){.smartvu-dialog-container .mat-dialog-container{width:175px;max-height:325px}}@media only screen and (max-width: 992px){h2{font-size:1.2rem!important}h5{font-size:1rem!important}}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-global-styling',
                    templateUrl: './global-styling.component.html',
                    styleUrls: ['./global-styling.component.scss'],
                    encapsulation: ViewEncapsulation.None
                }]
        }], ctorParameters: function () { return []; } });

/**
 * A Vuframe CMS Share object referencing a public SmartVu object.
 */
class Share {
    /**
     * Constructs a Share object
     * @param json serialized Share returned by the Vuframe API
     */
    constructor(json) {
        this._shareCode = json.share_code;
        this._projectToken = json.project;
        this._title = json.title;
        this._description = json.description;
        this._previewURL = json.preview_image_url;
        this._qrCodeURL = json.qr_code_url;
        this._type = json.type;
        this._targetEmpty = json.empty;
        if (json.payload && json.payload.token) {
            this._targetPassword = json.payload.password;
            this._targetIdentifier = json.payload.token;
            this._targetType = json.payload.template_identifier;
        }
    }
    /**
     * Checks whether a Share object's target exists and is valid
     * @returns true if the Share object's target is valid
     */
    isValid() {
        return (!this._targetEmpty &&
            Share.IsValidShareCode(this._shareCode) &&
            this._targetIdentifier != null &&
            this._targetType != null);
    }
    /**
     * Check whether the Share object's target can be displayed by the Web Viewer*
     * @returns true if the Web Viewer is able to display the Share object's target
     */
    isSupported() {
        return Share.SUPPORTED_TYPES.indexOf(this._type) !== -1;
    }
    /**
     * Checks whether the given string is a valid Vuframe share code (8 character hexadecimal string)
     * @param code the string that should be validated
     * @returns true if the string is a valid share code
     */
    static IsValidShareCode(code) {
        return /^[a-fA-F0-9]{8}$/.test(code);
    }
    /**
     * Getter and Setter methods
     */
    get shareCode() {
        return this._shareCode;
    }
    get projectToken() {
        return this._projectToken;
    }
    get title() {
        return this._title;
    }
    get description() {
        return this._description;
    }
    get previewURL() {
        return this._previewURL;
    }
    get qrCodeURL() {
        return this._qrCodeURL;
    }
    get targetType() {
        return this._targetType;
    }
    get targetIdentifier() {
        return this._targetIdentifier;
    }
    get targetPassword() {
        return this._targetPassword;
    }
}
/**
 * Supported types of Share objects (Currently only shared SmartVus are supported, but the Vuframe CMS technically supports sharing Items as well. This feature is not implemented at the moment, though.)
 */
Share.SUPPORTED_TYPES = ['experience'];

/**
 * Abstract class that designates classes as a JSON parseable entity retrieved from the Vuframe API
 */
class APIEntity {
    /**
     * Creates a new APIEntity objet
     * @param json the serialized APIEntity object returned by the Vuframe API
     */
    constructor(json) {
        this.json = json;
    }
}

/**
 * Generic base class for Item objects retrieved from the Vuframe API. Contains a download URL to a file as well as meta information such as title and preview image of the Item.
 */
class Item extends APIEntity {
    /**
     * Constructs a new Item
     * @param json serialized Item returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._identifier = json.token;
        this._title = json.title;
        this._previewURL = json.preview_image_url;
        this._mimeType = json.content_mime_type;
        const is3DItem = json.item_type == "any3d_scene";
        if (json.properties) {
            if (json.properties.additional_formats) {
                if (Array.isArray(json.properties.additional_formats)) {
                    this._availableFormats = json.properties.additional_formats;
                    this._filesize = json.file_size;
                }
                else {
                    this._availableFormats = Object.keys(json.properties.additional_formats);
                    var auraweb = json.properties.additional_formats['auraweb'];
                    if (auraweb) {
                        this._filesize = auraweb.filesize;
                    }
                    else {
                        var glb = json.properties.additional_formats['glb'];
                        if (glb) {
                            this._filesize = glb.filesize;
                        }
                    }
                }
            }
        }
        // add an auth hash generated from the Web Viewer's API access tokens to the download URL
        // this is required in order to be able download files from the Vuframe CMS
        // add cloudfront parameter
        this._downloadURL = addURLParams(json.content_url, {
            auth: getVuframeAuthHash(),
            cloudfront: (useCloudFrontCDN() && is3DItem) ? 1 : 0,
        });
    }
    /**
     * Getter and Setter methods
     */
    get identifier() {
        return this._identifier;
    }
    get title() {
        return this._title;
    }
    get filesize() {
        return this._filesize;
    }
    get previewURL() {
        return this._previewURL;
    }
    get downloadURL() {
        return this._downloadURL;
    }
    get mimeType() {
        return this._mimeType;
    }
    get parent() {
        return this._parent;
    }
    set parent(smartVu) {
        this._parent = smartVu;
    }
}

/**
 * Item containing a 3D model as well as its meta data.
 */
class ModelItem extends Item {
    /**
     * Constructs a new ModelItem
     * @param json serialized ModelItem returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._config = {
            triggers: this.parseTriggers(json),
            optionTriggerMapping: {},
            eventTriggerMapping: {},
            version: json.version
        };
    }
    /**
     * Parses the models trigger information from the serialized Item as a JSON objects.
     * @param json the serialized ModelItem
     * @returns a list of VFTriggerMeta objects describing the model's trigger objects
     */
    parseTriggers(json) {
        const triggers = [];
        if (json.properties && json.properties.triggers) {
            // create a new VFTriggerMeta object for each trigger in the JSON item
            json.properties.triggers.forEach(trigger => {
                triggers.push({
                    name: trigger.name,
                    type: trigger.type,
                    enableScreenButton: false,
                    customButtonTextureHash: 0,
                    forwardsToTrigger: null,
                    lookDirection: null,
                });
            });
        }
        return triggers;
    }
    /**
     * Checks whether the model can be displayed (needs to be available as GLB)
     * @returns true if the model is available as a GLB file
     */
    isSupported() {
        const supported = this._availableFormats && (this._availableFormats.indexOf('glb') !== -1 || this._availableFormats.indexOf('auraweb') !== -1);
        if (supported === false) {
            Logger.warn('unsupported model ' + this._title + ' -> ' + this._availableFormats);
        }
        return supported;
    }
    /**
     * Checks whether the model can be displayed in iOS QuickLook (needs to be available as USDZ)
     * @returns true if the model is available as a USDZ file
     */
    supportsQuickLook() {
        return this._availableFormats && this._availableFormats.indexOf('usdz') !== -1;
    }
    /**
     * Getter and setter methods
     */
    get config() {
        return this._config;
    }
}

/**
 * Abstract base class implementing shared functionality for Actions, Widgets and Extensions
 */
class SmartVuElement extends APIEntity {
    /**
     * Constructs a new SmartVuElement
     * @param json the serialized SmartVuElement object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._identifier = json.id;
        this._sortOrder = json.sort_order;
    }
    /**
     * Getter and Setter methods
     */
    get identifier() {
        return this._identifier;
    }
    get sortOrder() {
        return this._sortOrder;
    }
    get parent() {
        return this._parent;
    }
    set parent(smartVu) {
        this._parent = smartVu;
    }
}
/**
 * Abstract base class for Action objects
 */
class Action extends SmartVuElement {
}
/**
 * Abstract base class for Command objects
 */
class Command {
}
/**
 * Special subtype of Action that requires UI elements (e.g. popup dialog). This type of action is derived from Action and is also added to the sidebar, albeit without any visible UI elements.
 */
class UIAction extends SmartVuElement {
    onTrigger() {
        if (this.component) {
            this.component.onActionTriggered();
        }
    }
}

/**
 * autoplay_delay: 5
 * autoplay_enabled: true
 * stop_ids: ""
 * time_between_stops: 5
 */
class TourExtension extends SmartVuElement {
    /**
     * Constructs a new TourExtension
     * @param json the serialized ARExtension object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this.autoplayEnabled = false;
        this.autoplayEnabled = json.params['autoplay_enabled'];
        this.autoplayDelay = json.params['autoplay_delay'] || 0;
        this.timeBetweenStops = json.params['time_between_stops'] || 0;
    }
    /**
     * Enables Playback for the 3D player
     * @returns a VFPlayerConfig with enableAR set to true
     */
    toPlayerConfig() {
        return {
            enableTour: this.autoplayEnabled,
            tourDelay: this.autoplayDelay,
            tourTimeBetween: this.timeBetweenStops,
        };
    }
}

/**
 * Extension that specifies the SmartVu's background color
 */
class BackgroundExtension extends SmartVuElement {
    /**
     * Constructs a new BackgroundExtension
     * @param json the serialized BackgroundExtension object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._colorString = json.params.color;
    }
    /**
     * Sets the background color of the 3D scene
     * @returns a VFPlayerConfig with sceneClearColor set to the extension's color string
     */
    toPlayerConfig() {
        const config = {};
        if (this._colorString) {
            config.sceneClearColor = this._colorString;
        }
        return config;
    }
}

/**
 * Different possible configuration settings for the behavior of the SmartVu's sidebar
 */
var SidebarBehavior;
(function (SidebarBehavior) {
    SidebarBehavior["Enabled"] = "show_initially";
    SidebarBehavior["Hidden"] = "hide_initially";
    SidebarBehavior["Disabled"] = "disabled";
})(SidebarBehavior || (SidebarBehavior = {}));
/**
 * Abstract base class for SmartVu objects. The SmartVu is the core entity in the Vuframe CMS and contains all information required to display the interactive 3D scene as well as the sidebar.
 */
class SmartVu {
    /**
     * Constructs a new SmartVu object
     * @param _identifier unique identifier string of the SmartVu
     * @param _title title of the Smartvu
     * @param _description description of the Smartvu
     * @param _previewImageURL the URL of the SmartVu's preview image file
     * @param _hashtags hashtags of the SmartVu as a string array
     * @param _feature_type the type of the SmartVu
     */
    constructor(_identifier, _title, _description, _previewImageURL, _hashtags, _feature_type, _shareCode, _context) {
        this._identifier = _identifier;
        this._title = _title;
        this._description = _description;
        this._previewImageURL = _previewImageURL;
        this._hashtags = _hashtags;
        this._feature_type = _feature_type;
        this._shareCode = _shareCode;
        this._context = _context;
        /**
         * SmartVu Extensions
         */
        this._hasTourExtension = -1;
        this._items = {};
        this._configFields = {};
        this._actions = {};
        this._widgets = {};
        this._mappedActions = {};
        this._mappedEntries = {};
        // default sidebar behavior is to hide it initially
        this._sidebarBehavior = SidebarBehavior.Hidden;
        this._showBranding = true;
    }
    /**
     * Generates a config object for the VFWebPlayerModule from the ConfigField and Extension objects of the SmartVu.
     * @returns the player configuration as a VFPlayerConfig object
     */
    generatePlayerConfig() {
        let config = {};
        // merge partial configuration objects created by the ConfigField objects
        for (const fieldKey in this._configFields) {
            config = Object.assign(Object.assign({}, this._configFields[fieldKey].toPlayerConfig()), config);
        }
        // merge partial configuration objects created by the Extension objects
        for (const extensionKey in this._extensions) {
            config = Object.assign(Object.assign({}, this._extensions[extensionKey].toPlayerConfig()), config);
        }
        return config;
    }
    /**
     * Gets an Item object by its identifier string
     * @param identifier identifier string of the requested Item
     * @returns the Item or null if no matching object is found
     */
    getItem(identifier) {
        if (identifier in this._items) {
            return this._items[identifier];
        }
        else {
            return null;
        }
    }
    /**
     * Gets all linked ModelItems as a list
     * @returns a list containing all linked ModelItems
     */
    getAllModelItems() {
        return Object.values(this._items).filter(item => item instanceof ModelItem);
    }
    /**
     * Returns a list of all the widget elements in the SmartVu's sidebar sorted by their sortOrder attribute.
     * @returns sidebarwidgets of the SmartVu as an array of Widget objects
     */
    getSortedWidgets() {
        // TODO fix this method, we have an endless loop here
        // sort Widget objects by ascending sortOrder
        for (const language in this._widgets) {
            this._widgets[language] = Object.values(this._widgets[language]).sort((a, b) => {
                return a.sortOrder - b.sortOrder;
            });
        }
        return this._widgets;
    }
    /**
     * Returns a list of all the SmartVu's UIActions which require Angular components in order to display their UI elements.
     * @returns UIActions of the SmartVu as an array of UIAction objects
     */
    getUIActions() {
        // get all UIActions from the SmartVu's Action objects
        return Object.values(this._actions).filter((action) => {
            return action instanceof UIAction;
        });
    }
    /**
     * Activate one of the SmartVu's action objects by its identifier string
     * @param identifier the identifier string of the Action that should be triggered
     */
    dispatchAction(identifier) {
        const action = this._actions[identifier];
        if (action) {
            // execute the Action's onTrigger method
            action.onTrigger();
        }
        else {
            // write a warning message to the console if no matching Action exists for the given identifier
            Logger.warn(`action with identifier ${action} does not exist`);
        }
    }
    /**
     * Returns true if the sidebar should be available for the SmartVu
     * @returns true if sidebar is enabled
     */
    hasSidebar() {
        return this._sidebarBehavior !== SidebarBehavior.Disabled && Object.keys(this._widgets).length > 0;
    }
    /**
     * Checks the initial 3D Model of the SmartVu can be displayed.
     * @returns true if the initial model is supported.
     */
    isSupported() {
        return this.getInitialModel().isSupported();
    }
    /**
     * Checks whether all models of the SmartVu are available for iOS QuicklLok
     * @returns true if all models of the SmartVu are supported by QuickLook
     */
    supportsQuickLook() {
        return this.getAllModelItems().find(model => !model.supportsQuickLook()) == null;
    }
    /**
     * Get one of the SmartVu's ConfigFields by its identifier
     * @param identifier the identifier of the requested ConfigField
     * @returns the ConfigField
     */
    getField(identifier) {
        return this._configFields[identifier];
    }
    getSmartVuField(identifier) {
        return this.getField(identifier);
    }
    getColorField(identifier) {
        return this.getField(identifier);
    }
    getImageField(identifier) {
        return this.getField(identifier);
    }
    getStringField(identifier) {
        return this.getField(identifier);
    }
    getActionField(identifier) {
        return this.getField(identifier);
    }
    /**
     * Helper method that constructs an object mapping from an array of objects implementing the SmartVuChild interface. The resulting mapping uses the objects' identifier as keys and the objects themselves as values. Used to set the _configFields, _items, _actions, _widgets and _extensions attributes
     * @param children an array of SmartVuChild objects
     * @returns a mapping of the objects' identifiers to the objects
     */
    mapChildren(children) {
        const childrenMap = {};
        for (const child of children) {
            const identifier = child.identifier;
            child.parent = this;
            childrenMap[identifier] = child;
        }
        return childrenMap;
    }
    mapChildrenWidgets(children) {
        const childrenMap = {};
        for (const element in children) {
            childrenMap[element] = {};
            for (const widget of children[element]) {
                const identifier = widget.identifier;
                widget.parent = this;
                childrenMap[element][identifier] = widget;
            }
        }
        return childrenMap;
    }
    /**
     * Getter and Setter methods
     */
    get identifier() {
        return this._identifier;
    }
    get title() {
        return this._title;
    }
    get description() {
        return this._description;
    }
    get previewImageURL() {
        return this._previewImageURL;
    }
    get hashtags() {
        return this._hashtags;
    }
    get widgets() {
        return this._widgets;
    }
    get shareCode() {
        return this._shareCode;
    }
    get context() {
        return this._context;
    }
    get sidebarBehavior() {
        return this._sidebarBehavior;
    }
    get showBranding() {
        return this._showBranding;
    }
    get featureType() {
        return this._feature_type;
    }
    get mappedActions() {
        return this._mappedActions;
    }
    get mappedEntries() {
        return this._mappedEntries;
    }
    get backgroundExtension() {
        return Object.values(this._extensions).find((extension) => extension instanceof BackgroundExtension);
    }
    get hasTourExtension() {
        if (!this._extensions) {
            return false;
        }
        if (this._hasTourExtension < 0) {
            const tourExtensions = Object.values(this._extensions).find((extension) => extension instanceof TourExtension);
            this._hasTourExtension = tourExtensions == null ? 0 : 1;
        }
        return this._hasTourExtension > 0;
    }
    set items(items) {
        this._items = this.mapChildren(items);
    }
    set configFields(fields) {
        this._configFields = this.mapChildren(fields);
        // update model config every time the ConfigFields are set
        this.parseModelConfig();
    }
    set widgets(widgets) {
        this._widgets = this.mapChildrenWidgets(widgets);
    }
    set actions(actions) {
        this._actions = this.mapChildren(actions);
    }
    set extensions(extensions) {
        this._extensions = this.mapChildren(extensions);
    }
    set sidebarBehavior(behavior) {
        if (Object.values(SidebarBehavior).includes(behavior)) {
            this._sidebarBehavior = behavior;
        }
        else {
            Logger.warn(`value '${behavior}' for sidebar behavior is not valid, using previous value ${this._sidebarBehavior}`);
        }
    }
    set showBranding(showBranding) {
        this._showBranding = showBranding;
    }
}

/**
 * SmartVu containing a single 3D model as a ModelItem. This is the default SmartVu type used in the Vuframe CMS.
 */
class SimpleSmartVu extends SmartVu {
    /**
     * Gets a reference to the SmartVu's product field that specifies the linked ModelItem
     * @returns the ModelField defining the linked ModelItem
     */
    getModelField() {
        return this.getField(SimpleSmartVu.MODEL_FIELD);
    }
    /**
     * Gets the initial (and only) 3D model linked in the SmartVu.
     * @returns the ModelItem linked in the SmartVu
     */
    getInitialModel() {
        return this.getModel();
    }
    /**
     * Gets the ModelItem linked in the SmartVu's product field
     * @returns the ModelItem linked in the SmartVu
     */
    getModel() {
        return this._items[this.getModelField().modelToken];
    }
    /**
     * Sets the linked ModelItem's trigger mapping according to the contents of the SmartVu's ModelField.
     */
    parseModelConfig() {
        const model = this.getModel();
        // get the trigger mapping information from the ModelField
        const triggerMapping = this.getModelField().triggerMapping;
        const validatedTriggerMapping = {};
        // map the Action objects referenced in the ModelField to the trigger names
        for (const key in triggerMapping) {
            const actionIdentifier = triggerMapping[key];
            if (actionIdentifier && this._actions[actionIdentifier]) {
                validatedTriggerMapping[key] = this._actions[actionIdentifier];
            }
        }
        // set the trigger mapping of ModelItem
        model.config.eventTriggerMapping = validatedTriggerMapping;
        // directly use option mapping for model config to be used by the player
        const triggerOptionMapping = this.getModelField().triggerOptionMapping;
        if (triggerOptionMapping) {
            model.config.optionTriggerMapping = triggerOptionMapping;
        }
    }
    getExternalTextureMapping() {
        return this.getModelField().externalTextureMapping;
    }
}
/**
 * Identifier of the ModelField object containing the ModelItem linked in the SmartVu
 */
SimpleSmartVu.MODEL_FIELD = 'product';

/**
 * Abstract base class describing a ConfigField retrieved from the Vuframe API. ConfigField objects contain configuration values following to a predefined structure.
 */
class ConfigField extends APIEntity {
    /**
     * Constructs a new ConfigField
     * @param json serialized ConfigField returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._identifier = json.key;
    }
    /**
     * Generates a partial VFPlayerConfig object from the ConfigField's values. This method can be overridden by subclasses of ConfigFields.
     * @returns an empty VFPlayerConfig object
     */
    toPlayerConfig() {
        return {};
    }
    /**
     * Getter and Setter Methods
     */
    get identifier() {
        return this._identifier;
    }
    get parent() {
        return this._parent;
    }
    set parent(smartVu) {
        this._parent = smartVu;
    }
}

/**
 * A ConfigField object containing a reference to a 3D model as a ;odelItem. Also specifies the mapping of Actions to the ModelItems trigger elements.
 */
class ModelField extends ConfigField {
    /**
     * Constructs a new ModelField
     * @param json serialized ModelField returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        const model = json.value[0];
        this._modelIdentifier = model.scene_token;
        this._triggerMapping = model.scene_mapped_triggers;
        this._triggerOptionMapping = model.scene_mapped_trigger_options;
        this._externalTextureMapping = model.scene_texture_slots;
    }
    /**
     * Getter and setter methods
     */
    get externalTextureMapping() {
        return this._externalTextureMapping;
    }
    get modelToken() {
        return this._modelIdentifier;
    }
    get triggerMapping() {
        return this._triggerMapping;
    }
    get triggerOptionMapping() {
        return this._triggerOptionMapping;
    }
}

/**
 * A ConfigField object containing a reference to a 3D model as a ModelItem. Also specifies the mapping of Actions to the ModelItems trigger elements.
 */
class ProductField extends ConfigField {
    /**
     * Constructs a new ModelField
     * @param smartVu
     * @param json serialized ModelField returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        const model = json.value[0];
        this._modelIdentifier = model.scene_token;
        this._triggerMapping = model.scene_mapped_triggers;
        this._triggerOptionMapping = model.scene_mapped_trigger_options;
        this._externalTextureMapping = model.scene_texture_slots;
        this._logoToken = model.logo_token;
    }
    /**
     * Getter and setter methods
     */
    get externalTextureMapping() {
        return this._externalTextureMapping;
    }
    get modelToken() {
        return this._modelIdentifier;
    }
    get triggerMapping() {
        return this._triggerMapping;
    }
    get triggerOptionMapping() {
        return this._triggerOptionMapping;
    }
    get logoToken() {
        return this._logoToken;
    }
}

class ColorField extends ConfigField {
    constructor(json) {
        super(json);
    }
}
class ImageField extends ConfigField {
    constructor(json) {
        super(json);
    }
}
class StringField extends ConfigField {
    constructor(json) {
        super(json);
    }
}
class ActionField extends ConfigField {
    constructor(json) {
        super(json);
    }
}
class SmartVuField extends ConfigField {
    constructor(json) {
        super(json);
    }
}

class EntryField extends ConfigField {
    constructor(json) {
        super(json);
    }
}

/**
 * Widget component that displays a custom HTML-formatted text
 */
class TextWidgetComponent {
    constructor() { }
    ngOnInit() { }
}
TextWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TextWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TextWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TextWidgetComponent, selector: "vf-text-widget", ngImport: i0, template: "<p *ngIf=\"model.text\" innerHTML=\"{{ model.text }}\"></p>\n", styles: [""], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TextWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-text-widget',
                    templateUrl: './text-widget.component.html',
                    styleUrls: ['./text-widget.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget that displays a custom HTMl-formatted text
 */
class TextWidget extends SmartVuElement {
    /**
     * Constructs a new TextWidget
     * @param json the serialized TextWidget object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The type of the component that should be creted for this widget
         */
        this.componentType = TextWidgetComponent;
        this._text = json.params.text;
    }
    /**
     * Getter and Setter methods
     */
    get text() {
        return this._text;
    }
}

class TranslationService {
    constructor() {
        this._language = new BehaviorSubject(null);
        this.language$ = this._language.asObservable();
    }
    // Setters and Getters
    set translations(value) {
        this._translations = value;
    }
    get title() {
        return this._translations['titleTranslations'][this._language.getValue()];
    }
    get description() {
        return this._translations['descriptionTranslations'][this._language.getValue()];
    }
    get hashtags() {
        var _a;
        return (_a = this._translations['hashtagsTranslations'][this._language.getValue()]) === null || _a === void 0 ? void 0 : _a.split(",");
    }
    set language(value) {
        this._language.next(value);
    }
}
TranslationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TranslationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TranslationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TranslationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget component that displays the current SmartVu's title
 */
class TitleWidgetComponent {
    constructor(translationsService) {
        this.translationsService = translationsService;
    }
    ngOnInit() {
        this.title = this.translationsService.title || this.model.parent.title;
    }
}
TitleWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TitleWidgetComponent, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
TitleWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TitleWidgetComponent, selector: "vf-title-widget", ngImport: i0, template: "<h2 *ngIf=\"title\">{{ title }}</h2>\n", styles: ["h2{margin:5px 0;max-width:90%;font-size:2rem}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TitleWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-title-widget',
                    templateUrl: './title-widget.component.html',
                    styleUrls: ['./title-widget.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Widget component displaying the current SmartVu's description
 */
class DescriptionWidgetComponent {
    constructor(translationService) {
        this.translationService = translationService;
    }
    ngOnInit() {
    }
}
DescriptionWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: DescriptionWidgetComponent, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
DescriptionWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: DescriptionWidgetComponent, selector: "vf-description-widget", ngImport: i0, template: "<p\n        *ngIf=\"translationService.description || model.parent.description\"\n        innerHTML=\"{{ translationService.description || model.parent.description }}\"\n        [ngClass]=\"{'text-justify': model.textAlignment === 'justify',\n                    'text-center': model.textAlignment === 'center',\n                    'text-left': model.textAlignment === 'left',\n                    'text-right': model.textAlignment === 'right',\n                    'font-weight-bold': model.textStyleBold === true,\n                    'font-italic': model.textStyleItalic === true,\n                    'text-underline': model.textStyleUnderlined === true\n                    }\"\n></p>\n", styles: [".text-underline{text-decoration:underline}.text-justify{text-align:justify!important}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: DescriptionWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-description-widget',
                    templateUrl: './description-widget.component.html',
                    styleUrls: ['./description-widget.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Widget component displaying the current SmartVu's hashtags
 */
class HashtagWidgetComponent {
    constructor(translationService) {
        this.translationService = translationService;
    }
    ngOnInit() { }
}
HashtagWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: HashtagWidgetComponent, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
HashtagWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: HashtagWidgetComponent, selector: "vf-hashtag-widget", ngImport: i0, template: "<p *ngIf=\"translationService.hashtags?.length > 0 || model.parent.hashtags.length > 0\">\n\t<strong *ngFor=\"let hashtag of translationService.hashtags || model.parent.hashtags\"\n\t\t>#{{ hashtag }}</strong\n\t>\n</p>\n", styles: [""], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: HashtagWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-hashtag-widget',
                    templateUrl: './hashtag-widget.component.html',
                    styleUrls: ['./hashtag-widget.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Widget component that displays the current SmartVu's preview image
 */
class PreviewImageWidgetComponent {
    constructor() { }
    ngOnInit() { }
}
PreviewImageWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PreviewImageWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PreviewImageWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PreviewImageWidgetComponent, selector: "vf-preview-image-widget", ngImport: i0, template: "<p *ngIf=\"model.parent.previewImageURL\">\n\t<img class=\"img-responsive\" [src]=\"model.parent.previewImageURL\" />\n</p>\n", styles: [".img-responsive{max-width:100%}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PreviewImageWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-preview-image-widget',
                    templateUrl: './preview-image-widget.component.html',
                    styleUrls: ['./preview-image-widget.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget component that displays the current SmartVu's preview image
 */
class ProductLogoWidgetComponent {
    constructor() { }
    ngOnInit() { }
    /**
       * Getter and Setter methods
       */
    get productLogoUrl() {
        const smartVu = this.model.parent;
        const logoToken = smartVu.getModelField().logoToken;
        if (logoToken) {
            return this.model.parent.getItem(logoToken).downloadURL;
        }
        else {
            return null;
        }
    }
}
ProductLogoWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductLogoWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProductLogoWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ProductLogoWidgetComponent, selector: "vf-product-logo-widget", ngImport: i0, template: "<p *ngIf=\"productLogoUrl\">\n\t<img class=\"img-responsive\" [src]=\"productLogoUrl\" alt=\"no product logo\" />\n</p>\n", styles: [".img-responsive{max-width:100%}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductLogoWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "vf-product-logo-widget",
                    templateUrl: "./product-logo-widget.component.html",
                    styleUrls: ["./product-logo-widget.component.scss"],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * A number of Widget classes that simply display a property of their parent SmartVu such as title, description or preview image
 */
/**
 * Widget that displays the current SmartVu's title
 */
class TitleWidget extends SmartVuElement {
    constructor() {
        super(...arguments);
        this.componentType = TitleWidgetComponent;
    }
}
/**
 * Widget that displays the current SmartVu's description
 */
class DescriptionWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = DescriptionWidgetComponent;
        this._textAlignment = json.params.text_alignment;
        this._textStyleBold = json.params.text_style_bold;
        this._textStyleItalic = json.params.text_style_italic;
        this._textStyleUnderlined = json.params.text_style_underlined;
    }
    get textAlignment() {
        return this._textAlignment;
    }
    get textStyleBold() {
        return this._textStyleBold;
    }
    get textStyleItalic() {
        return this._textStyleItalic;
    }
    get textStyleUnderlined() {
        return this._textStyleUnderlined;
    }
}
/**
 * Widget that displays the current SmartVu's hashtags
 */
class HashtagWidget extends SmartVuElement {
    constructor() {
        super(...arguments);
        this.componentType = HashtagWidgetComponent;
    }
}
/**
 * Widget that displays the current SmartVu's preview image
 */
class PreviewImageWidget extends SmartVuElement {
    constructor() {
        super(...arguments);
        this.componentType = PreviewImageWidgetComponent;
    }
}
/**
 * Widget that displays the current SmartVu's product logo
 */
class ProductLogoWidget extends SmartVuElement {
    constructor() {
        super(...arguments);
        this.componentType = ProductLogoWidgetComponent;
    }
}

/**
 * Widget component that displays a list of bullet points
 */
class BulletPointsWidgetComponent {
    constructor() { }
    ngOnInit() { }
}
BulletPointsWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BulletPointsWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
BulletPointsWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: BulletPointsWidgetComponent, selector: "vf-bullet-points-widget", ngImport: i0, template: "<ul *ngIf=\"model.entries.length > 0\">\n\t<li *ngFor=\"let entry of model.entries\">{{ entry }}</li>\n</ul>\n", styles: ["ul{padding-left:20px}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BulletPointsWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-bullet-points-widget',
                    templateUrl: './bullet-points-widget.component.html',
                    styleUrls: ['./bullet-points-widget.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget displaying a set of entries asa list of bullet points
 */
class BulletPointsWidget extends SmartVuElement {
    /**
     * Constructs a new BulletPointsWidget
     * @param json the serialized BulletPointsWidget object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The type of the component that should be creted for this widget
         */
        this.componentType = BulletPointsWidgetComponent;
        this._entries = json.params.entries || [];
    }
    /**
     * Getter and Setter methods
     */
    get entries() {
        return this._entries;
    }
}

/**
 * Widget component that displays a list of key-value pairs as a table
 */
class KeyValueWidgetComponent {
    constructor() { }
    ngOnInit() { }
}
KeyValueWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: KeyValueWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
KeyValueWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: KeyValueWidgetComponent, selector: "vf-key-value-widget", ngImport: i0, template: "<div class=\"key-value-container\" *ngIf=\"model.entries.length > 0\">\n\t<div class=\"key-value-item\" *ngFor=\"let entry of model.entries\">\n\t\t<strong class=\"key-value-key\">{{ entry.key }}</strong>\n\t\t<span class=\"key-value-value\">{{ entry.value }}</span>\n\t</div>\n</div>\n", styles: [".key-value-container{margin-block-start:1em;margin-block-end:1em}.key-value-container .key-value-item{display:flex;margin-bottom:3px}.key-value-container .key-value-item .key-value-key{flex:1}.key-value-container .key-value-item .key-value-value{flex:2}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: KeyValueWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-key-value-widget',
                    templateUrl: './key-value-widget.component.html',
                    styleUrls: ['./key-value-widget.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget that displays a set of key-value pairs as a table
 */
class KeyValueWidget extends SmartVuElement {
    /**
     * Constructs a new KeyValueWidget
     * @param json the serialized KeyValueWidget object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The type of the component that should be creted for this widget
         */
        this.componentType = KeyValueWidgetComponent;
        this._entries = json.params.entries || [];
    }
    /**
     * Getter and Setter Methods
     */
    get entries() {
        return this._entries;
    }
}

class CaptionWidgetComponent {
    constructor() {
    }
    ngOnInit() {
    }
}
CaptionWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: CaptionWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CaptionWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: CaptionWidgetComponent, selector: "vf-caption-widget", ngImport: i0, template: "<h5 [innerHTML]=\"model.caption\"></h5>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: CaptionWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-caption-widget',
                    templateUrl: './caption-widget.component.html',
                    styleUrls: ['./caption-widget.component.css']
                }]
        }], ctorParameters: function () { return []; } });

class CaptionWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = CaptionWidgetComponent;
        this._caption = json.params.text;
    }
    get caption() {
        return this._caption;
    }
}

class ProductFactsWidgetComponent {
    constructor() {
        /**
         * ID of the products field
         */
        this.productFieldIdentifier = 'products';
        /**
         * Array which stores the facts
         */
        this.factsArray = [];
    }
    ngOnInit() {
        this.factsArray = this.model.parent.getField(this.productFieldIdentifier).json.value[0].facts;
    }
}
ProductFactsWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductFactsWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProductFactsWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ProductFactsWidgetComponent, selector: "vf-product-facts-widget", ngImport: i0, template: "<ul>\n    <li *ngFor=\"let item of factsArray\">\n        {{item}}\n    </li>\n</ul>\n", styles: ["ul{padding-left:20px}\n"], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductFactsWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-product-facts-widget',
                    templateUrl: './product-facts-widget.component.html',
                    styleUrls: ['./product-facts-widget.component.css']
                }]
        }], ctorParameters: function () { return []; } });

class ProductFactsWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = ProductFactsWidgetComponent;
    }
}

class ProductPropertiesWidgetComponent {
    constructor() {
    }
    ngOnInit() {
        this.propertiesArray = this.model.parent.getField("products").json.value[0].properties;
    }
}
ProductPropertiesWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductPropertiesWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProductPropertiesWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ProductPropertiesWidgetComponent, selector: "vf-product-properties-widget", ngImport: i0, template: "<div class=\"key-value-container\" *ngIf=\"propertiesArray?.length > 0\">\n    <div class=\"key-value-item\" *ngFor=\"let entry of propertiesArray\">\n        <strong class=\"key-value-key\">{{ entry.key }}</strong>\n        <span class=\"key-value-value\">{{ entry.value }}</span>\n    </div>\n</div>\n", styles: [".key-value-container{margin-block-start:1em;margin-block-end:1em}.key-value-container .key-value-item{display:flex;margin-bottom:3px}.key-value-container .key-value-item .key-value-key{flex:1}.key-value-container .key-value-item .key-value-value{flex:2}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductPropertiesWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-product-properties-widget',
                    templateUrl: './product-properties-widget.component.html',
                    styleUrls: ['./product-properties-widget.component.scss']
                }]
        }], ctorParameters: function () { return []; } });

class ProductPropertiesWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = ProductPropertiesWidgetComponent;
    }
}

class ProductUrlWidgetComponent {
    constructor() {
    }
    ngOnInit() {
        this.url = this.model.parent.getField('products').json.value[0].shop_url;
        if (!/^https?:\/\/.*/.test(this.url)) {
            this.url = 'http://' + this.url;
        }
    }
}
ProductUrlWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductUrlWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProductUrlWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ProductUrlWidgetComponent, selector: "vf-product-url-widget", ngImport: i0, template: "<a\n        mat-raised-button\n        color=\"primary\"\n        class=\"action-button\"\n        [href]=\"url\"\n        target=\"_blank\"\n>\n    Webshop\n</a>\n", styles: ["a.action-button{display:block;font-size:1.1em;padding:5px 20px;margin:20px 0;cursor:pointer}@media only screen and (max-width: 992px){a.action-button{font-size:.9em;padding:2px 10px}}\n"], components: [{ type: i1$2.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductUrlWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-product-url-widget',
                    templateUrl: './product-url-widget.component.html',
                    styleUrls: ['./product-url-widget.component.css']
                }]
        }], ctorParameters: function () { return []; } });

class ProductURLWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = ProductUrlWidgetComponent;
    }
}

class ProductSkuWidgetComponent {
    constructor() {
    }
    ngOnInit() {
        this.sku = this.model.parent.getField("products").json.value[0].sku;
    }
}
ProductSkuWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductSkuWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProductSkuWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ProductSkuWidgetComponent, selector: "vf-product-sku-widget", ngImport: i0, template: "<p>{{sku}}</p>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ProductSkuWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-product-sku-widget',
                    templateUrl: './product-sku-widget.component.html',
                    styleUrls: ['./product-sku-widget.component.css']
                }]
        }], ctorParameters: function () { return []; } });

class ProductSkuWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = ProductSkuWidgetComponent;
    }
}

class PriceWidgetComponent {
    constructor() {
    }
    ngOnInit() {
        this.productPrice = this.model.parent.getField('products').json.value[0].price;
    }
}
PriceWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PriceWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PriceWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PriceWidgetComponent, selector: "vf-price-widget", ngImport: i0, template: "<p>{{productPrice}}</p>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PriceWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-price-widget',
                    templateUrl: './price-widget.component.html',
                    styleUrls: ['./price-widget.component.css']
                }]
        }], ctorParameters: function () { return []; } });

class PriceWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = PriceWidgetComponent;
    }
}

/**
 * Widget component displaying a clickable, customizable action button
 */
class ActionButtonWidgetComponent {
    constructor() {
    }
    ngOnInit() {
    }
    /**
     * Triggers the Action assigned to the button on click
     */
    onClick() {
        this.model.parent.dispatchAction(this.model.actionIdentifier);
    }
}
ActionButtonWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ActionButtonWidgetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ActionButtonWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ActionButtonWidgetComponent, selector: "vf-action-button-widget", ngImport: i0, template: "<a\n\tmat-raised-button\n\tcolor=\"primary\"\n\tclass=\"action-button\"\n\t[ngStyle]=\"{\n\t\tbackground: model.buttonColor,\n\t\tcolor: model.textColor\n\t}\"\n\t[disabled]=\"!model.actionIdentifier\"\n\t(click)=\"onClick()\"\n>\n\t{{ model.title }}\n</a>\n", styles: ["a.action-button{display:block;font-size:1.1em;padding:5px 20px;margin:20px 0;cursor:pointer}@media only screen and (max-width: 992px){a.action-button{font-size:.9em;padding:2px 10px}}\n"], components: [{ type: i1$2.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }], directives: [{ type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ActionButtonWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-action-button-widget',
                    templateUrl: './action-button-widget.component.html',
                    styleUrls: ['./action-button-widget.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Widget that displays a customizable action button
 */
class ActionButtonWidget extends SmartVuElement {
    /**
     * Constructs a new ActionButtonWidget
     * @param json the serialized ActionButtonWidget object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The type of the component that should be creted for this widget
         */
        this.componentType = ActionButtonWidgetComponent;
        this._title = json.params.title;
        this._actionIdentifier = json.params.action;
        this._buttonColor = json.params.button_color;
        this._textColor = json.params.text_color;
    }
    /**
     * Getter and Setter Methods
     */
    get title() {
        return this._title;
    }
    get actionIdentifier() {
        return this._actionIdentifier;
    }
    get buttonColor() {
        return this._buttonColor;
    }
    get textColor() {
        return this._textColor;
    }
}

/**
 * Abstract base class for popup actions. Opens a popup with a title, a text and an optional action button.
 */
class PopupAction extends UIAction {
    /**
     * Constructs a new PopupAction
     * @param json the serialized PopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._popupTitle = json.params.popup_title || json.name;
        this._popupText = json.params.popup_text;
        this._buttonTitle = json.params.button_title;
        this._actionIdentifier = json.params.button_action;
    }
    /**
     * Getter and Setter Methods
     */
    get popupTitle() {
        return this._popupTitle;
    }
    get popupText() {
        return this._popupText;
    }
    get buttonTitle() {
        return this._buttonTitle;
    }
    get actionIdentifier() {
        return this._actionIdentifier;
    }
}

/**
 * Angular pipe class that allows bypassing HTML or URL injection restrictions, e.g. for writing URL strings or custom HTML text directly to a a component's template
 */
class SafePipe {
    /**
     * Constructs a new SafePipe
     * @param domSanitizer DomSanitizer object provided via dependeny injection
     */
    constructor(domSanitizer) {
        this.domSanitizer = domSanitizer;
    }
    /**
     * Sanitizes the given string for output in a component's HTML template
     * @param value the value to be sanitized
     * @param type the type of sanitization to perform (allowed values: 'html', 'style', 'script', 'url' or 'resourceURL')
     * @returns the sanitized URL as a SafeResourceUrl
     */
    transform(value, type) {
        switch (type) {
            case 'html':
                return this.domSanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.domSanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.domSanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.domSanitizer.bypassSecurityTrustUrl(value);
            case 'resourceURL':
                return this.domSanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Invalid safe type specified: ${type}`);
        }
    }
}
SafePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SafePipe, deps: [{ token: i1$3.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
SafePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SafePipe, name: "safe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SafePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safe',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.DomSanitizer }]; } });

/**
 * Generic base class for popup actions. Displays a dialog containing title, text and an optional action button.
 */
class PopupActionComponent {
    /**
     * Constructs a new PopupActionComponent
     * @param dialog a MatDialog instance used for opening dialogs
     */
    constructor(dialog) {
        this.dialog = dialog;
        /**
         * Attribute defining the type of dialog the component should open (in this case the basic PopupDialog)
         */
        this.dialogComponentType = PopupDialog;
        /**
         * Tthe CSS class that should be applied to the popup
         */
        this.DIALOG_CSS_CLASSES = ['viewer-popup-dialog', 'viewer-popup-cms'];
        this.DIALOG_MAX_WIDTH = '80vw';
    }
    /**
     * Opens the dialog when the Action is triggered
     */
    onActionTriggered() {
        this.openDialog();
    }
    /**
     * Opens the popup dialog with the data defined in the model object
     */
    openDialog() {
        const dialogRef = this.dialog.open(this.dialogComponentType, {
            autoFocus: false,
            panelClass: this.DIALOG_CSS_CLASSES,
            data: this.getDialogData(),
            maxWidth: this.DIALOG_MAX_WIDTH
        });
    }
    /**
     * Dispatches the action that was registered to the popup's action button.
     */
    actionButtonClicked() {
        if (this.model.actionIdentifier) {
            this.model.parent.dispatchAction(this.model.actionIdentifier);
        }
    }
    /**
     * Gets the data for the PopupDialog from the PopupAction object
     * @returns the data for the PopupDialog
     */
    getDialogData() {
        const hidesActionButton = !this.model.actionIdentifier;
        return {
            popupTitle: this.model.popupTitle,
            popupText: this.model.popupText,
            buttonTitle: this.model.buttonTitle,
            onActionButtonClick: this.actionButtonClicked.bind(this),
            showsButton: hidesActionButton == false, // don't show button for PDF popup
        };
    }
    ngOnInit() {
    }
}
PopupActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PopupActionComponent, deps: [{ token: i1$4.MatDialog }], target: i0.ɵɵFactoryTarget.Component });
PopupActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PopupActionComponent, selector: "vf-media-popup-action", ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PopupActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-media-popup-action',
                    template: '<div></div>',
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialog }]; } });
/**
 * Basic Popup dialog with a title, text and action button
 */
class PopupDialog {
    /**
     * Constructs a new PopupDialog
     * @param dialogRef reference to the dialogs HTML elements
     * @param data the data the dialog should display
     */
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.dialogRef.afterOpened().subscribe(() => Callbacks.didShowPopup(data.popupTitle));
        this.dialogRef.afterClosed().subscribe(() => Callbacks.didHidePopup(data.popupTitle));
    }
}
PopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
PopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PopupDialog, selector: "vf-popup-action-dialog", ngImport: i0, template: "<h2 mat-dialog-title>\n\t{{ data.popupTitle || '&nbsp;' }}\n\t<a [mat-dialog-close]=\"null\" class=\"mat-dialog-close-button\">\n\t\t<mat-icon>close</mat-icon>\n\t</a>\n</h2>\n<div mat-dialog-content>\n\t<p\n\t\t*ngIf=\"data.popupText\"\n\t\t[innerHTML]=\"data.popupText | safe: 'html'\"\n\t\tclass=\"popupText\"\n\t></p>\n\t<ng-content></ng-content>\n</div>\n<div mat-dialog-actions *ngIf=\"data.showsButton\">\n\t<button\n\t\tmat-raised-button\n\t\tcolor=\"primary\"\n\t\t[mat-dialog-close]=\"null\"\n\t\tcdkFocusInitial\n\t\t(click)=\"data.onActionButtonClick()\"\n\t>\n\t\t{{ data.buttonTitle || 'OK' }}\n\t</button>\n</div>\n", styles: [":host{width:100%;text-align:center}:host .popup-text{min-width:80%}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: i1$2.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1$4.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i1$4.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }, { type: i1$4.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$4.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }], pipes: { "safe": SafePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-popup-action-dialog',
                    templateUrl: './popup-action.component.html',
                    styleUrls: ['./popup-action.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

/**
 * Subclass of the generic PopupAction that displays an image Item in the popup.
 */
class ImagePopupActionComponent extends PopupActionComponent {
    constructor() {
        super(...arguments);
        /**
         * Define ImagePopupDialog as the type of dialog the component should open
         */
        this.dialogComponentType = ImagePopupDialog;
        this.DIALOG_CSS_CLASSES = [
            'viewer-popup-fullscreen',
            'viewer-popup-media',
            'viewer-popup-cms',
        ];
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    /**
     * Gets the data that the dialog should display
     * @returns the data for the ImagePopupDialog
     */
    getDialogData() {
        let data = super.getDialogData();
        return Object.assign(Object.assign({}, data), { imageURL: this.model.parent.getItem(this.model.imageIdentifier)
                .downloadURL });
    }
}
ImagePopupActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ImagePopupActionComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
ImagePopupActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ImagePopupActionComponent, selector: "vf-image-popup-action", usesInheritance: true, ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ImagePopupActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-image-popup-action',
                    template: '<div></div>',
                }]
        }] });
/**
 * Popup action dialog displaying an additional image item.
 */
class ImagePopupDialog extends PopupDialog {
    /**
     * Constructs a new ImagePopupDialog
     * @param dialogRef reference to dialog HTML element
     * @param data data the dialog should display
     */
    constructor(dialogRef, data) {
        super(dialogRef, data);
        this.dialogRef = dialogRef;
        this.data = data;
    }
}
ImagePopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ImagePopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
ImagePopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ImagePopupDialog, selector: "vf-image-popup-dialog", usesInheritance: true, ngImport: i0, template: `
		<vf-popup-action-dialog>
			<img class="img-responsive" [src]="data.imageURL" />
		</vf-popup-action-dialog>
	`, isInline: true, styles: ["img{display:block;margin:auto;max-height:80vh}vf-popup-action-dialog{min-height:80vh}\n"], components: [{ type: PopupDialog, selector: "vf-popup-action-dialog" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ImagePopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-image-popup-dialog',
                    template: `
		<vf-popup-action-dialog>
			<img class="img-responsive" [src]="data.imageURL" />
		</vf-popup-action-dialog>
	`,
                    styleUrls: ['./image-popup-action.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

/**
 * PopupAction that opens a popup with an additional image
 */
class ImagePopupAction extends PopupAction {
    /**
     * Constructs a new ImagePopupAction
     * @param json the serialized ImagePopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The component type that will be created for this action
         */
        this.componentType = ImagePopupActionComponent;
        this._imageIdentifier = json.params.image_file;
    }
    /**
     * Getter and Setter methods
     */
    get imageIdentifier() {
        return this._imageIdentifier;
    }
}

/**
 * Subclass of the generic PopupAction that displays a video in the popup.
 */
class VideoPopupActionComponent extends PopupActionComponent {
    constructor() {
        super(...arguments);
        /**
         * Define VideoPopupDialog as the type of dialog the component should open
         */
        this.dialogComponentType = VideoPopupDialog;
        this.DIALOG_CSS_CLASSES = [
            'viewer-popup-fullscreen',
            'viewer-popup-media',
            'viewer-popup-cms',
        ];
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    /**
     * Gets the data the dialog should display
     * @returns the data the dialog should display
     */
    getDialogData() {
        let data = super.getDialogData();
        return Object.assign(Object.assign({}, data), { videoURL: this.model.parent.getItem(this.model.videoIdentifier)
                .downloadURL, autoplay: this.model.autoplay });
    }
}
VideoPopupActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VideoPopupActionComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
VideoPopupActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: VideoPopupActionComponent, selector: "vf-video-popup-action", usesInheritance: true, ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VideoPopupActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-video-popup-action',
                    template: '<div></div>',
                }]
        }] });
/**
 * Dialog component that adds a video to the basic PopupDialog
 */
class VideoPopupDialog extends PopupDialog {
    constructor(dialogRef, data) {
        super(dialogRef, data);
        this.dialogRef = dialogRef;
        this.data = data;
        dialogRef.afterOpened().subscribe(() => {
            const video = this.videoplayer.nativeElement;
            video.play();
        });
    }
}
VideoPopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VideoPopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
VideoPopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: VideoPopupDialog, selector: "vf-video-popup-dialog", viewQueries: [{ propertyName: "videoplayer", first: true, predicate: ["videoPlayer"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
		<vf-popup-action-dialog>
			<video controls [autoplay]="data.autoplay" preload="metadata" #videoPlayer>
				<source src="{{ data.videoURL }}#t=0.1" />
				Your browser does not support videos.
			</video>
		</vf-popup-action-dialog>
	`, isInline: true, styles: ["video{display:block;margin:auto;width:100%;height:100vh}\n"], components: [{ type: PopupDialog, selector: "vf-popup-action-dialog" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VideoPopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-video-popup-dialog',
                    template: `
		<vf-popup-action-dialog>
			<video controls [autoplay]="data.autoplay" preload="metadata" #videoPlayer>
				<source src="{{ data.videoURL }}#t=0.1" />
				Your browser does not support videos.
			</video>
		</vf-popup-action-dialog>
	`,
                    styleUrls: ['./video-popup-action.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; }, propDecorators: { videoplayer: [{
                type: ViewChild,
                args: ['videoPlayer']
            }] } });

/**
 * PopupAction that opens a popup with an additional video
 */
class VideoPopupAction extends PopupAction {
    /**
     * Constructs a new VideoPopupAction
     * @param json the serialized VideoPopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The component type that will be created for this action
         */
        this.componentType = VideoPopupActionComponent;
        this._videoIdentifier = json.params.video_file;
        this._autoplay = json.params.autoplay;
    }
    /**
     * Getter and Setter methods
     */
    get videoIdentifier() {
        return this._videoIdentifier;
    }
    get autoplay() {
        return this._autoplay;
    }
}

/**
 * Subclass of the generic PopupAction that displays an embedded pdf in the popup.
 */
class PdfPopupActionComponent extends PopupActionComponent {
    constructor() {
        super(...arguments);
        /**
         * Define PdfPopupDialog as the type of dialog the component should open
         */
        this.dialogComponentType = PdfPopupDialog;
        /**
         * Tthe CSS class that should be applied to the popup
         */
        this.DIALOG_CSS_CLASSES = [
            'viewer-popup-fullscreen',
            'viewer-popup-pdf',
        ];
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    /**
     * Gets the data the dialog should display
     * @returns the data the dialog should display
     */
    getDialogData() {
        let data = super.getDialogData();
        return Object.assign(Object.assign({}, data), { pdfURL: this.model.parent.getItem(this.model.pdfIdentifier)
                .downloadURL });
    }
}
PdfPopupActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PdfPopupActionComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
PdfPopupActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PdfPopupActionComponent, selector: "vf-pdf-popup-action", usesInheritance: true, ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PdfPopupActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-pdf-popup-action',
                    template: '<div></div>',
                }]
        }] });
/**
 * Dialog component that adds an embedded webpage to the basic PopupDialog
 */
class PdfPopupDialog extends PopupDialog {
    /**
     * Constructs a new PdfPopupDialog
     * @param dialogRef reference to the dialog's HTMl element
     * @param data the data the dialog should display
     */
    constructor(dialogRef, data, http) {
        super(dialogRef, data);
        this.dialogRef = dialogRef;
        this.data = data;
        this.http = http;
        let url = data.pdfURL;
        this.downloadFile(url).subscribe((res) => {
            this.pdfViewer.pdfSrc = res;
            this.pdfViewer.refresh();
        });
    }
    downloadFile(url) {
        return this.http.get(url, { responseType: 'blob' })
            .pipe(map((result) => {
            return result;
        }));
    }
}
PdfPopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PdfPopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Component });
PdfPopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PdfPopupDialog, selector: "vf-pdf-popup-dialog", viewQueries: [{ propertyName: "pdfViewer", first: true, predicate: ["pdfViewer"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
		<vf-popup-action-dialog>
			<ng2-pdfjs-viewer #pdfViewer></ng2-pdfjs-viewer>
		</vf-popup-action-dialog>
	`, isInline: true, styles: [""], components: [{ type: PopupDialog, selector: "vf-popup-action-dialog" }, { type: i4.PdfJsViewerComponent, selector: "ng2-pdfjs-viewer", inputs: ["externalWindow", "showSpinner", "openFile", "download", "viewBookmark", "print", "fullScreen", "find", "useOnlyCssZoom", "errorOverride", "errorAppend", "diagnosticLogs", "page", "pdfSrc", "viewerId", "viewerFolder", "downloadFileName", "startDownload", "startPrint", "zoom", "nameddest", "pagemode", "lastPage", "rotatecw", "rotateccw", "cursor", "scroll", "spread", "locale", "errorMessage", "externalWindowOptions"], outputs: ["onBeforePrint", "onAfterPrint", "onDocumentLoad", "onPageChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PdfPopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-pdf-popup-dialog',
                    template: `
		<vf-popup-action-dialog>
			<ng2-pdfjs-viewer #pdfViewer></ng2-pdfjs-viewer>
		</vf-popup-action-dialog>
	`,
                    styleUrls: ['./pdf-popup-action.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: i2.HttpClient }]; }, propDecorators: { pdfViewer: [{
                type: ViewChild,
                args: ['pdfViewer']
            }] } });

/**
 * PopupAction that opens a popup displaying an embedded external pdf
 */
class PdfPopupAction extends PopupAction {
    /**
     * Constructs a new PdfPopupAction
     * @param json the serialized PdfPopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The component type that will be created for this action
         */
        this.componentType = PdfPopupActionComponent;
        this._pdfIdentifier = json.params.pdf_file;
    }
    /**
     * Getter and Setter methods
     */
    get pdfIdentifier() {
        return this._pdfIdentifier;
    }
}

/**
 * A factory class for creating Media Widget Mapped objects.
 */
class MediaWidgetFactory {
    constructor(title, type, identifier, dialogComponentType, imageURL, pdfURL, videoURL, previewImage) {
        this.title = title;
        this.type = type;
        this.identifier = identifier;
        this.dialogComponentType = dialogComponentType;
        this.imageURL = imageURL;
        this.pdfURL = pdfURL;
        this.videoURL = videoURL;
        this.previewImage = previewImage;
    }
}

class MediaWidgetComponent {
    constructor(vuframeAPIService, dialog) {
        this.vuframeAPIService = vuframeAPIService;
        this.dialog = dialog;
        /**
         * Array which stores all the media widget Image objects
         */
        this.imagesArray = [];
        /**
         * Array which stores all the media widget PDF objects
         */
        this.pdfsArray = [];
        /**
         * Array which stores all the media widget Video objects
         */
        this.videosArray = [];
        this.DIALOG_CSS_CLASSES = [
            'viewer-popup-fullscreen',
            'viewer-popup-media',
            'viewer-popup-cms',
        ];
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    ngOnInit() {
        this.getItems(this.model.items);
    }
    /**
     * Getting all the data that we need to create a Media Item object and triggering the MediaWidgetFactory
     * @param identifiers Media Item identifier
     * @private
     */
    getItems(identifiers) {
        for (const identifier of identifiers) {
            this.createMediaItemObject(identifier);
        }
    }
    /**
     * Opens a dialog and loads the media item inside
     * @param mediaItem The media item object
     */
    openDialog(mediaItem) {
        const mediaItemData = this.getDialogData(mediaItem);
        const dialogConfig = this.getDialogConfig();
        const dialogRef = this.dialog.open(mediaItem.dialogComponentType, Object.assign(Object.assign({}, dialogConfig), { panelClass: mediaItemData.panelClass, data: mediaItemData.data }));
        dialogRef.afterClosed().subscribe(result => {
        });
    }
    /**
     * Gets the data that the dialog modal needs to be able to display the media item
     * @param mediaItem
     * @private
     */
    getDialogData(mediaItem) {
        const dialogConfig = this.getDialogConfig();
        switch (mediaItem.type) {
            case this.isImage(mediaItem.type):
                return Object.assign(Object.assign({}, dialogConfig), { data: {
                        popupTitle: mediaItem.title,
                        imageURL: mediaItem.imageURL
                    } });
            case "application/pdf":
                return Object.assign(Object.assign({}, dialogConfig), { panelClass: ['viewer-popup-fullscreen',
                        'viewer-popup-pdf'], data: {
                        popupTitle: mediaItem.title,
                        pdfURL: mediaItem.pdfURL
                    } });
            case this.isVideo(mediaItem.type):
                return Object.assign(Object.assign({}, dialogConfig), { data: {
                        popupTitle: mediaItem.title,
                        videoURL: mediaItem.videoURL
                    } });
            default:
                const error = { message: "The file format of a media item is not supported.", name: "Wrong file format" };
                throw new Error(error.message);
        }
    }
    /**
     * A method which interacts with the MediaWidgetFactory and returns Media Widget Item objects with all the data
     * which is needed for their proper displaying in the SmartVu's sidebar, open in dialog functionality etc.
     * @param identifier The media item identifier
     */
    createMediaItemObject(identifier) {
        const contentType = this.getContentType(identifier);
        switch (contentType) {
            case this.isImage(contentType):
                const imageMediaItem = new MediaWidgetFactory(this.getTitle(identifier), contentType, identifier, ImagePopupDialog, this.getDownloadUrl(identifier));
                this.imagesArray.push(imageMediaItem);
                return imageMediaItem;
            case "application/pdf":
                const pdfMediaItem = new MediaWidgetFactory(this.getTitle(identifier), contentType, identifier, PdfPopupDialog, null, this.getDownloadUrl(identifier), null, this.getPreviewImage(identifier));
                this.pdfsArray.push(pdfMediaItem);
                return pdfMediaItem;
            case this.isVideo(contentType):
                const videoMediaItem = new MediaWidgetFactory(this.getTitle(identifier), contentType, identifier, VideoPopupDialog, null, null, this.getDownloadUrl(identifier), this.getPreviewImage(identifier));
                this.videosArray.push(videoMediaItem);
                return videoMediaItem;
            default:
                const error = { message: "The file format of a media item is not supported.", name: "Wrong file format" };
                throw new Error(error.message);
        }
    }
    getDownloadUrl(identifier) {
        return this.model.parent.getItem(identifier)
            .downloadURL;
    }
    getTitle(identifier) {
        return this.model.parent.getItem(identifier)
            .title;
    }
    getPreviewImage(identifier) {
        return this.model.parent.getItem(identifier).previewURL;
    }
    getContentType(identifier) {
        return this.model.parent.getItem(identifier).mimeType;
    }
    isVideo(contentType) {
        const videoContentTypes = ["application/octet-stream", "video/mp4", "video/quicktime", "video/x-msvideo", "video/x-ms-wmv", "application/mp4"];
        return videoContentTypes.find(el => el === contentType);
    }
    isImage(contentType) {
        const imageContentTypes = ["image/apng", "image/avif", "image/gif", "image/jpeg", "image/png", "image/svg+xml", "image/webp"];
        return imageContentTypes.find(el => el === contentType);
    }
    setStyle() {
        if (this.model.displayMode === "list") {
            return { 'width.px': 125, 'height.px': 125 };
        }
        else {
            return { 'width.px': 160, 'height.px': 160 };
        }
    }
    getDialogConfig() {
        return {
            autoFocus: false,
            maxWidth: this.DIALOG_MAX_WIDTH,
            panelClass: this.DIALOG_CSS_CLASSES
        };
    }
}
MediaWidgetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MediaWidgetComponent, deps: [{ token: VuframeAPIService }, { token: i1$4.MatDialog }], target: i0.ɵɵFactoryTarget.Component });
MediaWidgetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: MediaWidgetComponent, selector: "vf-media-widget", ngImport: i0, template: "<h5 class=\"mb-1\">{{this.model.name}}</h5>\n<div *ngIf=\"model.displayMode === 'grid'\">\n  <mat-grid-list cols=\"2\" rowHeight=\"1:1\">\n    <div *ngFor=\"let image of imagesArray\">\n      <mat-grid-tile>\n\n        <div class=\"image-container\">\n          <img [src]=\"image.imageURL\">\n          <div class=\"overlay\" (click)=\"openDialog(image)\">\n            <mat-icon class=\"overlay-icon\">image</mat-icon>\n          </div>\n        </div>\n\n      </mat-grid-tile>\n    </div>\n\n    <div *ngFor=\"let video of videosArray\">\n      <mat-grid-tile>\n\n        <div class=\"video-container\">\n          <video poster=\"{{video.previewImage}}\">\n            <source [src]=\"video.videoURL\">\n            Your browser does not support the video tag.\n          </video>\n          <div class=\"overlay\" (click)=\"openDialog(video)\">\n            <mat-icon class=\"overlay-icon\">play_circle_outline</mat-icon>\n          </div>\n        </div>\n\n      </mat-grid-tile>\n    </div>\n\n    <div *ngFor=\"let pdf of pdfsArray\">\n      <mat-grid-tile>\n        <div (click)=\"openDialog(pdf)\" class=\"d-flex align-items-center justify-content-center pdf-container\">\n          <img [src]=\"pdf.previewImage\"/></div>\n      </mat-grid-tile>\n    </div>\n  </mat-grid-list>\n\n</div>\n\n<div class=\"media-items-container\" *ngIf=\"model.displayMode === 'list'\">\n  <div *ngFor=\"let image of imagesArray\">\n    <div class=\"d-flex align-items-center py-2 px-2 media-item-box\">\n      <div class=\"image-container flex-shrink-0\" [ngStyle]=\"setStyle()\">\n        <img [src]=\"image.imageURL\">\n        <div class=\"overlay\" (click)=\"openDialog(image)\">\n          <mat-icon class=\"overlay-icon\">image</mat-icon>\n        </div>\n      </div>\n      <h6 class=\"px-2 text-break\">{{image.title}}</h6>\n    </div>\n\n  </div>\n\n  <div *ngFor=\"let video of videosArray\">\n    <div class=\"d-flex align-items-center py-2 px-2 media-item-box\">\n      <div class=\"video-container flex-shrink-0\" [ngStyle]=\"setStyle()\">\n        <video>\n          <source [src]=\"video.videoURL + '#t=0.001'\">\n          Your browser does not support the video tag.\n        </video>\n        <div class=\"overlay\" (click)=\"openDialog(video)\">\n          <mat-icon class=\"overlay-icon\">play_circle_outline</mat-icon>\n        </div>\n      </div>\n      <h6 class=\"px-2 text-break\">{{video.title}}</h6>\n    </div>\n  </div>\n\n  <div *ngFor=\"let pdf of pdfsArray\">\n    <div class=\"d-flex align-items-center py-2 px-2 media-item-box\">\n      <div (click)=\"openDialog(pdf)\"\n           class=\"d-flex align-items-center justify-content-center pdf-container flex-shrink-0\"\n           [ngStyle]=\"setStyle()\">\n        <div>\n          <mat-icon class=\"custom-icon-size\">picture_as_pdf</mat-icon>\n        </div>\n      </div>\n      <h6 class=\"px-2 text-break\">{{pdf.title}}</h6>\n    </div>\n  </div>\n</div>\n\n\n\n\n", styles: [".video-container,.image-container,.pdf-container{width:160px;height:160px;text-align:center;position:relative;cursor:pointer}.video-container .overlay,.image-container .overlay,.pdf-container .overlay{position:absolute;top:0;bottom:0;left:0;right:0;height:100%;width:100%;opacity:0;transition:.3s ease;border-radius:10px}.video-container .overlay:hover,.image-container .overlay:hover,.pdf-container .overlay:hover{background-color:#dddddd40;opacity:1}.video-container .overlay .overlay-icon,.image-container .overlay .overlay-icon,.pdf-container .overlay .overlay-icon{font-size:50px;height:50px;width:50px;color:#fff;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);-ms-transform:translate(-50%,-50%);text-align:center}.pdf-container{border-radius:10px;border:1px solid}.pdf-container .custom-icon-size{font-size:50px;height:50px;width:50px;display:flex;align-self:center}img,video{width:100%;height:100%;object-fit:cover;border-radius:10px;border:1px solid}@media only screen and (max-width: 992px){.video-container,.image-container,.pdf-container{width:80px;height:80px;text-align:center;position:relative;cursor:pointer}.media-item-box{flex-direction:column}}\n"], components: [{ type: i3$1.MatGridList, selector: "mat-grid-list", inputs: ["cols", "gutterSize", "rowHeight"], exportAs: ["matGridList"] }, { type: i3$1.MatGridTile, selector: "mat-grid-tile", inputs: ["rowspan", "colspan"], exportAs: ["matGridTile"] }, { type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MediaWidgetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-media-widget',
                    templateUrl: './media-widget.component.html',
                    styleUrls: ['./media-widget.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: VuframeAPIService }, { type: i1$4.MatDialog }]; } });

class MediaWidget extends SmartVuElement {
    constructor(json) {
        super(json);
        this.componentType = MediaWidgetComponent;
        this._displayMode = json.params.display_mode;
        this._items = json.params.items;
        this._name = json.name;
    }
    get displayMode() {
        return this._displayMode;
    }
    get items() {
        return this._items;
    }
    get name() {
        return this._name;
    }
}

/**
 * Class representing a video item
 */
class VideoItem extends Item {
    /**
     * Constructs a new VideoItem
     * @param json serialized VideoItem returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        // explicitly request hd resolution
        this._downloadURL = addURLParams(this._downloadURL, { res: 'hd' });
    }
}

/**
 * Subclass of the generic PopupAction that displays an embedded website in the popup.
 */
class WebsitePopupActionComponent extends PopupActionComponent {
    constructor() {
        super(...arguments);
        /**
         * Define WebsitePopupDialog as the type of dialog the component should open
         */
        this.dialogComponentType = WebsitePopupDialog;
        /**
         * Tthe CSS class that should be applied to the popup
         */
        this.DIALOG_CSS_CLASSES = [
            'viewer-popup-cms',
            'viewer-popup-fullscreen',
        ];
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    /**
     * Gets the data the dialog should display
     * @returns the data the dialog should display
     */
    getDialogData() {
        let data = super.getDialogData();
        return Object.assign(Object.assign({}, data), { websiteURL: this.model.websiteURL, openWith: this.model.openWith });
    }
}
WebsitePopupActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebsitePopupActionComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
WebsitePopupActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: WebsitePopupActionComponent, selector: "vf-website-popup-action", usesInheritance: true, ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebsitePopupActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-website-popup-action',
                    template: '<div></div>',
                }]
        }] });
/**
 * Dialog component that adds an embedded webpage to the basic PopupDialog
 */
class WebsitePopupDialog extends PopupDialog {
    /**
     * Constructs a new WebsitePopupDialog
     * @param dialogRef reference to the dialog's HTMl element
     * @param data the data the dialog should display
     */
    constructor(dialogRef, data) {
        super(dialogRef, data);
        this.dialogRef = dialogRef;
        this.data = data;
        this.iframeAllowed = -1;
        this.linkInvalid = false;
        this.popupBlocked = false;
    }
    ngOnInit() {
        // behavior for whitelisted link
        if (this.whiteListedLink()) {
            if (this.data.openWith === "redirect") {
                this.openLinkInTab();
            }
            else {
                this.openLink(true);
            }
            return;
        }
        this._style = document.querySelectorAll(".viewer-popup-fullscreen")[0].attributes['style'];
        // Adding this check to solve a bug in Safari Mobile Browser. Without this check the Website Popup Action is not opening from the first click.
        if (!detectSafari()) {
            document.querySelectorAll(".viewer-popup-fullscreen")[0].setAttribute('style', "width: 400px; max-width:calc(100% - 40px); height: 300px;");
        }
        // if link is invalid
        if (this.invalidLink()) {
            this.linkInvalid = true;
            console.warn("Tried to open invalid link: " + this.data.websiteURL);
            return;
        }
        document.querySelectorAll(".mat-dialog-close-button")[0].setAttribute('style', "display: none;");
        if (this.data.openWith === "redirect") {
            this.openLinkInTab();
            return;
        }
        if (WebsitePopupDialog.cachedPopupInfo[this.data.websiteURL] === undefined) {
            VuframeAPIService.shared.checkUrlForIframe(this.data.websiteURL).subscribe(data => {
                this.openLink(data.iframe_allowed);
            });
        }
        else {
            this.openLink(WebsitePopupDialog.cachedPopupInfo[this.data.websiteURL] === 1);
        }
    }
    whiteListedLink() {
        if (!this.data.websiteURL) {
            return false;
        }
        if ((this.data.websiteURL.includes("view") || this.data.websiteURL.includes("share")) && this.data.websiteURL.includes("vuframe.com")) {
            return true;
        }
        if (this.data.websiteURL.includes("vuframe.studio")) {
            return true;
        }
        return false;
    }
    invalidLink() {
        if (!this.data.websiteURL) {
            return true;
        }
        const url = this.data.websiteURL.replace("http://", "");
        if (url == "") {
            return true;
        }
        if (url === "undefined") {
            return true;
        }
        if (url === "undefined/") {
            return true;
        }
        return false;
    }
    openLink(allowed) {
        const allowedState = allowed === false ? 0 : 1;
        WebsitePopupDialog.cachedPopupInfo[this.data.websiteURL] = allowedState;
        if (allowedState < 1) {
            this.openLinkInTab();
        }
        this.iframeAllowed = allowedState;
        if (!detectSafari()) {
            document.querySelectorAll(".viewer-popup-fullscreen")[0].setAttribute('style', this._style);
        }
        document.querySelectorAll(".mat-dialog-close-button")[0].setAttribute('style', "");
    }
    openLinkInTab() {
        if (this.openWindowWithUrl(this.data.websiteURL)) {
            setTimeout(() => {
                this.dialogRef.close();
            }, 200);
            return;
        }
        else {
            this.popupBlocked = true;
        }
    }
    openWindowManually() {
        this.openWindowWithUrl(this.data.websiteURL);
    }
    openWindowWithUrl(url) {
        var newWindow = window.open(url, '_blank');
        if (!newWindow) {
            return false;
        }
        return true;
    }
}
WebsitePopupDialog.cachedPopupInfo = {};
WebsitePopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebsitePopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
WebsitePopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: WebsitePopupDialog, selector: "vf-website-popup-dialog", usesInheritance: true, ngImport: i0, template: `
    <vf-popup-action-dialog>
      <div class="dialogCentered text-center" *ngIf="iframeAllowed < 0 && !linkInvalid">
        <mat-spinner diameter="40" strokeWidth="2" class="m-auto"></mat-spinner>
        <div class="small text-muted mt-3">Opening Website</div>
      </div>
      <div class="dialogCentered text-center" *ngIf="linkInvalid">
        <mat-icon class="mat-icon-64 fill-icon">close</mat-icon>
        <div class="small text-muted mt-3">Website not found</div>
      </div>
      <div class="dialogCentered text-center" *ngIf="popupBlocked">
        <div class="small text-muted mb-3">Website was blocked</div>
        <button
          mat-raised-button
          color="primary"
          [mat-dialog-close]="null"
          cdkFocusInitial
          (click)="openWindowManually()"
        >
          Open Website
        </button>
      </div>
      <iframe *ngIf="iframeAllowed > 0"
              [src]="data.websiteURL | safe: 'resourceURL'"
              frameborder="0"
              allowfullscreen></iframe>
    </vf-popup-action-dialog>
  `, isInline: true, styles: ["iframe{width:100%;height:100%;position:absolute;top:0px;bottom:0px;left:0px;right:0px}\n"], components: [{ type: PopupDialog, selector: "vf-popup-action-dialog" }, { type: i3$2.MatSpinner, selector: "mat-spinner", inputs: ["color"] }, { type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: i1$2.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$4.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }], pipes: { "safe": SafePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebsitePopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-website-popup-dialog',
                    template: `
    <vf-popup-action-dialog>
      <div class="dialogCentered text-center" *ngIf="iframeAllowed < 0 && !linkInvalid">
        <mat-spinner diameter="40" strokeWidth="2" class="m-auto"></mat-spinner>
        <div class="small text-muted mt-3">Opening Website</div>
      </div>
      <div class="dialogCentered text-center" *ngIf="linkInvalid">
        <mat-icon class="mat-icon-64 fill-icon">close</mat-icon>
        <div class="small text-muted mt-3">Website not found</div>
      </div>
      <div class="dialogCentered text-center" *ngIf="popupBlocked">
        <div class="small text-muted mb-3">Website was blocked</div>
        <button
          mat-raised-button
          color="primary"
          [mat-dialog-close]="null"
          cdkFocusInitial
          (click)="openWindowManually()"
        >
          Open Website
        </button>
      </div>
      <iframe *ngIf="iframeAllowed > 0"
              [src]="data.websiteURL | safe: 'resourceURL'"
              frameborder="0"
              allowfullscreen></iframe>
    </vf-popup-action-dialog>
  `,
                    styleUrls: ['./website-popup-action.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

/**
 * PopupAction that opens a popup displaying an embedded external website
 */
class WebsitePopupAction extends PopupAction {
    /**
     * Constructs a new WebsitePopupAction
     * @param json the serialized WebsitePopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The component type that will be created for this action
         */
        this.componentType = WebsitePopupActionComponent;
        this._websiteURL = json.params.website || json.params.website_url;
        this._openWith = json.params.open_with;
        if (this._websiteURL && !this._websiteURL.includes("mailto:")) {
            // attempt to fix URLs with missing protocol
            if (!/^https?:\/\/.*/.test(this._websiteURL)) {
                this._websiteURL = 'http://' + this._websiteURL;
            }
        }
    }
    /**
     * Getter and Setter methods
     */
    get websiteURL() {
        return this._websiteURL;
    }
    get openWith() {
        return this._openWith;
    }
}

/**
 * Component which includes all the functionality for the message action
 */
class MessageActionComponent {
    constructor(_snackBar, dialog) {
        this._snackBar = _snackBar;
        this.dialog = dialog;
        /**
         * The message styles that the user can choose from the Studio
         */
        this.STYLE_MAPPING = {
            toast: 'toast',
            alert: 'alert'
        };
        /**
         * The toast duration
         */
        this.durationInSeconds = 3;
    }
    /**
     * Opens a toast with the message
     */
    openSnackBar() {
        this._snackBar.openFromComponent(ToastMessageComponent, {
            duration: this.durationInSeconds * 1000,
            data: this.model.message
        });
    }
    /**
     * Opens a dialog window with the message and the title
     */
    openDialog() {
        const dialogRef = this.dialog.open(MessageActionDialogComponent, {
            width: '300px',
            autoFocus: false,
            data: {
                title: this.model.title,
                message: this.model.message,
            }
        });
    }
    onActionTriggered() {
        if (this.model.style === this.STYLE_MAPPING.toast) {
            this.openSnackBar();
        }
        else {
            this.openDialog();
        }
    }
}
MessageActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MessageActionComponent, deps: [{ token: i1$5.MatSnackBar }, { token: i1$4.MatDialog }], target: i0.ɵɵFactoryTarget.Component });
MessageActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: MessageActionComponent, selector: "vf-message-action", ngImport: i0, template: "", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MessageActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-message-action',
                    templateUrl: './message-action.component.html',
                    styleUrls: ['./message-action.component.css']
                }]
        }], ctorParameters: function () { return [{ type: i1$5.MatSnackBar }, { type: i1$4.MatDialog }]; } });
/**
 * The toast message component
 */
class ToastMessageComponent {
    constructor(data) {
        this.data = data;
    }
}
ToastMessageComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ToastMessageComponent, deps: [{ token: MAT_SNACK_BAR_DATA }], target: i0.ɵɵFactoryTarget.Component });
ToastMessageComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ToastMessageComponent, selector: "vf-toast-message", ngImport: i0, template: '<div class="center">{{data}}</div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ToastMessageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-toast-message',
                    template: '<div class="center">{{data}}</div>'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_SNACK_BAR_DATA]
                }] }]; } });
/**
 * The message dialog component
 */
class MessageActionDialogComponent {
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
    }
}
MessageActionDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MessageActionDialogComponent, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
MessageActionDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: MessageActionDialogComponent, selector: "vf-message-action-dialog", ngImport: i0, template: "<div><h2 class=\"center\">{{data.title}}</h2><p class=\"center\">{{data.message}}</p><div class=\"center\"><button type=\"button\" class=\"btn btn-light\" mat-dialog-close>Close</button></div></div>", isInline: true, directives: [{ type: i1$4.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MessageActionDialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-message-action-dialog',
                    template: '<div><h2 class="center">{{data.title}}</h2>' +
                        '<p class="center">{{data.message}}</p>' +
                        '<div class="center"><button type="button" class="btn btn-light" mat-dialog-close>Close</button></div><' +
                        '/div>'
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

/**
 * Class which is responsible for the creation of a Message Action object
 */
class MessageAction extends UIAction {
    constructor(json) {
        super(json);
        this.componentType = MessageActionComponent;
        this._message = json.params.message;
        this._title = json.params.title;
        this._style = json.params.style;
    }
    //Getters
    get message() {
        return this._message;
    }
    get title() {
        return this._title;
    }
    get style() {
        return this._style;
    }
}

class PlayAudioActionComponent extends PopupActionComponent {
    constructor() {
        super(...arguments);
        this.dialogComponentType = PlayAudioPopupDialog;
        this.DIALOG_MAX_WIDTH = 'calc(100vw - 40px)';
    }
    ngOnInit() {
    }
    getDialogData() {
        const data = super.getDialogData();
        return Object.assign(Object.assign({}, data), { audioURL: this.model.parent.getItem(this.model.audioIdentifier).downloadURL, autoplay: this.model.autoplay });
    }
}
PlayAudioActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayAudioActionComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
PlayAudioActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PlayAudioActionComponent, selector: "vf-play-audio-action", usesInheritance: true, ngImport: i0, template: "", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayAudioActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-play-audio-action',
                    templateUrl: './play-audio-action.component.html',
                    styleUrls: ['./play-audio-action.component.css']
                }]
        }] });
class PlayAudioPopupDialog extends PopupDialog {
    constructor(dialogRef, data) {
        super(dialogRef, data);
        this.dialogRef = dialogRef;
        this.data = data;
        dialogRef.afterOpened().subscribe(() => {
            const audio = this.audioPlayer.nativeElement;
            if (data.autoplay) {
                audio.play();
            }
        });
    }
}
PlayAudioPopupDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayAudioPopupDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
PlayAudioPopupDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PlayAudioPopupDialog, selector: "vf-play-audio-dialog", viewQueries: [{ propertyName: "audioPlayer", first: true, predicate: ["audioPlayer"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
        <vf-popup-action-dialog>
            <audio controls [autoplay]="data.autoplay" #audioPlayer>
                <source src="{{data.audioURL}}" />
            </audio>
        </vf-popup-action-dialog>
    `, isInline: true, components: [{ type: PopupDialog, selector: "vf-popup-action-dialog" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayAudioPopupDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-play-audio-dialog',
                    template: `
        <vf-popup-action-dialog>
            <audio controls [autoplay]="data.autoplay" #audioPlayer>
                <source src="{{data.audioURL}}" />
            </audio>
        </vf-popup-action-dialog>
    `,
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; }, propDecorators: { audioPlayer: [{
                type: ViewChild,
                args: ['audioPlayer']
            }] } });

class PlayAudioAction extends PopupAction {
    constructor(json) {
        super(json);
        this.componentType = PlayAudioActionComponent;
        this._audioIdentifier = json.params.audio_file;
        this._autoplay = json.params.autoplay;
    }
    get audioIdentifier() {
        return this._audioIdentifier;
    }
    get autoplay() {
        return this._autoplay;
    }
}

/**
 * PopupAction that opens a popup with an html-formatted text and an optional action button
 */
class TextPopupAction extends PopupAction {
    /**
     * Constructs a new TextPopupAction
     * @param json the serialized TextPopupAction object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        /**
         * The component type that will be created for this action
         */
        this.componentType = PopupActionComponent;
        this._popupText = json.params.text;
    }
}

/**
 * A service responsible for emitting an event with the identifier of the SmartVu that we want to change to
 */
class SmartVuService {
    constructor() {
        this.getSmartVuToken = SmartVuService._setSmartVuToken.asObservable();
        this.isSmartVuOpenedFromAction = SmartVuService._isSmartVuOpenedFromAction.asObservable();
        this.triggerLoadSmartVuEvent = SmartVuService._triggerLoadSmartVuEvent.asObservable();
        this.pushToArray = SmartVuService._pushToArray.asObservable();
        /**
         * Array which acts the role of a stack and stores the opened SmartVu's history so we can always return to the previous SmartVu using the "Back" button functionality
         */
        this.smartVuObjectsArray = [];
    }
    /**
     * Sends value to the BehaviorSubject
     * @param value - SmartVu's identifier
     */
    static setSmartVuIdentifier(value) {
        SmartVuService._setSmartVuToken.next(value);
    }
    static setPushToArray(value) {
        SmartVuService._pushToArray.next(value);
    }
    loadPreviousSmartVu() {
        SmartVuService._setSmartVuToken.next({ token: this.smartVuObjectsArray[this.smartVuObjectsArray.length - 2]._identifier });
        const poppedElement = this.smartVuObjectsArray.pop();
        if (this.smartVuObjectsArray.length === 1) {
            this.toggleBackButton();
        }
        //sets the push to array to false when we click on the back button
        SmartVuService._pushToArray.next(false);
    }
    setIsSmartVuOpenedFromAction(value) {
        SmartVuService._isSmartVuOpenedFromAction.next(value);
    }
    /**
     * Sets the SmartVu ID and triggers the loadSmartvu method
     * @param value
     */
    setTriggerLoadSmartVuEvent(value) {
        SmartVuService._triggerLoadSmartVuEvent.next(value);
    }
    /**
     * Fills the smartVuObjectsArray.
     * @param smartVu the SmartVu object
     * @private
     */
    fillSmartVuArray(smartVu, pushToArray = true) {
        if (pushToArray) {
            this.smartVuObjectsArray.push(smartVu);
        }
        //this.toggleBackButton();
    }
    toggleBackButton() {
        if (this.smartVuObjectsArray.length > 1) {
            this.setIsSmartVuOpenedFromAction(true);
        }
        else {
            this.setIsSmartVuOpenedFromAction(false);
        }
    }
}
SmartVuService._setSmartVuToken = new BehaviorSubject(null);
SmartVuService._isSmartVuOpenedFromAction = new BehaviorSubject(false);
SmartVuService._triggerLoadSmartVuEvent = new Subject();
SmartVuService._pushToArray = new BehaviorSubject(null);
SmartVuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SmartVuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * A class which implements the Open SmartVu or Page Action
 */
class OpenSmartVuOrPageAction extends SmartVuElement {
    constructor(json) {
        super(json);
        this._token = this.formatToken(json.params.target_feature);
    }
    /**
     * Formats the token to an acceptable value
     * @param token
     * @private
     */
    formatToken(token) {
        return token.replace('!feature', '');
    }
    /**
     * Method which is triggered when the Action Hotspot is clicked
     */
    onTrigger() {
        SmartVuService.setSmartVuIdentifier({ token: this._token });
        SmartVuService.setPushToArray(true);
    }
}

class ContactAction extends SmartVuElement {
    constructor(json) {
        super(json);
        this._targetUrl = json.params.contact_email;
    }
    onTrigger() {
        window.open(`mailto:${this._targetUrl}`);
    }
}

class DimensioningExtension extends SmartVuElement {
    /**
     * Constructs a new DimensioningExtension
     * @param json the serialized DimensioningExtension object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
        this._dimensioningType = json.params['dimensioning_type'] || "auto";
    }
    /**
     * Enables Playback for the 3D player
     * @returns a VFPlayerConfig with enableAR set to true
     */
    toPlayerConfig() {
        return {
            enableDimensioning: true,
            dimensioningType: this._dimensioningType
        };
    }
}

/**
 * Extension that defines the available AR functions for the SmartVu. Currently, AR is enabled if this extension is present.
 */
class ARExtension extends SmartVuElement {
    /**
     * Constructs a new ARExtension
     * @param json the serialized ARExtension object returned by the Vuframe API
     */
    constructor(json) {
        super(json);
    }
    /**
     * Enables AR for the 3D player
     * @returns a VFPlayerConfig with enableAR set to true
     */
    toPlayerConfig() {
        return {
            enableAR: true,
        };
    }
}

/**
 * Abstract, generic base factory class for creating APIEntity objects from a JSON object returned by the Vuframe API
 */
class APIEntityFactory {
    /**
     * Creates a new APIEntity object from the given JSON object.
     * @param json the JSON object returned by the Vuframe API
     * @returns the new APIEntity object
     */
    createAPIEntityFromJSON(json) {
        const targetType = this.getType(json[this.TYPE_KEY]);
        return new targetType(json);
    }
    /**
     * Checks if a type mapping exists for the given type identifier
     * @param typeIdentifier the type identifier to check
     * @returns true if a mapping exists or false if not
     */
    supportsType(typeIdentifier) {
        return typeIdentifier in this.TYPE_MAPPING;
    }
    /**
     * Attempts to find the corresponding APIEntity from TYPE_MAPPING class for the given type identifier string
     * @throws UnsupportedTypeError if no mapping exists for the identifier and no fallback type was provided
     * @param typeIdentifier the type identifier string from the Vuframe API JSON
     * @returns the type mapped to the given identifier (or the fallback type if no mapping was found)
     */
    getType(typeIdentifier) {
        if (this.supportsType(typeIdentifier) || this.FALLBACK_TYPE) {
            const type = this.TYPE_MAPPING[typeIdentifier] || this.FALLBACK_TYPE;
            return type;
        }
        else {
            return this.FALLBACK_TYPE;
        }
    }
}
/**
 * Factory class for creating instances of Item and its subclasses from JSON objects returned by the Vuframe API.
 */
class ItemFactory extends APIEntityFactory {
    constructor() {
        super(...arguments);
        this.TYPE_MAPPING = {
            any3d_scene: ModelItem,
            movie: VideoItem,
        };
        this.TYPE_KEY = 'item_type';
        /**
         * Use generic item class in case no subclass exists for the item_type value
         */
        this.FALLBACK_TYPE = Item;
    }
}
/**
 * Factory class for creating instances of ConfigField subclasses from JSON objects returned by the Vuframe API.
 */
class ConfigFieldFactory extends APIEntityFactory {
    constructor() {
        super(...arguments);
        this.TYPE_MAPPING = {
            any3d_scene_field: ModelField,
            product_field: ProductField,
            color_field: ColorField,
            image_field: ImageField,
            action_field: ActionField,
            smart_view_field: SmartVuField,
            string_field: StringField,
            discovery_entry_field: EntryField
        };
        this.TYPE_KEY = 'field_type';
    }
}
/**
 * Factory class for creating widget instances derived from Widget from JSON objects returned by the Vuframe API.
 */
class WidgetFactory extends APIEntityFactory {
    constructor() {
        super(...arguments);
        this.TYPE_MAPPING = {
            text_widget: TextWidget,
            title_widget: TitleWidget,
            description_widget: DescriptionWidget,
            hashtag_widget: HashtagWidget,
            product_preview_widget: PreviewImageWidget,
            product_logo_widget: ProductLogoWidget,
            bullet_points_widget: BulletPointsWidget,
            key_value_widget: KeyValueWidget,
            caption_widget: CaptionWidget,
            product_facts_widget: ProductFactsWidget,
            product_properties_widget: ProductPropertiesWidget,
            product_url_widget: ProductURLWidget,
            sku_widget: ProductSkuWidget,
            price_widget: PriceWidget,
            action_button_widget: ActionButtonWidget,
            media_widget: MediaWidget
        };
        this.TYPE_KEY = 'widget_type';
    }
}
/**
 * Factory class for creating instances of Action subclasses from JSON objects returned by the Vuframe API.
 */
class ActionFactory extends APIEntityFactory {
    constructor() {
        super(...arguments);
        /**
         * Action type which comes from the API.
         */
        this.TYPE_MAPPING = {
            open_website_action: WebsitePopupAction,
            popup_with_text_action: TextPopupAction,
            popup_with_image_action: ImagePopupAction,
            popup_with_video_action: VideoPopupAction,
            popup_with_pdf_action: PdfPopupAction,
            popup_with_website_action: WebsitePopupAction,
            //parent_page_message_action: ParentMessageAction,
            //run3d_command_action: Run3dCommandAction,
            open_page_or_view_action: OpenSmartVuOrPageAction,
            contact_action: ContactAction,
            popup_image: ImagePopupAction,
            message_action: MessageAction,
            //bookmark_action: BookmarkAction,
            //combined_action: CombinedAction,
            play_audio_action: PlayAudioAction,
        };
        this.TYPE_KEY = 'action_type';
    }
}
/**
 * Factory class for creating instances of Extension subclasses from JSON objects returned by the Vuframe API.
 */
class ExtensionFactory extends APIEntityFactory {
    constructor() {
        super(...arguments);
        this.TYPE_MAPPING = {
            lifesize_extension: ARExtension,
            tour_extension: TourExtension,
            background_extension: BackgroundExtension,
            dimensioning_extension: DimensioningExtension
        };
        this.TYPE_KEY = 'extension_type';
    }
}

/**
 * Default SmartVu® – Simply displays the first linked 3D model item with a GLB version while ignoring any template-specific logic and configuration.
 */
class DefaultSmartVu extends SmartVu {
    /**
     * Gets the first linked ModelItem
     * @returns the first ModelItem linked in the SmartVu
     */
    getInitialModel() {
        return this.getModel();
    }
    /**
     * Gets a reference to the SmartVu's model field that specifies the linked ModelItem
     * @returns the ModelField defining the linked ModelItem
     */
    getModelField() {
        return this.getField(DefaultSmartVu.MODEL_FIELD);
    }
    /**
     * Gets the ModelItem linked in the SmartVu's product field
     * @returns the ModelItem linked in the SmartVu
     */
    getModel() {
        return this._items[this.getModelField().modelToken];
    }
    /**
     * Displays a warning on the console - model config can not be parsed as internal structure of the SmartVu is unknown
     */
    parseModelConfig() {
        const model = this.getInitialModel();
        // get the trigger mapping information from the ModelField
        const productEntries = this.getModelField();
        var triggerMapping = null;
        if (Array.isArray(productEntries)) {
            triggerMapping = this.getModelField()[0].triggerMapping;
        }
        else {
            triggerMapping = this.getModelField().triggerMapping;
        }
        const validatedTriggerMapping = {};
        // map the Action objects referenced in the ModelField to the trigger names
        for (const key in triggerMapping) {
            const actionIdentifier = triggerMapping[key];
            if (actionIdentifier && this._actions[actionIdentifier]) {
                validatedTriggerMapping[key] = this._actions[actionIdentifier];
            }
        }
        // set the trigger mapping of ModelItem
        model.config.eventTriggerMapping = validatedTriggerMapping;
        // directly use option mapping for model config to be used by the player
        const triggerOptionMapping = this.getModelField().triggerOptionMapping;
        if (triggerOptionMapping) {
            model.config.optionTriggerMapping = triggerOptionMapping;
        }
    }
    getExternalTextureMapping() {
        return this.getModelField().externalTextureMapping;
    }
}
/**
 * Identifier of the ModelField object containing the ModelItem linked in the SmartVu
 */
DefaultSmartVu.MODEL_FIELD = 'products';

class SmartVuCollection extends SmartVu {
    getExternalTextureMapping() {
        return undefined;
    }
    getInitialModel() {
        return undefined;
    }
    parseModelConfig() {
        //parse the actions and create an object property with the actions which were chosen from the studio
        for (const item of this._configFields.actions.json.value) {
            for (const action in this._actions) {
                if (action === item.target.split("!")[0]) {
                    this._mappedActions[action] = this._actions[action];
                }
            }
        }
    }
}

class OptionWheelSmartVu extends SmartVu {
    getExternalTextureMapping() {
        return undefined;
    }
    getInitialModel() {
        return this.getAllModelItems()[0];
    }
    parseModelConfig() {
        // parse the entries (actions)
        let counter = 1;
        for (const item of this._configFields.entries.json.value) {
            for (const action in this._actions) {
                if (action === item.target_3d.split("!")[0]) {
                    this._mappedEntries[counter] = {
                        actionId: this._actions[action].identifier,
                        entry: item
                    };
                }
            }
            counter++;
        }
    }
}

/**
 * Helper class that constructs a SmartVu object from a serialized SmartVu object returned by the Vuframe API
 */
class SmartVuBuilder {
    /**
     * Constructs a new SmartVuBuilder operating on the given serialized SmartVu object
     * @param json the serialized SmartVu object
     */
    constructor(json) {
        this.json = json;
        this.originalJSON = json;
        this.smartVuJSON = SmartVuBuilder.GetInitialFeatureJSON(json);
        this.context = SmartVuBuilder.GetContextFromJSON(json);
    }
    /**
     * Creates a new SmartVu object based on the JSON object the SmartVuBuilder was serialized with.
     * @returns the newly created SmartVu object
     */
    buildSmartVu() {
        const smartVuType = this.getSmartVuType();
        const hashtags = this.getHashtags();
        const json = this.smartVuJSON;
        // create a new Smartvu object of the type specified in the API response
        const smartVu = new smartVuType(json.token, json.title, json.feature_description, json.preview_image_url, hashtags, json.feature_type, json.share_code, this.context);
        // parse items, actions, widgets, extensions and config fields from API response
        smartVu.items = this.getItems();
        smartVu.actions = this.getActions();
        smartVu.widgets = this.getWidgets();
        smartVu.extensions = this.getExtensions();
        smartVu.configFields = this.getConfigFields();
        smartVu.sidebarBehavior = json.sidebar_behavior;
        smartVu.showBranding = true; // !json.hide_watermark
        return smartVu;
    }
    /**
     * Gets the subclass of SmartVu for the type identifier set in the serialized SmartVu object. If no matching mapping is found, returns the class FallbackSmartVu which offers a generic fallback, albeit with limited functionality.
     * @returns the SmartVu class corresponding to the serialized Smartvu object
     */
    getSmartVuType() {
        const typeIdentifier = this.smartVuJSON.feature_type;
        if (!SmartVuBuilder.SupportsType(typeIdentifier)) {
            Logger.warn(`unsupported SmartVu type ${typeIdentifier}, will use generic fallback`);
        }
        return SmartVuBuilder.TYPE_MAPPING[typeIdentifier];
    }
    /**
     * Gets the hashtags of the serialized SmartVu object
     * @returns the hashtags as a string array
     */
    getHashtags() {
        const hashtags = this.smartVuJSON.hashtags;
        if (hashtags && hashtags.length > 0) {
            return hashtags.split(',');
        }
        else {
            return [];
        }
    }
    /**
     * Gets the Item objects belonging to the serialized SmartVu
     * @returns a list of the items belonging to the SmartVu
     */
    getItems() {
        return this.parseAPIEntities(this.json.manifest, new ItemFactory());
    }
    /**
     * Gets the Config field objects defined in the serialized SmartVu
     * @returns a list of the SmartVu's config fields
     */
    getConfigFields() {
        return this.parseAPIEntities(this.smartVuJSON.fields, new ConfigFieldFactory());
    }
    /**
     * Gets the Action objects defined in the serialized SmartVu
     * @returns a list of the SmartVu's actions
     */
    getActions() {
        return this.parseAPIEntities(this.smartVuJSON.actions, new ActionFactory());
    }
    /**
     * Gets the Widgets objects defined in the serialized SmartVu
     * @returns a list of the SmartVu's widgets
     */
    getWidgets() {
        const initialFeatureObject = this.json.features.find((feature) => feature.token === this.json.initial_feature);
        if (Object.keys(initialFeatureObject.widget_translations).length === 0) {
            return this.parseWidgets({ default: this.smartVuJSON.widgets }, new WidgetFactory());
        }
        else {
            return this.parseWidgets(initialFeatureObject.widget_translations, new WidgetFactory());
        }
    }
    /**
     * Gets the Extension objects defined in the serialized SmartVu
     * @returns a list of the SmartVu's extensions
     */
    getExtensions() {
        return this.parseAPIEntities(this.smartVuJSON.extensions, new ExtensionFactory());
    }
    /**
     * Helper method that takes an array of serialized APIEntity objects and deserializes them using the given APIEntityFactory object.
     * @param jsonObjects: an array of serialized APIEntity objects
     * @param factory the APIEntityFactory instance that will be used to deserialize the objects
     * @returns a list of the deserialized APIEntity objects
     */
    parseAPIEntities(jsonObjects, factory) {
        return jsonObjects.reduce((parsedEntities, jsonItem) => {
            try {
                // for each serialized object, try to deserialize it using the given factory class and add it to the result array
                parsedEntities.push(factory.createAPIEntityFromJSON(jsonItem));
            }
            catch (error) {
                // console.log("error", error)
                // write a warning to the console in case deserialization fails (e.g. if the object's type is not supported)
                Logger.warn(error);
            }
            return parsedEntities;
        }, []);
    }
    /**
     * Helper method that takes an object containing the widget translations and parses it.
     * @param jsonObjects: an object of serialized Widget objects
     * @param factory the APIEntityFactory instance that will be used to deserialize the objects
     * @returns a list of the deserialized APIEntity objects
     */
    parseWidgets(jsonObjects, factory) {
        const widgets = {};
        for (const jsonObjectsKey in jsonObjects) {
            const parsedWidgets = jsonObjects[jsonObjectsKey].reduce((parsedEntities, jsonItem) => {
                try {
                    // for each serialized object, try to deserialize it using the given factory class and add it to the result array
                    parsedEntities.push(factory.createAPIEntityFromJSON(jsonItem));
                }
                catch (error) {
                    // console.log("error", error)
                    // write a warning to the console in case deserialization fails (e.g. if the object's type is not supported)
                    Logger.warn(error);
                }
                return parsedEntities;
            }, []);
            widgets[jsonObjectsKey] = parsedWidgets;
        }
        return widgets;
    }
    /**
     * Gets the serialized SmartVu object from the API response.
     * @param json the API response object
     * @returns the serialized SmartVu object
     */
    static GetInitialFeatureJSON(json) {
        return json.features.find((feature) => feature.token === json.initial_feature);
    }
    static GetContextFromJSON(json) {
        return {
            projectIdentifier: json.project,
            companyIdentifier: json.company,
        };
    }
    /**
     * Checks if a SmartVu subclass exists for the given type identifier
     * @param typeIdentifier the SmartVu's type string
     * @returns true if the SmartVu type is supported
     */
    static SupportsType(typeIdentifier) {
        return typeIdentifier in this.TYPE_MAPPING;
    }
}
/**
 * Mapping of SmartVu type identifiers to SmartVu classes
 */
SmartVuBuilder.TYPE_MAPPING = {
    simple_smartvu: SimpleSmartVu,
    virtual_product_view: DefaultSmartVu,
    smartview_collection: SmartVuCollection,
    product_discovery_view: OptionWheelSmartVu
};

/**
 * Custom error class thrown in case the Vuframe AIP returns objects of an unsupported type
 */
class UnsupportedTypeError extends Error {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, UnsupportedTypeError.prototype);
    }
}
/**
 * Custom error class thrown if a share item is not supported yet
 */
class UnsupportedShareTypeError extends Error {
    constructor() {
        super('This share item can not be displayed. Please try to open it in the Vuframe® App for iOS or Android.');
        Object.setPrototypeOf(this, UnsupportedShareTypeError.prototype);
    }
}
/**
 * Custom error class thrown if the 3D player initialization fails
 */
class PlayerError extends Error {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, PlayerError.prototype);
    }
}

class SmartVuDetails {
    constructor(data) {
        this.previewImageUrl = data.preview_image_url;
        this.title = data.title;
        this.description = data.description;
        this.shareCode = data.share_code;
    }
    get shareCode() {
        return this._shareCode;
    }
    set shareCode(value) {
        this._shareCode = value;
    }
    get previewImageUrl() {
        return this._previewImageUrl;
    }
    set previewImageUrl(value) {
        this._previewImageUrl = value;
    }
    get title() {
        return this._title;
    }
    set title(value) {
        this._title = value;
    }
    get description() {
        return this._description;
    }
    set description(value) {
        this._description = value;
    }
}

/**
 * Helper service class providing cross-component error messages.
 */
class ErrorService {
    /**
     * Constructs a new ErrorService
     * @param router Angular Router object provided via dependency injection
     */
    constructor(router) {
        this.router = router;
        /**
         * The current error object as a subscribable BehaviourSubject
         */
        this.error = new BehaviorSubject(null);
        // clear error everytime the current URL changes
        router.events.subscribe(event => {
            this.setError(null);
        });
    }
    /**
     * Sets the current error
     * @param error the new error object
     */
    setError(error) {
        this.error.next(error);
    }
    /**
     * Clears the current error
     */
    clear() {
        this.setError(null);
    }
}
ErrorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorService, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }]; } });

/**
 * Service class for loading and deserializing data form the Vuframe API
 */
class VuframeAPIService {
    /**
     * Constructs a new VuframeAPIService
     * @param errorService ErrorService instance provided via dependency injection
     * @param http HttpClient instance provided via dependency injection
     */
    constructor(http, errorService, sharedService, translationsService) {
        this.http = http;
        this.errorService = errorService;
        this.sharedService = sharedService;
        this.translationsService = translationsService;
        VuframeAPIService.shared = this;
    }
    /**
     * Loads a Share object from the Vuframe API by its share code
     * @param shareCode the share code of the requested Share
     * @returns an Observable that returns the Share on success
     */
    getShare(shareCode) {
        return this.makeAPIGetRequest(`share/${shareCode}`, 'v2').pipe(map(data => {
            // deserialize response object
            const share = new Share(data);
            // check if Share is valid and its target is supported
            if (!share.isValid()) {
                throw new Error('Invalid share item');
            }
            else if (!share.isSupported()) {
                throw new UnsupportedShareTypeError();
            }
            return share;
        }), catchError(error => {
            // pass errors to the ErrorService
            this.errorService.setError(error);
            throw error;
        }));
    }
    /**
     * Loads a SmartVu object from the Vuframe API by its identifier
     * @param identifier the unique identifier of the SmartVu
     * @param requestReferrer - an optional parameter showing if the request is triggered from a Collection SmartVu
     * @returns an Observable that returns the SmartVu on success
     */
    getSmartVu(identifier, requestReferrer) {
        const isLive = window.location.href.includes('live=0') ? 0 : 1;
        return this.makeAPIGetRequest(`viewer/experience/${identifier}?live=${isLive}&web=1`, 'v2').pipe(map(data => {
            const initialFeatureObject = data.features.find((feature) => feature.token === data.initial_feature);
            this.translationsService.translations = {
                titleTranslations: initialFeatureObject.title_translations,
                descriptionTranslations: initialFeatureObject.feature_description_translations,
                hashtagsTranslations: initialFeatureObject.hashtags_translations
            };
            if (requestReferrer === 'collection_smartvu') {
                // creates an object with the required props for a collection only
                return new SmartVuDetails(initialFeatureObject);
            }
            // deserialize the API response using the SmartVuBuilder class
            return new SmartVuBuilder(data).buildSmartVu();
        }), catchError(error => {
            // pass errors to the ErrorService
            this.errorService.setError(error);
            throw error;
        }));
    }
    /**
     * Loads a SmartVu object from the Vuframe API by its identifier
     * @param identifier the unique identifier of the SmartVu
     * @param accessToken the auth0 accessToken for the Studio API
     * @returns an Observable that returns the SmartVu on success
     */
    getSmartVuWithAuth0(identifier, accessToken) {
        return this.makeAuth0APIGetRequest(`viewer/experience/${identifier}?live=0&web=1`, 'v4', accessToken).pipe(map(data => {
            // deserialize the API response using the SmartVuBuilder class
            const smartVu = new SmartVuBuilder(data).buildSmartVu();
            return smartVu;
        }), catchError(error => {
            // pass errors to the ErrorService
            this.errorService.setError(error);
            throw error;
        }));
    }
    checkUrlForIframe(url) {
        return this.makeAPIPostRequest("link", "v4", { url: url }).pipe(map(data => {
            return data;
        }), catchError(error => {
            // pass errors to the ErrorService
            this.errorService.setError(error);
            throw error;
        }));
    }
    /**
     * Perform an authenticated HTTP GET request to the given Vuframe API endpoint
     * @param url the endpoint the request should go to
     * @returns an Observable containing the API response as a JSON object
     */
    makeAPIGetRequest(url, version) {
        const referrer = this.getReferrer();
        // set content type header to JSON
        // create basic authentication header from the API credentials defined for the current application environment or Basic Auth if credentials provided
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Basic ${getVuframeAuthHash()}`,
                'vf-origin': referrer
            }),
        };
        const resolvedUrl = this.addPlatformQuery(`${this.sharedService.inputConfig.API_BASE_URL}${version}/${url}`);
        Logger.debug(`makeAPIGetRequest with referrer: ${referrer} to url ${resolvedUrl}`);
        // perform the GET request using the Angular HttpClient
        return this.http.get(resolvedUrl, httpOptions);
    }
    /**
     * Perform an HTTP GET request to the given Vuframe API endpoint
     * authentication with the provided auth0 accessToken
     * @param url the endpoint the request should go to
     * @param accessToken the auth0 accessToken for the Studio API
     * @returns an Observable containing the API response as a JSON object
     */
    makeAuth0APIGetRequest(url, version, accesToken) {
        const referrer = this.getReferrer();
        // set content type header to JSON
        // create bearer authentication header from the access token provided by auth0
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accesToken}`,
                'vf-origin': referrer
            }),
        };
        const resolvedUrl = this.addPlatformQuery(`${this.sharedService.inputConfig.API_BASE_URL}${version}/${url}`);
        Logger.debug(`makeAPIGetRequest with referrer: ${referrer} to url ${resolvedUrl}`);
        // perform the GET request using the Angular HttpClient
        return this.http.get(resolvedUrl, httpOptions);
    }
    /**
     * Perform an authenticated HTTP POST request to the given Vuframe API endpoint
     * @param url the endpoint the request should go to
     * @returns an Observable containing the API response as a JSON object
     */
    makeAPIPostRequest(url, version, params) {
        const referrer = this.getReferrer();
        // set content type header to JSON
        // create basic authentication header from the API credentials defined for the current application environment
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Basic ${getVuframeAuthHash()}`,
                'vf-origin': referrer
            }),
        };
        let resolvedUrl = this.addPlatformQuery(`${this.sharedService.inputConfig.API_BASE_URL}${version}/${url}`);
        Logger.debug(`makeAPIPostRequest with referrer: ${referrer} to url ${resolvedUrl}`);
        // perform the GET request using the Angular HttpClient
        return this.http.post(resolvedUrl, params, httpOptions);
    }
    addPlatformQuery(url) {
        let currentViewerMode;
        if (BaseViewerConfig.viewerMode) {
            currentViewerMode = BaseViewerConfig.viewerMode.toString();
        }
        else {
            currentViewerMode = 'undefined';
        }
        const query = {
            v: libEnv.version,
            p: 'web',
            client: `viewer.web.${currentViewerMode}`
        };
        if (url.includes("?")) {
            url += `&${this.encodeQueryData(query)}`;
        }
        else {
            url += `?${this.encodeQueryData(query)}`;
        }
        return url;
    }
    getReferrer() {
        const isiFrame = (window.location != window.parent.location);
        return (isiFrame ? document.referrer : document.location.href) || 'unknown';
    }
    encodeQueryData(data) {
        const ret = [];
        for (let d in data) {
            ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
        }
        return ret.join('&');
    }
}
VuframeAPIService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VuframeAPIService, deps: [{ token: i2.HttpClient }, { token: ErrorService }, { token: SharedService }, { token: TranslationService }], target: i0.ɵɵFactoryTarget.Injectable });
VuframeAPIService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VuframeAPIService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VuframeAPIService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2.HttpClient }, { type: ErrorService }, { type: SharedService }, { type: TranslationService }]; } });

/**
 * Component which controls the accessibility of the password protected SmartVu's
 */
class PasswordModalComponent {
    /**
     * Constructs a new dialog popup window and asks for the SmartVu's password
     * @param dialogRef --
     * @param data --
     */
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.hide = true;
    }
    ngOnInit() {
        this.currentSmartvuPassword = this.data.password;
        this.currentSmartvuToken = this.data.token;
        this.myForm = new FormGroup({
            password: new FormControl('', { validators: [Validators.required, this.checkPassword()], updateOn: "submit" })
        });
    }
    /**
     * Encrypts the password from the user input and check if it is equal with the SmartVu's password
     * @private
     */
    checkPassword() {
        return (control) => {
            const encrypted = shajs('sha256').update(`${control.value}${this.currentSmartvuToken}`).digest('hex');
            const valid = encrypted === this.currentSmartvuPassword;
            return !valid ? { wrongPassword: true } : null;
        };
    }
    getErrorMessage() {
        if (this.myForm.get('password').hasError('required')) {
            return 'You must enter a password';
        }
        return this.myForm.get('password').hasError('wrongPassword') ? 'Wrong password' : '';
    }
    onOkClick(form) {
        if (form.valid) {
            this.dialogRef.close({ hasAccess: true });
        }
    }
}
PasswordModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalComponent, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
PasswordModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: PasswordModalComponent, selector: "vf-password-modal", ngImport: i0, template: "<div class=\"d-flex flex-column justify-content-center align-items-center\" id=\"password-protection-modal\">\n  <img src=\"../assets/img/icon.svg\" alt=\"Logo image\" class=\"vuframe-logo d-none d-md-block\">\n<form class=\"d-flex flex-column justify-content-center align-items-center\" [formGroup]=\"myForm\" (ngSubmit)=\"onOkClick(myForm)\">\n  <div mat-dialog-content class=\"d-flex flex-column align-items-center\">\n    <button mat-fab color=\"primary\" class=\"mb-3\" type=\"button\">\n      <mat-icon>lock</mat-icon>\n    </button>\n    <p class=\"align-self-center mb-1 h5\">This SmartVu&reg; is protected</p>\n    <small class=\"text-muted\">\n      Please enter the password to view this SmartVu&reg;\n    </small>\n\n    <mat-form-field appearance=\"legacy\">\n      <mat-label>Password</mat-label>\n      <input matInput [type]=\"hide ? 'password' : 'text'\" id=\"password-input\" formControlName=\"password\" required>\n      <mat-error *ngIf=\"myForm.invalid\" class=\"error\">{{getErrorMessage()}}</mat-error>\n      <button mat-icon-button matSuffix type=\"button\" (click)=\"hide = !hide\" [attr.aria-label]=\"'Hide password'\"\n              [attr.aria-pressed]=\"hide\">\n        <mat-icon>{{hide ? 'visibility_off' : 'visibility'}}</mat-icon>\n      </button>\n    </mat-form-field>\n\n  </div>\n  <div mat-dialog-actions class=\"align-self-center\">\n    <button class=\"d-block btn btn-md btn-rounded btn-info\" type=\"submit\" id=\"password-modal-submit\">\n      Submit </button>\n  </div>\n</form>\n\n</div>\n\n", styles: ["#password-protection-modal{border-radius:15px;padding:30px 0}mat-form-field{width:90%;margin-top:10px}input{font-size:16px}.mat-dialog-actions{width:60%}p{text-align:center}.btn-info{width:100%;color:#fff!important;border:1px solid transparent!important;border-radius:15px;background-image:linear-gradient(to right,#1DC4E9 0%,#1DE9B6 50%,#1DC4E9 100%);background-size:200% auto;transition:background-position .2s ease-in-out}.btn-info:hover{background-position:right center}.vuframe-logo{position:absolute;top:-50%}\n"], components: [{ type: i1$2.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: i4$1.MatFormField, selector: "mat-form-field", inputs: ["color", "floatLabel", "appearance", "hideRequiredMarker", "hintLabel"], exportAs: ["matFormField"] }], directives: [{ type: i5.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i5.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i5.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i1$4.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i4$1.MatLabel, selector: "mat-label" }, { type: i6.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["id", "disabled", "required", "type", "value", "readonly", "placeholder", "errorStateMatcher"], exportAs: ["matInput"] }, { type: i5.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i5.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i5.FormControlName, selector: "[formControlName]", inputs: ["disabled", "formControlName", "ngModel"], outputs: ["ngModelChange"] }, { type: i5.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4$1.MatError, selector: "mat-error", inputs: ["id"] }, { type: i4$1.MatSuffix, selector: "[matSuffix]" }, { type: i1$4.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-password-modal',
                    templateUrl: './password-modal.component.html',
                    styleUrls: ['./password-modal.component.scss']
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

/**
 * Service which takes care of the password modal functions
 */
class PasswordModalService {
    constructor(dialog) {
        this.dialog = dialog;
        this._hasAccess = new Subject();
        this.hasAccess$ = this._hasAccess.asObservable();
    }
    /**
     * Opens the password popup
     */
    openDialog(password, token) {
        const dialogRef = this.dialog.open(PasswordModalComponent, {
            panelClass: 'password-dialog',
            data: { password: password, token: token },
            disableClose: true,
            backdropClass: 'password-dialog-background'
        });
        dialogRef.afterClosed().subscribe(result => {
            this._hasAccess.next(result.hasAccess);
        });
    }
    setHasAccessObservable(value) {
        this._hasAccess.next(value);
    }
}
PasswordModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalService, deps: [{ token: i1$4.MatDialog }], target: i0.ɵɵFactoryTarget.Injectable });
PasswordModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: "root" }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialog }]; } });

class BaseViewerComponent extends BaseViewerConfig {
    constructor(apiService, smartVuService, sharedService, passwordModalService, analyticsService) {
        super();
        this.apiService = apiService;
        this.smartVuService = smartVuService;
        this.sharedService = sharedService;
        this.passwordModalService = passwordModalService;
        this.analyticsService = analyticsService;
        this.shareCode = window.location.pathname.replace('/', '');
        this.readConfigParams();
    }
    ngOnInit() {
        // loads the SmartVu if the user has access
        this.passwordModalService.hasAccess$.subscribe(hasAccess => {
            if (hasAccess) {
                this.getSmartVuWithIdentifier(this.targetId);
            }
        });
        //gets the token which comes from the Open SmartVu Action
        this.smartVuService.getSmartVuToken.subscribe(data => {
            if (data !== null) {
                this.getSmartVuWithIdentifier(data.token);
            }
        });
        //gets the token and opens a SmartVu from the SmartVu Collection popup dialog
        this.smartVuService.triggerLoadSmartVuEvent.subscribe(identifier => {
            this.getSmartVuWithIdentifier(identifier);
        });
    }
    /**
     * Send a request which returns the SmartVu object.
     */
    getSmartVu() {
        if (this.shareCode.includes('token')) {
            this.getSmartVuWithIdentifier(this.shareCode.replace('token/', ''));
        }
        else {
            this.sharedService.smartVuInfo$.subscribe((smartVuInfo) => {
                if (smartVuInfo) {
                    this.analyticsService.registerShareHit(smartVuInfo);
                    this.targetId = smartVuInfo.token;
                    //check if the SmartVu is password protected and ask for the password
                    if (smartVuInfo.password != null && !this.hasAccess) {
                        //set the access to false until the password is provided
                        this.passwordModalService.setHasAccessObservable(false);
                        this.passwordModalService.openDialog(smartVuInfo.password, smartVuInfo.token);
                    }
                    else {
                        this.getSmartVuWithIdentifier(smartVuInfo.token);
                    }
                }
            });
        }
    }
    /**
     * Used to load a SmartVu with the SmartVu's identifier
     * @param identifier the identifier of the SmartVu
     * @private
     */
    getSmartVuWithIdentifier(identifier) {
        this.apiService.getSmartVu(identifier)
            .subscribe((smartVu) => {
            this.smartVu = smartVu;
            this.sharedService.smartVuType = smartVu.featureType;
        });
    }
    /**
     * Method which sets the style of the player
     * @param player
     * @protected
     */
    setStyle(player) {
        player.shareViewer = this;
        this._playerStyle = player.playerStyle;
    }
    get playerStyle() {
        return this._playerStyle;
    }
}
BaseViewerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BaseViewerComponent, deps: [{ token: VuframeAPIService }, { token: SmartVuService }, { token: SharedService }, { token: PasswordModalService }, { token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Component });
BaseViewerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: BaseViewerComponent, selector: "vf-base-viewer", usesInheritance: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BaseViewerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-base-viewer',
                    template: '',
                }]
        }], ctorParameters: function () { return [{ type: VuframeAPIService }, { type: SmartVuService }, { type: SharedService }, { type: PasswordModalService }, { type: AnalyticsService }]; } });

//import {WebPlayerComponent} from "../web-player/web-player.component";
/**
 * Service class providing cross-component access to the Web Player module. Used to initialize the web player, load the linked 3D model items and switch between AR contexts.
 */
class PlayerService {
    /**
     * Constructs a new PlayerService instance
     * @param analyticsService instance of AnalyticsService used for tracking, provided by dependency injection
     * @param platformService instance of PlatformService used for device specific configuration, provided by dependency injection
     * @param errorService instance of ErrorService used for error bubbling to UI
     */
    constructor(sharedService, analyticsService) {
        this.sharedService = sharedService;
        this.analyticsService = analyticsService;
        /**
         * Flag indicating whether ego context is currently active
         */
        this.isEgoActive = new BehaviorSubject(false);
        this.didFinishLoading = new BehaviorSubject(false);
        this.didStartLoading = new BehaviorSubject(null);
        this.onMouseDownCallback = null;
        this._isEgoActive = false;
        this._currentTourTrigger = -1;
        this._tourIsActive = false;
        this._tourIsActiveFromDelay = false;
        this._tourIsStopped = false;
        this._tourStartedInEgo = false;
        this._tourProgress = 0;
    }
    registerOnMouseDownCallback(callback) {
        this.onMouseDownCallback = callback;
    }
    /**
     * Getter methods
     */
    get player() {
        return this._player;
    }
    get currentSmartVu() {
        return this._currentSmartVu;
    }
    get currentModel() {
        return this._currentModel;
    }
    /**
     * Initializes the web player for a SmartVu object and loads its initial model.
     * @param smartVu the SmartVu object that should be displayed
     * @param canvas the HTML canvas element the player should render to
     * @returns a promise that resolves when the initialization is complete
     */
    displaySmartVu(smartVu, player, configOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            this._currentSmartVu = smartVu;
            this.didStartLoading.next(smartVu);
            // generate config by merging some default values with the partial VFPlayerConfig generated by the SmartVu object
            // note that AR is disabled by default as it is enabled via the config partial created by the
            // SmartVu's ARExtension if present
            let config = Object.assign(Object.assign({}, this.getDefaultConfig()), smartVu.generatePlayerConfig());
            // apply config override if specified
            if (configOverrides) {
                config = Object.assign(Object.assign({}, config), configOverrides);
            }
            // initalize the player with the render canvas as well as the config
            this._player = new VFWebPlayer(player.canvas.nativeElement, config);
            this._config = config;
            player.backgroundColor = config.sceneClearColor;
            // register callbacks for entering and exiting ego context
            this._player.registerOnEgoInitCallback(() => {
                this.isEgoActive.next(true);
                this._isEgoActive = true;
                Callbacks.didExitContext(Constants.TOUCH_CONTEXT);
                Callbacks.didEnterContext(Constants.EGO_CONTEXT);
            });
            this._player.registerOnEgoExitCallback(() => {
                this.isEgoActive.next(false);
                this._isEgoActive = false;
                Callbacks.didExitContext(Constants.EGO_CONTEXT);
                Callbacks.didEnterContext(Constants.TOUCH_CONTEXT);
            });
            this._player.registerDidActivateTriggerCallback((triggerType, triggerId) => {
                Callbacks.didActivateTrigger(triggerType, triggerId);
                this.stopTour();
            });
            this._player.registerDidHoverTriggerCallback((triggerType, triggerId) => {
                if (triggerId) {
                    Callbacks.didHoverTrigger(triggerType, triggerId);
                }
                else {
                    Callbacks.didUnhoverTrigger();
                }
            });
            this._player.registerBeforeCompleteSceneLoadCallback((sceneId, format, formatVersion) => {
                this.loadExternalTextures(smartVu);
            });
            this._player.registerDidCompleteSceneLoadCallback((sceneId, format, formatVersion) => {
                Callbacks.didCompleteSceneLoad(sceneId, format, formatVersion);
                this.didFinishLoading.next(true);
            });
            this._player.registerDidFailToLoadCallback((error) => {
                Callbacks.didFailToLoadCallback(error);
            });
            this._player.registerOnMouseDownCallback((event) => {
                if (this.onMouseDownCallback) {
                    this.onMouseDownCallback(event);
                }
            });
            // load the SmartVu's initial model
            const initialModel = this.currentSmartVu.getInitialModel();
            yield this.loadModelItem(initialModel);
        });
    }
    /**
     * Gets mapping from the SmartVu and sets the external textures
     * @param smartVu the SmartVu object that is displayed and contains external texture mapping
     */
    loadExternalTextures(smartVu) {
        const externalTextures = smartVu.getExternalTextureMapping();
        if (externalTextures) {
            const keys = Object.keys(externalTextures);
            for (let key of keys) {
                if (externalTextures[key] != null) {
                    const url = smartVu.getItem(externalTextures[key]).downloadURL;
                    const relativeObjectPath = key.replace(/\/[^\/]+/, '');
                    this.player.setTextureForId(relativeObjectPath, url, 1);
                }
            }
        }
    }
    /**
     * Loads and displays the given 3D model item
     * @param model the ModelItem that should be loaded
     * @returns a promise that resolves when the model has been loaded successfully
     */
    loadModelItem(model) {
        return __awaiter(this, void 0, void 0, function* () {
            // load the given model with its meta data
            this._currentModel = model;
            const modelConfig = model.config;
            this._player.scene.apiUrl = this.sharedService.inputConfig.CDN_BASE_URL + model.identifier + '/edge/';
            this._player.scene.auth = btoa(`${this.sharedService.inputConfig.API_TOKEN}:${this.sharedService.inputConfig.API_SECRET}`);
            this._player.scene.edgeEnabled = this.sharedService.inputConfig.CLOUDFRONT_CDN;
            if (this._player.expectsAuraWebFile) {
                var downloadURL = model.downloadURL.replace('format=glb', 'format=auraweb');
                yield this._player.loadModel(downloadURL, modelConfig);
            }
            else {
                yield this._player.loadModel(model.downloadURL, modelConfig);
            }
            // set trigger callbacks according to the ModelItems trigger mapping
            if (modelConfig.eventTriggerMapping) {
                for (const triggerName in modelConfig.eventTriggerMapping) {
                    try {
                        this._player.registerTriggerCallback(triggerName, () => {
                            // call the onTrigger method of the Action object mapped to the trigger on click
                            modelConfig.eventTriggerMapping[triggerName].onTrigger();
                        });
                    }
                    catch (error) {
                        Logger.warn(`failed to register callback for trigger '${triggerName}': ${error}`);
                    }
                }
            }
            // register a hit for the loaded model
            this.analyticsService.registerItemHit(this.currentModel, this.currentSmartVu);
            // start tour
            if (this._player.config.enableTour) {
                this._tourIsActiveFromDelay = true;
                setTimeout(this.startTour.bind(this), this._player.config.tourDelay * 1000);
            }
        });
    }
    startTour() {
        this._tourTriggers = Object.values(this.player.scene.triggers).filter((trigger) => trigger.triggerType == VFTriggerType.Ego);
        if (this._tourTriggers.length < 1) {
            Logger.warn("no stops for tour");
            return;
        }
        if (this._tourIsActiveFromDelay) {
            this._tourIsActiveFromDelay = false;
            if (this._tourIsStopped) {
                Logger.debug("tour was stopped before beginning");
                this._tourIsStopped = false;
                return;
            }
        }
        this._tourIsActive = true;
        this._tourIsStopped = false;
        this._tourStopDuraton = this._player.config.tourTimeBetween * 1000;
        if (this._isEgoActive) {
            this._tourStartedInEgo = true;
        }
        this._tourInterval = interval(this._tourStopDuraton)
            .subscribe((val) => {
            this.nextTourStop();
        });
        if (this._currentTourTrigger < 0) {
            this.nextTourStop();
            Logger.debug("startTour");
        }
        else {
            this.resumeTour();
            Logger.debug("resumeTour");
        }
    }
    stopTourAndExitEgo() {
        Logger.debug("stopTourAndExitEgo");
        if (!this._tourStartedInEgo && this._isEgoActive) {
            this.exitEgoContext();
        }
        else {
            this.stopTour();
        }
    }
    stopTour() {
        if (this._tourIsActive === true) {
            Logger.debug("stopTour");
            if (this._tourInterval) {
                this._tourInterval.unsubscribe();
            }
            if (this._tourProgressInterval) {
                this._tourProgressInterval.unsubscribe();
            }
        }
        this._tourIsActive = false;
        this._tourIsStopped = true;
        this._tourStartedInEgo = false;
    }
    get tourStarted() {
        return this._tourIsActive;
    }
    get tourStopDuration() {
        return this._tourStopDuraton;
    }
    get tourProgress() {
        return this._tourProgress;
    }
    /**
     * This method moves the user to the next tour stop within a tour.
     * @returns true when navigation to next stop is triggered
     */
    moveToNextStop() {
        if (!this.tourStarted) {
            return false;
        }
        this._currentTourTrigger += 1;
        if (this._currentTourTrigger > this._tourTriggers.length - 1) {
            this._currentTourTrigger = 0;
        }
        this.changeTourStop(this._currentTourTrigger);
        return true;
    }
    /**
     * This method moves the user to the previous tour stop within a tour.
     * @returns true when navigation to previous stop is triggered
     */
    moveToPreviousStop() {
        if (!this.tourStarted) {
            return false;
        }
        this._currentTourTrigger -= 1;
        if (this._currentTourTrigger < 0) {
            this._currentTourTrigger = this._tourTriggers.length - 1;
        }
        this.changeTourStop(this._currentTourTrigger);
        return true;
    }
    nextTourStop() {
        this._tourProgress = 0;
        this._currentTourTrigger += 1;
        if (this._currentTourTrigger > this._tourTriggers.length - 1) {
            this._currentTourTrigger = 0;
        }
        this.changeTourStop(this._currentTourTrigger);
    }
    resumeTour() {
        this._tourProgress = 0;
        if (this._currentTourTrigger > this._tourTriggers.length - 1) {
            this._currentTourTrigger = 0;
        }
        if (this._currentTourTrigger < 0) {
            this._currentTourTrigger = this._tourTriggers.length - 1;
        }
        this.changeTourStop(this._currentTourTrigger);
    }
    /**
     * This method triggers change of tour stop.
     * @param index Index of tour stop to be navigated to
     */
    changeTourStop(index) {
        const triggerMeta = this._tourTriggers[index];
        // const trigger : VFTrigger = this.player.scene.triggers[triggerMeta.name]
        // const path: string = this.player.scene.getPathForNode(trigger.pathNode)
        this.player.scene.activateTrigger(triggerMeta.name, true);
    }
    /**
     * Checks whether the AR functionality of the WebXR API is available
     * @returns a promise that resolves to true if AR using WebXR is available
     */
    isARAvailable() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._player && (yield this._player.checkARAvailability());
        });
    }
    /**
     * Attempts to start a new AR session using WebXR
     * @returns a promise that resolves when the AR session has been initialized
     */
    enterAR() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._player) {
                return this._player.enterAR();
            }
        });
    }
    /**
     * Generates a default configuration object for the web player
     * @returns the default player configuration
     */
    getDefaultConfig() {
        return {
            enableEnvironment: true,
            environmentMapURL: 'assets/img/environment-map/',
            eventTriggerImageURL: 'assets/img/event-trigger.png',
            egoTriggerImageURL: 'assets/img/ego-trigger.png',
            groundTextureURL: 'assets/img/ground-texture.png',
            arPlacementIndicatorURL: 'assets/img/placement-indicator.png',
            arrowImageURL: 'assets/img/arrow.png',
            enableDebug: !libEnv.production,
            enableShadows: true,
            enableAR: false,
            enableTour: false,
            tourDelay: 0,
            tourTimeBetween: 0
        };
    }
    /**
     * Requests exit from ego context
     */
    exitEgoContext() {
        this._player.exitEgo();
        if (this._tourIsActive) {
            this.stopTour();
        }
    }
    pause() {
        if (this.player) {
            this.player.pause();
        }
    }
    unpause() {
        if (this.player) {
            this.player.unpause();
        }
    }
    navigateBack() {
        if (this.player) {
            if (this.isEgoActive) {
                this.player.exitEgo();
            }
        }
    }
    allowRotation(state) {
        if (this.player) {
            this.player.allowRotation(state);
        }
    }
    get config() {
        return this._config;
    }
}
PlayerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayerService, deps: [{ token: SharedService }, { token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable });
PlayerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PlayerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SharedService }, { type: AnalyticsService }]; } });

/**
 * Directive designating the element as a host for dynamically created components. This is currently used for creating the widgets in the SidebarComponent.
 */
class ComponentHostDirective {
    constructor(viewContainerRef) {
        this.viewContainerRef = viewContainerRef;
    }
}
ComponentHostDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ComponentHostDirective, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
ComponentHostDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: ComponentHostDirective, selector: "[component-host]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ComponentHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[component-host]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }]; } });

/**
 * Host component that dynamically renders a set of widget or ui-action objects.
 */
class WidgetHostComponent {
    /**
     * Constructs a new WidgetHostComponent
     * @param componentFactoryResolver ComponentFactoryResolver instance provided via dependency injection
     */
    constructor(componentFactoryResolver, translationService) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.translationService = translationService;
        this.actions = [];
        /**
         * The SmartVu object that is currently being displayed
         */
        this.smartVu = null;
    }
    ngOnInit() {
        this.translationService.language$.subscribe(language => {
            if (this.widgets) {
                this.renderComponents(language);
            }
            if (this.actions) {
                this.prepareActions();
            }
        });
    }
    /**
     * Dynamically creates the sidebar content elements. This is achieved by creating instances of SidebarChildComponent for every Widget object and adding them to the componentHost element
     */
    renderComponents(language) {
        // remove all current sidebar content
        const viewContainerRef = this.componentHost.viewContainerRef;
        viewContainerRef.clear();
        if (!this.widgets[language]) {
            language = Object.keys(this.widgets)[0];
        }
        // create a new component for every widget
        for (const widget in this.widgets[language]) {
            // retrieve an instance of the component factory for the component type declared by the widget
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.widgets[language][widget].componentType);
            // create a new instance of that component class and add it to the sidebar
            const componentRef = viewContainerRef.createComponent(componentFactory);
            const componentInstance = (componentRef.instance);
            // set reference to component instance in model class and vice versa
            componentInstance.model = this.widgets[language][widget];
            this.widgets[language][widget].component = componentInstance;
        }
    }
    /**
     * Sets up the action components which will be triggered by click on an action.
     */
    prepareActions() {
        // create a new component for every action/widget
        for (const action of this.actions) {
            // retrieve an instance of the component factory for the component type declared by the action/widget
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(action.componentType);
            // create a new instance of that component class
            const componentRef = this.componentHost.viewContainerRef.createComponent(componentFactory);
            const componentInstance = (componentRef.instance);
            // set reference to component instance in model class and vice versa
            componentInstance.model = action;
            action.component = componentInstance;
        }
    }
}
WidgetHostComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WidgetHostComponent, deps: [{ token: i0.ComponentFactoryResolver }, { token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
WidgetHostComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: WidgetHostComponent, selector: "vf-widget-host", inputs: { widgets: "widgets", actions: "actions", smartVu: "smartVu" }, viewQueries: [{ propertyName: "componentHost", first: true, predicate: ComponentHostDirective, descendants: true, static: true }], ngImport: i0, template: "<ng-template component-host></ng-template>", styles: [""], directives: [{ type: ComponentHostDirective, selector: "[component-host]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WidgetHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-widget-host',
                    templateUrl: './widget-host.component.html',
                    styleUrls: ['./widget-host.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: TranslationService }]; }, propDecorators: { widgets: [{
                type: Input
            }], actions: [{
                type: Input
            }], smartVu: [{
                type: Input
            }], componentHost: [{
                type: ViewChild,
                args: [ComponentHostDirective, { static: true }]
            }] } });

/**
 * Expandable Sidebar that serves as a host for a SmartVu's widget components.
 */
class SidebarComponent {
    constructor(renderer) {
        this.renderer = renderer;
        /**
         * The SmartVu object that is currently being displayed
         */
        this.smartVu = null;
        this.foregroundColor = null;
        this._forceSidebar = false;
        /**
         * Flag determining whether the sidebar is currently opened.
         */
        this.isOpen = false;
    }
    get forceSidebar() {
        return this._forceSidebar;
    }
    /**
     * Flag determining whether the SmartVu sidebar UI should be forced to be in its initial state (also requires the sidebar to be enabled for the SmartVu in studio)
     */
    set forceSidebar(value) {
        this._forceSidebar = value;
        if (value) {
            this.isOpen = true;
        }
        this.applySidebar();
    }
    ngOnInit() {
        // open automatically if SmartVu is configured accordingly
        if (this.smartVu.sidebarBehavior === SidebarBehavior.Enabled) {
            this.isOpen = true;
            this.applySidebar();
        }
    }
    /**
     * Opens or closes the sidebar.
     */
    toggleOpen() {
        this.isOpen = !this.isOpen;
        this.applySidebar();
    }
    applySidebar() {
        if (this.isOpen) {
            this.renderer.addClass(document.querySelector("#webPlayerContainer"), "sidebar-open");
        }
        else {
            this.renderer.removeClass(document.querySelector("#webPlayerContainer"), "sidebar-open");
        }
    }
}
SidebarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SidebarComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
SidebarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SidebarComponent, selector: "vf-sidebar", inputs: { smartVu: "smartVu", foregroundColor: "foregroundColor", playerBackgroundColor: "playerBackgroundColor", forceSidebar: "forceSidebar" }, ngImport: i0, template: "<a class=\"toggle-button open\" (click)=\"toggleOpen()\" [style.color]=\"foregroundColor\" *ngIf=\"!forceSidebar\">\n\t<mat-icon>menu</mat-icon>\n</a>\n\n<div id=\"sidebar\" [ngClass]=\"{ open: isOpen }\">\n\t<a class=\"toggle-button close d-flex justify-content-center align-items-center\" (click)=\"toggleOpen()\" *ngIf=\"!forceSidebar\">\n\t\t<mat-icon class=\"close-btn\">close</mat-icon>\n\t</a>\n\t<div id=\"elementContainer\">\n\t\t<vf-widget-host [smartVu]=\"smartVu\" [widgets]=\"smartVu.getSortedWidgets()\"></vf-widget-host>\n\t</div>\n</div>\n", styles: [".toggle-button{cursor:pointer;-webkit-user-select:none;user-select:none;top:20px;right:20px;width:42px;height:42px}.toggle-button mat-icon{font-size:40px}.toggle-button.open{position:fixed}.toggle-button.close{position:absolute;right:10px}.toggle-button .close-btn{background-color:#000000bf;border-radius:20px;color:#fff;font-size:24px;transform:scale(1.25)}#sidebar{will-change:right,box-shadow;position:fixed;top:0;right:-420px;height:100%;width:420px;max-width:calc(100% - 25px);overflow:auto;background:#ffffff;transition:right .3s;-webkit-overflow-scrolling:touch}#sidebar.open{right:0;box-shadow:0 8px 10px -5px #0003,0 16px 24px 2px #00000024,0 6px 30px 5px #0000001f}#sidebar #elementContainer{padding:15px 25px 25px}@media only screen and (max-width: 992px){#sidebar{width:250px}#sidebar .close{top:5px;right:0px}.close-btn{transform:scale(1)!important}}@media only screen and (max-width: 768px){#sidebar{width:225px}}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: WidgetHostComponent, selector: "vf-widget-host", inputs: ["widgets", "actions", "smartVu"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SidebarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "vf-sidebar",
                    templateUrl: "./sidebar.component.html",
                    styleUrls: ["./sidebar.component.scss"],
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { smartVu: [{
                type: Input
            }], foregroundColor: [{
                type: Input
            }], playerBackgroundColor: [{
                type: Input
            }], forceSidebar: [{
                type: Input
            }] } });

class BackButtonComponent {
    constructor(smartVuService) {
        this.smartVuService = smartVuService;
        this.exitEgoContext = new EventEmitter();
    }
    set isEgoActive(value) {
        this._isEgoActive = value;
        this.sendEvent(value);
    }
    get isEgoActive() {
        return this._isEgoActive;
    }
    ngOnInit() {
        this.smartVuService.isSmartVuOpenedFromAction.subscribe(res => {
            this.isSmartVuOpenedFromAction = res;
            this.sendEvent(res);
        });
        window.addEventListener('back_button_trigger', (e) => {
            this.isSmartVuOpenedFromAction ? this.loadPreviousSmartVu() : this.triggerExitEgoContext();
        });
    }
    /**
     * Exits from the ego view
     * @private
     */
    triggerExitEgoContext() {
        this.exitEgoContext.emit();
    }
    /**
     * Triggers a method to load the previous opened SmartVu from the stack
     * @private
     */
    loadPreviousSmartVu() {
        this.smartVuService.loadPreviousSmartVu();
    }
    sendEvent(value) {
        const event = new CustomEvent("is_ego_active", {
            detail: {
                isEgoActive: value,
                foregroundColor: this.foregroundColor
            }
        });
        window.dispatchEvent(event);
    }
}
BackButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BackButtonComponent, deps: [{ token: SmartVuService }], target: i0.ɵɵFactoryTarget.Component });
BackButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: BackButtonComponent, selector: "vf-back-button", inputs: { config: "config", foregroundColor: "foregroundColor", showBackButton: "showBackButton", isEgoActive: "isEgoActive" }, outputs: { exitEgoContext: "exitEgoContext" }, ngImport: i0, template: "<a class=\"ego-back-button text-decoration-none\" *ngIf=\"isEgoActive && showBackButton\" [style.color]=\"foregroundColor\" (click)=\"triggerExitEgoContext()\">\n    <mat-icon>chevron_left</mat-icon>\n    <span>Back</span>\n</a>\n\n<a class=\"ego-back-button text-decoration-none\" *ngIf=\"(this.smartVuService.isSmartVuOpenedFromAction | async) && !isEgoActive && showBackButton\" (click)=\"loadPreviousSmartVu()\" [style.color]=\"foregroundColor\">\n    <mat-icon>chevron_left</mat-icon>\n    <span>Back</span>\n</a>\n", styles: [".ego-back-button{opacity:.2;transition:opacity .2s;position:fixed;display:flex;top:20px;left:20px;font-size:1.2em;align-items:center;cursor:pointer}.ego-back-button:hover{opacity:.5}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i1$1.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: BackButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-back-button',
                    templateUrl: './back-button.component.html',
                    styleUrls: ['./back-button.component.scss']
                }]
        }], ctorParameters: function () { return [{ type: SmartVuService }]; }, propDecorators: { exitEgoContext: [{
                type: Output
            }], config: [{
                type: Input
            }], foregroundColor: [{
                type: Input
            }], showBackButton: [{
                type: Input
            }], isEgoActive: [{
                type: Input
            }] } });

class OverlayUiComponent {
    constructor(modalService, playerService, errorService, componentsService) {
        this.modalService = modalService;
        this.playerService = playerService;
        this.errorService = errorService;
        this.componentsService = componentsService;
        this._tourAvailable = false;
        this._loadingFinished = false;
        this.controlsMenu = true;
        this.emitToggleFullscreen = new EventEmitter();
        this.showHint = true;
        this.showControls = true;
        this.appVersion = version;
        const params = new URL(window.location.href).searchParams;
        if (params.get('enable_xr') === '1') {
            this.showWebARButton = true;
        }
        else {
            this.showWebARButton = false;
        }
    }
    get resolution() {
        if (this.config.playerResolution === '512') {
            return 'SD';
        }
        if (this.config.playerResolution === '1024') {
            return 'HD';
        }
        if (this.config.playerResolution === '2048') {
            return 'UHD';
        }
        return 'Undefined';
    }
    get format() {
        let str = this.config.playerFormat == 'aura' ? 'Auraweb' : 'GLB';
        if (this.player) {
            if (this.player.scene) {
                if (this.player.scene.model) {
                    if (this.player.scene.model.formatVersion > 1000) {
                        str += ' v1.1';
                    }
                    else {
                        str += ' v1.0';
                    }
                }
            }
        }
        return str;
    }
    ngOnInit() {
        this.setMenuLayout();
        // clear state flags on error
        this.errorService.error.subscribe(error => {
            if (error) {
                this.showHint = false;
                this.showControls = false;
            }
        });
        this.playerService.didStartLoading.subscribe(smartVu => {
            if (smartVu) {
                this._tourAvailable = this.smartVu !== null && this.smartVu.hasTourExtension;
            }
        });
        this.playerService.didFinishLoading.subscribe(finished => {
            if (finished) {
                this._loadingFinished = true;
            }
        });
        this.playerService.registerOnMouseDownCallback((event) => {
            this.hideHint();
        });
    }
    documentClick(event) {
        this.hideHint();
    }
    hideHint() {
        if (this.isStarted && this.didFinishLoading) {
            this.showHint = false;
        }
    }
    openHelp(content) {
        this.showHint = false;
        this.modalService.open(content, {
            centered: true,
            windowClass: 'dark-modal',
            backdropClass: 'blurred-bg',
            size: 'lg'
        });
    }
    /**
     * This event will be caught in the Embed/Share Page and will trigger the WebAR.
     */
    triggerWebAR() {
        const event = new CustomEvent("webar", { detail: "open_webar" });
        window.dispatchEvent(event);
    }
    /**
     * This method sets the menu layout (control buttons and tutorial dialog).
     */
    setMenuLayout() {
        const params = new URL(window.location.href).searchParams;
        if (params.get("hide_tutorial") === '1') {
            //hide tutorial dialog
            document.getElementById("wv-tutorial-dialog").classList.add("hide");
        }
        if (params.get("hide_menu") === '1') {
            // hide buttons
            document.getElementById("wv-controls").classList.add("hide");
        }
        if (params.get("menu_layout") === '2') {
            // hide ar button
            this.showWebARButton = false;
            // add buttons up class
            document.getElementById("wv-controls").classList.add("position-top");
        }
        if (params.get("menu_layout") === '3') {
            // hide buttons
            document.getElementById("wv-controls").classList.add("hide");
        }
    }
    startTour() {
        if (!this.tourAvailable) {
            Logger.warn("tour not available");
            return;
        }
        this.playerService.startTour();
    }
    stopTour() {
        this.playerService.stopTourAndExitEgo();
    }
    get tourAvailable() {
        return this._tourAvailable && this._loadingFinished;
    }
    get tourStarted() {
        return this.playerService.tourStarted;
    }
    get tourStopDuration() {
        return this.playerService.tourStopDuration;
    }
    setResolution(resolution) {
        this.componentsService.setResolution(resolution);
    }
    closeToast() {
        this.showHint = false;
    }
    toggleFullscreen() {
        this.emitToggleFullscreen.emit();
    }
}
OverlayUiComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: OverlayUiComponent, deps: [{ token: i1$6.NgbModal }, { token: PlayerService }, { token: ErrorService }, { token: SharedService }], target: i0.ɵɵFactoryTarget.Component });
OverlayUiComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: OverlayUiComponent, selector: "vf-overlay-ui", inputs: { config: "config", player: "player", didFinishLoading: "didFinishLoading", isStarted: "isStarted", smartVu: "smartVu", isFullscreen: "isFullscreen", controlsMenu: "controlsMenu" }, outputs: { emitToggleFullscreen: "emitToggleFullscreen" }, host: { listeners: { "document:mousedown": "documentClick($event)" } }, ngImport: i0, template: "<div class=\"toast-container\" id=\"wv-tutorial-dialog\">\n    <ngb-toast *ngIf=\"showHint\" align=\"center\" class=\"bg-black text-light pointer-link\" (click)=\"closeToast()\">\n        <p class=\"mt-0\">\n            Click to interact with\n            <mat-icon>touch_app</mat-icon>\n            hotspots.\n            Use the mouse\n            <mat-icon>mouse</mat-icon>\n            to explore.\n        </p>\n        <small>\n            Click <strong>left</strong> to\n            <mat-icon>3d_rotation</mat-icon>\n            rotate.\n            Click <strong>right</strong> to\n            <mat-icon>multiple_stop</mat-icon>\n            move.\n            <strong>Scroll</strong> to\n            <mat-icon>zoom_in</mat-icon>\n            zoom.\n        </small>\n    </ngb-toast>\n</div>\n\n\n<ng-template #content let-modal>\n    <div class=\"modal-header\">\n        <h5 class=\"modal-title\">How it works</h5>\n        <button (click)=\"modal.dismiss('Cross click')\" aria-label=\"Close\" class=\"close\" type=\"button\">\n            <span aria-hidden=\"true\">&times;</span>\n        </button>\n    </div>\n    <div class=\"modal-body\">\n        <div class=\"row mb-5 mt-5\">\n            <div class=\"col px-3\">\n                <h2 class=\"text-uppercase mb-4\">Explore</h2>\n                <p>\n                    Click to interact with\n                    <mat-icon>touch_app</mat-icon>\n                    hotspots.\n                </p>\n                &mdash;\n                <p>\n                    <br/>Use the mouse\n                    <mat-icon>mouse</mat-icon>\n                    to explore.\n                </p>\n                <small>\n                    Click <strong>left</strong> to\n                    <mat-icon>3d_rotation</mat-icon>\n                    rotate.\n                    <br/>Click <strong>right</strong> to\n                    <mat-icon>multiple_stop</mat-icon>\n                    move.\n                    <br/><strong>Scroll</strong> to\n                    <mat-icon>zoom_in</mat-icon>\n                    zoom.\n                </small>\n            </div>\n            <div class=\"col px-3\">\n                <h2 class=\"text-uppercase mb-4\">Download</h2>\n                <img src=\"../../../../assets/img/vuframe-app.gif\" class=\"img-responsive mb-3 rounded-lg\" height=\"150\">\n                <p class=\"small mt-3\">\n                    Download & view\n                    <mat-icon>smartphone</mat-icon>\n                    this SmartVu\u00AE on your phone\n                    <br/> \u2013 in <strong>3D</strong> and with <strong>Augmented Reality</strong>.\n                </p>\n                <div class=\"store-badges\">\n                    <a href=\"https://apps.apple.com/app/vuframe/id1178107241\" target=\"_blank\">\n                        <img src=\"../../../../assets/img/appstore-badge-light.png\" class=\"img-responsive shadow-sm d-inline\" height=\"40px\"/>\n                    </a>\n                    <a href=\"https://play.google.com/store/apps/details?id=com.vuframe.viewer\" target=\"_blank\" class=\"ml-2\">\n                        <img src=\"../../../../assets/img/playstore-badge-light.png\" class=\"img-responsive shadow-sm d-inline\" height=\"40px\"/>\n                    </a>\n                </div>\n            </div>\n        </div>\n    </div>\n    <div class=\"modal-footer\">\n        <small class=\"text-muted mx-auto\">\n            Need more help? Get it here:\n            <a href=\"https://www.vuframe.com/docs/help\" target=\"_blank\">Help & Troubleshooting</a>\n        </small>\n    </div>\n</ng-template>\n\n<div class=\"controls\" id=\"wv-controls\">\n    <button *ngIf=\"showControls && controlsMenu\" (click)=\"toggleFullscreen()\" class=\"btn btn-sm rounded-pill btn-dark footer-toggle-fullscreen\" ngbTooltip=\"{{ isFullscreen ? 'Exit Fullscreen' : 'Go Fullscreen' }}\">\n        <mat-icon class=\"mat-icon-16 fill-icon\">{{ isFullscreen ? 'close_fullscreen' : 'zoom_out_map' }}</mat-icon>\n    </button>\n\n    <button *ngIf=\"showControls && controlsMenu\" (click)=\"openHelp(content)\" class=\"ml-1 btn btn-sm rounded-pill btn-dark footer-toggle-help\" ngbTooltip=\"Help\">\n        <mat-icon class=\"mat-icon-16 fill-icon\">help_outline</mat-icon>\n    </button>\n\n\n    <span *ngIf=\"showControls && controlsMenu\" ngbDropdown>\n        <button class=\"ml-1 btn btn-sm rounded-pill btn-dark footer-toggle-resolution\" id=\"dropdownConfig\" ngbDropdownToggle ngbTooltip=\"{{ format }}\" autoClose=\"true\">{{ resolution }}</button>\n        <div ngbDropdownMenu aria-labelledby=\"dropdownConfig\" class=\"dropdown-menu\">\n            <button ngbDropdownItem [ngClass]=\"{ 'text-center': true, 'active': (resolution=='UHD')}\" (click)=\"setResolution('uhd')\">UHD</button>\n            <button ngbDropdownItem [ngClass]=\"{ 'text-center': true, 'active': (resolution=='HD')}\" (click)=\"setResolution('hd')\">HD</button>\n            <button ngbDropdownItem [ngClass]=\"{ 'text-center': true, 'active': (resolution=='SD')}\" (click)=\"setResolution('sd')\">SD</button>\n        </div>\n    </span>\n\n    <button *ngIf=\"showControls && showWebARButton && controlsMenu\" (click)=\"triggerWebAR()\" class=\"ml-1 btn btn-sm rounded-pill btn-dark footer-toggle-help\" ngbTooltip=\"WebAR\" id=\"wv-webar-button\">\n      <span class=\"mr-1\">AR</span>\n      <mat-icon class=\"mat-icon-16 fill-icon\">camera</mat-icon>\n    </button>\n\n<!--    <button *ngIf=\"showControls && tourAvailable && !tourStarted\" (click)=\"startTour()\" class=\"ml-1 btn btn-sm rounded-pill btn-dark\" ngbTooltip=\"Start Tour\" placement=\"top\">-->\n<!--        <mat-icon class=\"mat-icon-16 fill-icon\">play_arrow</mat-icon>-->\n<!--    </button>-->\n\n<!--    <button *ngIf=\"showControls && tourAvailable && tourStarted\" (click)=\"stopTour()\" class=\"ml-1 btn btn-sm rounded-pill btn-dark\" ngbTooltip=\"Stop Tour\" placement=\"top\">-->\n<!--        <mat-icon class=\"mat-icon-16 fill-icon\">pause</mat-icon>-->\n<!--    </button>-->\n\n    <!--    <div *ngIf=\"showControls && tourAvailable && tourStarted\" class=\"ml-1 d-inline-block position-relative countdown\" style=\"top:10px;\">-->\n    <!--        <svg [ngClass]=\"webPlayer.spinnerStyle\" >-->\n    <!--            <circle r=\"9\" cx=\"12\" cy=\"12\" [ngStyle]=\"{ 'animation': 'countdown ' + tourStopDuration + 's linear infinite forwards' }\"></circle>-->\n    <!--        </svg>-->\n    <!--    </div>-->\n</div>\n\n\n\n", styles: [":host .controls{position:fixed;left:5px;bottom:5px;z-index:100;font-size:11px}:host button{background-color:#00000080;border:none;font-weight:bold;font-size:.75rem}:host .pointer-none{pointer-events:none}:host .pointer-link{cursor:pointer}:host .dropdown-toggle:after{display:none}.material-icons.mat-icon-16{font-size:16px;height:16px;width:16px;position:relative}.material-icons.mat-icon-16.fill-icon{margin:-3px;top:4px;position:relative}.material-icons.mat-icon-20{font-size:20px;height:20px;width:20px;position:relative}.material-icons.mat-icon-18.fill-icon{margin:-14px -7px;top:8px;position:relative}.toast-container{left:50%;position:fixed;transform:translate(-50%);bottom:10%}.toast{max-width:100%}.dropdown-menu{min-width:3rem;background-color:#000000d9}.dropdown-menu button{background-color:transparent}.dropdown-menu button:not(.active){color:#ffffff5e;font-weight:normal}.dropdown-menu button:hover{color:#ffffffab}.countdown{position:relative;height:24px;width:24px}.countdown svg{position:absolute;top:0;right:0;width:24px;height:24px;transform:rotateY(-180deg) rotate(-90deg) scaleY(-1)}.countdown>svg circle{stroke-dasharray:113px;stroke-dashoffset:0px;stroke-linecap:round;stroke-width:2px;fill:none}.countdown .animated>svg circle{animation:countdown 10s linear infinite forwards}@keyframes countdown{0%{stroke-dashoffset:113px}to{stroke-dashoffset:0px}}.spinner-light circle{stroke:#fff}.spinner-dark circle{stroke:#000}.hide{display:none}.position-top{top:25px}\n"], components: [{ type: i1$6.NgbToast, selector: "ngb-toast", inputs: ["delay", "autohide", "header"], outputs: ["hide"], exportAs: ["ngbToast"] }, { type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$6.NgbTooltip, selector: "[ngbTooltip]", inputs: ["autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { type: i1$6.NgbDropdown, selector: "[ngbDropdown]", inputs: ["open", "placement", "container", "autoClose", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { type: i1$6.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { type: i1$6.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { type: i1$6.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["disabled"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: OverlayUiComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-overlay-ui',
                    templateUrl: './overlay-ui.component.html',
                    styleUrls: ['./overlay-ui.component.scss']
                }]
        }], ctorParameters: function () { return [{ type: i1$6.NgbModal }, { type: PlayerService }, { type: ErrorService }, { type: SharedService }]; }, propDecorators: { config: [{
                type: Input
            }], player: [{
                type: Input
            }], didFinishLoading: [{
                type: Input
            }], isStarted: [{
                type: Input
            }], smartVu: [{
                type: Input
            }], isFullscreen: [{
                type: Input
            }], controlsMenu: [{
                type: Input
            }], emitToggleFullscreen: [{
                type: Output
            }], documentClick: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }] } });

class NavigationUiComponent {
    constructor(playerService, componentsService) {
        this.playerService = playerService;
        this.componentsService = componentsService;
        this._tourAvailable = false;
        this._loadingFinished = false;
    }
    ngOnInit() {
        this.playerService.didStartLoading.subscribe(smartVu => {
            if (smartVu) {
                this._tourAvailable = this.smartVu !== null && this.smartVu.hasTourExtension;
            }
        });
        this.playerService.didFinishLoading.subscribe(finished => {
            if (finished) {
                this._loadingFinished = true;
            }
        });
    }
    get playerStyle() {
        return this.componentsService.playerStyle;
    }
    get showTour() {
        return this._tourAvailable && this._loadingFinished;
    }
    get elementsVisible() {
        return this.showTour;
    }
    get tourStarted() {
        return this.playerService.tourStarted;
    }
    startTour() {
        this.playerService.startTour();
    }
    nextStop() {
        this.playerService.moveToNextStop();
    }
    lastStop() {
        this.playerService.moveToPreviousStop();
    }
    stopTour() {
        this.playerService.stopTourAndExitEgo();
    }
}
NavigationUiComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: NavigationUiComponent, deps: [{ token: PlayerService }, { token: SharedService }], target: i0.ɵɵFactoryTarget.Component });
NavigationUiComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: NavigationUiComponent, selector: "vf-navigation-ui", inputs: { smartVu: "smartVu" }, ngImport: i0, template: "<div class=\"navigation-section\" [ngClass]=\"playerStyle\" *ngIf=\"elementsVisible\">\n  <div class=\"tour\" *ngIf=\"showTour\">\n    <b data-bgholder=\"overlay\" class=\"full-wh nav-bg\" data-lgradient=\"y\"\n       data-linear-gradient=\"rgba(0,0,0, 0.0)|rgba(0,0,0, 1)\"></b>\n    <ul class=\"navigation-ul row gt0 align-items-start\">\n      <li class=\"navigation-li spacer last-one start-button\" (click)=\"startTour()\" *ngIf=\"!tourStarted\">\n        <span class=\"icon\"><mat-icon>play_arrow</mat-icon></span>\n        <span class=\"text\">Start Tour</span>\n        <span class=\"line\"></span>\n      </li>\n      <li class=\"navigation-li spacer tour-button\" (click)=\"lastStop()\" *ngIf=\"tourStarted\">\n        <span class=\"icon\"><mat-icon>arrow_back_ios</mat-icon></span>\n        <span class=\"text\">Back</span>\n        <span class=\"line\"></span>\n      </li>\n      <li class=\"navigation-li tour-button\" (click)=\"stopTour()\" *ngIf=\"tourStarted\">\n        <span class=\"icon\"><mat-icon>stop</mat-icon></span>\n        <span class=\"text\">Stop</span>\n        <span class=\"line\"></span>\n      </li>\n      <li class=\"navigation-li last-one tour-button\" (click)=\"nextStop()\" *ngIf=\"tourStarted\">\n        <span class=\"icon\"><mat-icon>arrow_forward_ios</mat-icon></span>\n        <span class=\"text\">Next</span>\n        <span class=\"line\"></span>\n      </li>\n    </ul>\n  </div>\n</div>\n", styles: [".player-dark .navigation-li{color:#000}.player-dark .navigation-li:hover{color:#000}.player-dark .navigation-li:hover .line{background-color:#000}.navigation-section.player-dark{background:linear-gradient(0deg,rgba(255,255,255,.35) 0%,rgba(255,255,255,0) 100%)}.player-light .navigation-li{color:#fff}.player-light .navigation-li:hover{color:#fff}.player-light .navigation-li:hover .line{background-color:#fff}.navigation-section.player-light{background:linear-gradient(0deg,rgba(0,0,0,.35) 0%,rgba(0,0,0,0) 100%)}.navigation-li{font-weight:600;text-align:center;white-space:nowrap!important;display:flex;align-items:center;justify-content:center;transition:all .4s ease-out;flex-direction:column;text-decoration:none;padding-left:10px!important;padding-right:10px!important}.navigation-li:hover{transition:all .3s ease-out;cursor:pointer}.navigation-li:hover .line{transition:all .3s ease-out}.navigation-li .line{width:20px;height:2px;background-color:transparent;transition:all .3s ease-out;margin-top:5px}.navigation-li .icon{opacity:.7}.navigation-li:hover .icon{opacity:1;transition:all .3s ease-out}.navigation-li .text{font-size:12px}.navigation-li.spacer{margin-left:auto}.navigation-li.last-one{margin-right:auto}.navigation-section{position:fixed;bottom:0;left:0;width:100%;z-index:8;padding-bottom:10px;padding-top:20px}.navigation-section .icon{line-height:1;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}.navigation-section ul{list-style-type:none;margin:0;padding:0}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: NavigationUiComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-navigation-ui',
                    templateUrl: './navigation-ui.component.html',
                    styleUrls: ['./navigation-ui.component.css']
                }]
        }], ctorParameters: function () { return [{ type: PlayerService }, { type: SharedService }]; }, propDecorators: { smartVu: [{
                type: Input
            }] } });

class DebugUiComponent {
    constructor() {
        this.appVersion = version;
    }
    ngOnInit() {
    }
    get lightmapped() {
        let str = this.config.playerFormat == "aura" ? "Auraweb" : "GLB";
        if (this.player) {
            if (this.player.scene) {
                if (this.player.scene.model) {
                    if (this.player.scene.model.formatVersion) {
                        str +=
                            " v" +
                                (this.player.scene.model.formatVersion || 1000)
                                    .toString()
                                    .split("")
                                    .join(".");
                    }
                }
            }
        }
        return str;
    }
    get debugFormat() {
        if (this.player) {
            if (this.player.scene) {
                if (this.player.scene.model) {
                    if (this.player.scene.useLightmaps) {
                        return "Light-mapped";
                    }
                }
            }
        }
        return "Realtime";
    }
}
DebugUiComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: DebugUiComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DebugUiComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: DebugUiComponent, selector: "vf-debug-ui", inputs: { isReady: "isReady", didFinishLoading: "didFinishLoading", player: "player", version: "version", config: "config" }, ngImport: i0, template: "<div class=\"debug\">\n    <small class=\"ml-2 badge-pill badge-danger p-1 px-2\">Debug</small>\n    <small class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"viewer-version\">Viewer v{{ appVersion }}</small>\n    <small class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"player-version\">Player v{{ version }}</small>\n    <small class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"format\">{{ debugFormat }}</small>\n    <small class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"lightmapped\">{{ lightmapped }}</small>\n    <small *ngIf=\"isReady && didFinishLoading\" class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"fps\">FPS {{ player.getFPS() }}</small>\n    <small *ngIf=\"isReady && didFinishLoading\" class=\"ml-2 badge-pill badge-danger p-1 px-2\" id=\"loading-time\">Took {{ player.getLoadingDuration() }} seconds</small>\n</div>\n", styles: [":host .debug{position:fixed;left:5px;right:5px;bottom:10px;z-index:99;font-size:11px;text-align:center}\n"], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: DebugUiComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "vf-debug-ui",
                    templateUrl: "./debug-ui.component.html",
                    styleUrls: ["./debug-ui.component.scss"],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { isReady: [{
                type: Input
            }], didFinishLoading: [{
                type: Input
            }], player: [{
                type: Input
            }], version: [{
                type: Input
            }], config: [{
                type: Input
            }] } });

/**
 * The root component which loads SmartVus from the types - Simple SmartVu, Default SmartVu and Fallback SmartVu. Contains the HTML canvas element the 3D player renders to as well as various UI elements such as the sidebar and a button for starting AR. This class also uses the service classes VuframeAPIService and APIService to load a SmartVu from the Vuframe API and display it using the VFWebPlayer.
 */
class WebPlayerComponent {
    /**
     * Constructs a new WebPlayerComponent
     * @param apiService VuframeAPI instance provided via dependency injection
     * @param errorService ErrorService instance provided via dependency injection
     * @param analyticsService AnalyticsService instance provided via dependency injection
     * @param playerService PlayerService instance provided via dependency injection
     * @param titleService
     * @param document
     * @param window
     * @param router
     * @param route
     */
    constructor(apiService, playerService, titleService, document, window, router, smartVuService, componentsService, analyticsService, optionalComponentsService, passwordModalService, route, sharedService, translationService) {
        this.apiService = apiService;
        this.playerService = playerService;
        this.titleService = titleService;
        this.document = document;
        this.window = window;
        this.router = router;
        this.smartVuService = smartVuService;
        this.componentsService = componentsService;
        this.analyticsService = analyticsService;
        this.optionalComponentsService = optionalComponentsService;
        this.passwordModalService = passwordModalService;
        this.route = route;
        this.sharedService = sharedService;
        this.translationService = translationService;
        this._logo = 'logo_title_white.svg';
        this.emitNgAfterContentInit = new EventEmitter();
        this.share = null;
        this.smartVu = null;
        this.isPreLoading = true;
        this._isStarted = false;
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    }
    get isStarted() {
        return this._isStarted;
    }
    get didFinishLoading() {
        return this._didFinishLoading;
    }
    stopLoading() {
        this.isPreLoading = false;
        this.isLoading = false;
    }
    get isReady() {
        return this._isReady;
    }
    get player() {
        return this.playerService.player;
    }
    get version() {
        return this.player.version;
    }
    get progress() {
        if (!this.playerService.currentModel) {
            return 0.0;
        }
        return Math.min(VFProgress.Progress, 1);
    }
    get paused() {
        if (!this.playerService.player) {
            return false;
        }
        return this.playerService.player.paused;
    }
    get pausedUi() {
        return (!this._playerPausedManually || (this._playerPausedManually && this._playerPausedManuallyWithUi)) && this.paused;
    }
    get debugMode() {
        return VFConfigManager.enableDebug;
    }
    //region Styling
    set backgroundColor(val) {
        this._backgroundColor = val;
        if (!this._backgroundColor) {
            this._backgroundColor = '#ffffff';
        }
        this.setColors();
    }
    setColors() {
        this._foregroundColor = colorIsDark(this.backgroundColor) ? '#f8f9fa' : '#343a40';
        this._playPauseButtonColor = colorIsDark(this.backgroundColor) ? '#ffffff' : '#000000';
        this.componentsService.playerStyle = colorIsDark(this.backgroundColor) ? 'player-light' : 'player-dark';
        this._spinnerStyle = colorIsDark(this.backgroundColor) ? 'spinner-light' : 'spinner-dark';
        this._logo = colorIsDark(this.backgroundColor) ? 'logo_title_white.svg' : 'logo_title_dark.svg';
        this.emitNgAfterContentInit.emit();
    }
    get backgroundColor() {
        return this._backgroundColor;
    }
    get foregroundColor() {
        return this._foregroundColor;
    }
    get playPauseButtonColor() {
        return this._playPauseButtonColor;
    }
    get playerStyle() {
        return this.componentsService.playerStyle;
    }
    get spinnerStyle() {
        return this._spinnerStyle;
    }
    get logo() {
        return this._logo;
    }
    get iFrameLocation() {
        return this._iframeLocation;
    }
    setTitle(newTitle) {
        this.titleService.setTitle(newTitle);
    }
    //endregion
    resetStateFlags() {
        this._didFinishLoading = true;
        this._isStarted = false;
        this.isPreLoading = false;
        this.isLoading = false;
        this.byteProgress = 1;
        this._isReady = false;
        this.webXRAvailable = true;
        this.startedLoading = false;
    }
    /**
     * Loads the SmartVu object for the share code passed as an URL parameter. This method makes use of the VuframeAPIService.
     */
    loadSmartVu(pushToArray) {
        // set state flags
        this._didFinishLoading = false;
        this._isStarted = true;
        this.isPreLoading = true;
        this.isLoading = true;
        this.byteProgress = 0.0;
        this._isReady = false;
        this.webXRAvailable = false;
        this.startedLoading = true;
        //the smartvu that we are receiving from the parent component
        this.setupSmartVu(this.smartVu, pushToArray);
    }
    /**
     * Initializes and loads a SmartVu
     */
    setupSmartVu(smartVu, pushToArray) {
        this.smartVuService.fillSmartVuArray(smartVu, pushToArray);
        this.setTitle(smartVu.title);
        this.displaySmartVu(smartVu);
        // register a new analytics event for the loaded SmartVu and Share
        this.analyticsService.registerSmartVuHit(smartVu);
    }
    /**
     * Initializes the 3D player with the current SmartVu object.
     * @returns a promise that resolves when the 3D player has been initialized successfully
     */
    displaySmartVu(smartVu) {
        return __awaiter(this, void 0, void 0, function* () {
            let hadError = false;
            try {
                // check whether all linked models can be displayed
                if (!this.smartVu.isSupported()) {
                    const viewerUrl = this.sharedService.inputConfig.VIEWER_BASE_URL;
                    Logger.info('SmartVu Model file not supported, redirecting to viewer: ' + viewerUrl);
                    Bugsnag.notify(new Error(`SmartVu® '${this.smartVu.title}' (${this.smartVu.shareCode}) has unsupported model file, will redirect to ${viewerUrl}. ${this.containerUrl()}`), (event) => event.addMetadata('Window', {
                        location: window.location.href,
                        referrer: document.referrer,
                        parent: window.parent.location.href,
                        top: window.top.location.href
                    }));
                    window.location.href = `${viewerUrl}${this.smartVu.shareCode}`;
                }
                // initialize the 3D player with the current SmartVu as well as the render canvas using the PlayerService
                yield this.playerService.displaySmartVu(smartVu, this, this.playerConfig);
                // update state flags
                this._isReady = true;
                this.showSidebar = this.config.enableSidebar && smartVu.hasSidebar();
                this.forceSidebar = this.config.forceSidebar;
                this.webXRAvailable = false;
            }
            catch (error) {
                // if an error occurs during initialization, display a generic error popup using the ErrorService
                Logger.error("displaySmartVu failed", error);
                hadError = true;
                this.stopLoading();
                this._isReady = false;
                Callbacks.didFailToLoadCallback(error);
            }
            finally {
                // ensure the component does not get stuck in loading state
                this.isLoading = false;
                if (!hadError) {
                    this.isPreLoading = true;
                }
            }
        });
    }
    /**
     * Generates a configuration object for the web player based on the configuration GET params passed to the page
     * @returns a VFPlayerConfiguration partial
     */
    getPlayerConfigOverrides() {
        const playerConfig = {};
        if (this.config.backgroundColor) {
            playerConfig.sceneClearColor = this.config.backgroundColor;
        }
        if (this.config.planeColor) {
            playerConfig.groundColor = this.config.planeColor;
        }
        if ('enableEnvironment' in this.config) {
            playerConfig.enableEnvironment = this.config.enableEnvironment;
        }
        if ('enableShadows' in this.config) {
            playerConfig.enableShadows = this.config.enableShadows;
        }
        if ('enableAmbientOcclusion' in this.config) {
            playerConfig.enableAmbientOcclusion = this.config.enableAmbientOcclusion;
        }
        if ('enableDimensioning' in this.config) {
            playerConfig.enableDimensioning = this.config.enableDimensioning;
        }
        if ('skipLightmaps' in this.config) {
            playerConfig.skipLightmaps = this.config.skipLightmaps;
        }
        if ('enableGroundMirror' in this.config) {
            playerConfig.enableGroundMirror = this.config.enableGroundMirror;
        }
        if ('playerFormat' in this.config) {
            playerConfig.playerFormat = this.config.playerFormat;
        }
        if ('playerResolution' in this.config) {
            playerConfig.playerResolution = this.config.playerResolution;
        }
        if ('postProcessExposure' in this.config) {
            playerConfig.postProcessExposure = this.config.postProcessExposure;
        }
        if ('postProcessContrast' in this.config) {
            playerConfig.postProcessContrast = this.config.postProcessContrast;
        }
        if ('antiAliasSamples' in this.config) {
            playerConfig.antiAliasSamples = this.config.antiAliasSamples;
        }
        if ('initialHotspot' in this.config) {
            playerConfig.initialTrigger = this.config.initialHotspot;
        }
        if ('autorotate' in this.config) {
            playerConfig.enableAutoRotate = this.config.autorotate;
        }
        if ('enableDebug' in this.config) {
            playerConfig.enableDebug = this.config.enableDebug;
        }
        if ('enableDebug' in this.config) {
            playerConfig.enableDebug = this.config.enableDebug;
        }
        if ('screenResolution' in this.config) {
            playerConfig.screenResolution = this.config.screenResolution;
        }
        playerConfig.webGlVersion = this.setWebGlVersion();
        this.componentsService.playerConfig = playerConfig;
        return playerConfig;
    }
    get isFullScreen() {
        return this._isFullScreen;
    }
    toggleFullscreen() {
        if (this.isFullScreen) {
            this.closeFullscreen();
        }
        else {
            this.openFullscreen();
        }
    }
    openFullscreen() {
        if (this.elem.requestFullscreen) {
            this.elem.requestFullscreen();
        }
        else if (this.elem.mozRequestFullScreen) {
            /* Firefox */
            this.elem.mozRequestFullScreen();
        }
        else if (this.elem.webkitRequestFullscreen) {
            /* Chrome, Safari and Opera */
            this.elem.webkitRequestFullscreen();
        }
        else if (this.elem.msRequestFullscreen) {
            /* IE/Edge */
            this.elem.msRequestFullscreen();
        }
    }
    /* Close fullscreen */
    closeFullscreen() {
        if (this.document.exitFullscreen) {
            this.document.exitFullscreen();
        }
        else {
            // @ts-ignore
            if (this.document.mozCancelFullScreen) {
                /* Firefox */
                // @ts-ignore
                this.document.mozCancelFullScreen();
            }
            // @ts-ignore
            else if (this.document.webkitExitFullscreen) {
                /* Chrome, Safari and Opera */
                // @ts-ignore
                this.document.webkitExitFullscreen();
            }
            // @ts-ignore
            else if (this.document.msExitFullscreen) {
                /* IE/Edge */
                // @ts-ignore
                this.document.msExitFullscreen();
            }
        }
    }
    fullscreenmodes(event) {
        this.chkScreenMode();
    }
    chkScreenMode() {
        if (document.fullscreenElement) {
            //fullscreen
            this._isFullScreen = true;
        }
        else {
            //not in full screen
            this._isFullScreen = false;
        }
    }
    /**
     * Resets the web players state and loads the requested SmartVu
     */
    ngOnInit() {
        this.smartVuService.isSmartVuOpenedFromAction.subscribe(value => this.isSmartVuOpenedFromAction = value);
        this.initializePlayer();
        window.addEventListener('message', this.onApiCall.bind(this), false);
        window.addEventListener('blur', function () {
            if (!this._didFinishLoading) {
                return;
            }
            if (this.config.autopause) {
                if (this._playerPausedManually) {
                    Logger.debug('event blur -> already manually paused, will not pause again');
                }
                else {
                    Logger.debug('event blur -> pause');
                    this.pausePlayer(false);
                }
            }
        }.bind(this));
        window.addEventListener('focus', function () {
            if (!this._didFinishLoading) {
                return;
            }
            if (this.config.autopause) {
                if (this._playerPausedManually) {
                    Logger.debug('event focus -> manually paused, cannot resume');
                }
                else {
                    Logger.debug('event focus -> resume');
                    this.unpausePlayer(false);
                }
            }
        }.bind(this));
        window.addEventListener('message', function (e) {
            if (e.data.event_id === 'vf.did_show_popup') {
                if (this._playerPausedManually) {
                    Logger.debug('event show popup-> already manually paused, will not pause again');
                }
                else {
                    Logger.debug('event show popup -> pause');
                    this.pausePlayer(false);
                }
            }
            else if (e.data.event_id === 'vf.did_hide_popup') {
                if (this._playerPausedManually) {
                    Logger.debug('event hide popup -> manually paused, cannot resume');
                }
                else {
                    Logger.debug('event hide popup -> resume');
                    this.unpausePlayer(false);
                }
            }
        }.bind(this));
        window.addEventListener('set_language', (e) => {
            const detail = e.detail;
            if (detail.language) {
                this.translationService.language = detail.language;
            }
        });
    }
    ngOnChanges(changes) {
        if (!changes.smartVu.isFirstChange()) {
            this.smartVuService.pushToArray.pipe(take(1)).subscribe(pushToArray => {
                this.triggerSmartVuLoad(pushToArray);
            });
        }
    }
    /**
     * Resets the initial variables and the player. Opens a new SmartVu.
     */
    triggerSmartVuLoad(pushToArray) {
        if (this.player) {
            this.player.dispose();
        }
        if (pushToArray) {
            this.smartVuService.setIsSmartVuOpenedFromAction(true);
        }
        this.initializePlayer(pushToArray);
    }
    inIframe() {
        try {
            return window.self !== window.top;
        }
        catch (e) {
            return true;
        }
    }
    containerUrl() {
        return (window.location != window.parent.location)
            ? document.referrer
            : document.location.href;
    }
    /**
     * Initializes the player, sets configurations etc.
     * @param data Optional parameter which comes from an Action or from the URL.
     */
    initializePlayer(pushToArray) {
        this.share = null;
        this.isPreLoading = true;
        this._isStarted = false;
        this.startedLoading = false;
        const isiFrame = this.inIframe();
        const containerUrl = this.containerUrl();
        Logger.info(`SmartVu® Viewer v${libEnv.version} ${VFConfigManager.enableDebug ? 'DEBUG MODE' : ''} ${isiFrame ? '[iFrame]' : ''}`);
        Logger.info(`Player URL: ${document.location.href}`);
        if (isiFrame) {
            this._iframeLocation = containerUrl;
            Logger.info(`iFrame location: ${this._iframeLocation || 'unknown'}`);
        }
        this.playerConfig = this.getPlayerConfigOverrides();
        this.elem = document.documentElement;
        const player = this.playerService;
        this.playerService.isEgoActive.subscribe(egoActive => {
            this.isEgoActive = egoActive;
        });
        this.playerSubscription = this.playerService.didFinishLoading.subscribe(loadingFinished => {
            if (loadingFinished) {
                this.isPreLoading = false;
                this.isLoading = false;
                this._didFinishLoading = true;
                if (player) {
                    player.unpause();
                }
            }
        });
        if (this.config.autoplay || this.isSmartVuOpenedFromAction) {
            // automatically load and display SmartVu by its share code (defined as an URL param)
            this.loadSmartVu(pushToArray);
        }
        else {
            this.isPreLoading = false;
        }
        setTimeout(function () {
            window.VUFRAME = player;
        }.bind(this));
    }
    ngAfterViewInit() {
        setTimeout(() => {
            if (this.config.backgroundColor) {
                this._backgroundColor = this.config.backgroundColor;
            }
            this.setColors();
        }, 100);
    }
    ngOnDestroy() {
        this.playerSubscription.unsubscribe();
    }
    setWebGlVersion() {
        const gl = document.createElement('canvas').getContext('webgl2');
        if (!gl) {
            return 1;
        }
        else {
            return 2;
        }
    }
    onApiCall(e) {
        if (e.data['method'] != null) {
            Logger.debug(e);
            if (e.data.method === 'vf.activate_trigger') {
                window.VUFRAME.player.activateTrigger(e.data.param || e.data.id);
            }
            else if (e.data.method === 'vf.set_ego_limits') {
                const startAngle = e.data.start_angle;
                const minAngle = e.data.min_angle;
                const maxAngle = e.data.max_angle;
                window.VUFRAME.player.setEgoLimits(startAngle, minAngle, maxAngle);
            }
            else if (e.data.method === 'vf.set_texture') {
                const id = e.data.id;
                const url = e.data.url;
                const alpha = e.data.alpha;
                let alphaNum = -1;
                if (alpha === true || alpha === "true") {
                    alphaNum = 1;
                }
                if (alpha === false || alpha === "false") {
                    alphaNum = 0;
                }
                window.VUFRAME.player.setTextureForId(id, url, alphaNum);
            }
            else if (e.data.method === 'vf.set_material_color') {
                const matId = e.data.id;
                const color = e.data.color;
                window.VUFRAME.player.setColorForMaterial(matId, color);
            }
            else if (e.data.method === 'vf.set_background') {
                const color = e.data.color;
                if (window.VUFRAME.player.setBackgroundColor(color)) {
                    Callbacks.didChangeBackgroundColor();
                    this.backgroundColor = hexColorFromString(color);
                    this.config.backgroundColor = hexColorFromString(color);
                }
            }
            else if (e.data.method === 'vf.show_object') {
                const path = e.data.path;
                window.VUFRAME.player.showObject(path);
            }
            else if (e.data.method === 'vf.hide_object') {
                const path = e.data.path;
                window.VUFRAME.player.hideObject(path);
            }
            else if (e.data.method === 'vf.pause') {
                const ui = e.data.ui;
                this.pausePlayer(true, ui === "true");
            }
            else if (e.data.method === 'vf.unpause') {
                this.unpausePlayer(true);
            }
            else if (e.data.method === 'vf.navigate_back') {
                this.navigateBack();
            }
            else if (e.data.method === 'vf.allow_rotation') {
                const state = (e.data.state === 'true');
                this.allowRotation(state);
            }
            else if (e.data.method === 'vf.ui.list_triggers') {
                const triggers = window.VUFRAME.player.scene.triggers;
                const hash = [];
                for (const [key, trigger] of Object.entries(triggers)) {
                    hash.push({
                        path: window.VUFRAME.player.scene.getPathForNode(trigger.parentNode),
                        type: trigger.triggerMeta.type
                    });
                }
                Callbacks.didListTriggers(hash);
            }
        }
    }
    pausePlayer(manually, withUi) {
        if (manually) {
            this._playerPausedManually = true;
            this._playerPausedManuallyWithUi = withUi;
        }
        if (this.player) {
            if (!this.player.paused) {
                Callbacks.didPause();
            }
            this.player.pause();
        }
    }
    unpausePlayer(manually) {
        if (manually) {
            this._playerPausedManually = false;
        }
        if (this.player) {
            if (this.player.paused) {
                Callbacks.didResume();
            }
            this.player.unpause();
        }
    }
    navigateBack() {
        if (this.playerService) {
            this.playerService.navigateBack();
        }
    }
    allowRotation(state) {
        if (this.playerService) {
            this.playerService.allowRotation(state);
        }
    }
    exitEgoContext() {
        this.playerService.exitEgoContext();
    }
}
WebPlayerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebPlayerComponent, deps: [{ token: VuframeAPIService }, { token: PlayerService }, { token: i1$3.Title }, { token: Document }, { token: Window }, { token: i1.Router }, { token: SmartVuService }, { token: SharedService }, { token: AnalyticsService }, { token: OptionalComponentsService }, { token: PasswordModalService }, { token: i1.ActivatedRoute }, { token: SharedService }, { token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
WebPlayerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: WebPlayerComponent, selector: "vf-web-player", inputs: { shareCode: "shareCode", smartvuToken: "smartvuToken", config: "config", smartVu: "smartVu", _backgroundColor: "_backgroundColor", backgroundColor: "backgroundColor" }, outputs: { emitNgAfterContentInit: "emitNgAfterContentInit" }, host: { listeners: { "document:fullscreenchange": "fullscreenmodes($event)", "document:webkitfullscreenchange": "fullscreenmodes($event)", "document:mozfullscreenchange": "fullscreenmodes($event)", "document:MSFullscreenChange": "fullscreenmodes($event)" } }, providers: [{ provide: Window, useValue: window }, { provide: Document, useValue: document }], viewQueries: [{ propertyName: "canvas", first: true, predicate: ["renderCanvas"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div id=\"webPlayerContainer\" [ngStyle]=\"{ 'background-color': config.backgroundColor }\" [ngClass]=\"playerStyle\">\n\n  <canvas #renderCanvas class=\"noSelect\"></canvas>\n\n  <span #playbutton class=\"play-button\" *ngIf=\"!(config.autoplay || isLoading || isReady)\"\n        (click)=\"loadSmartVu()\">\n    \t\t<mat-icon [style.color]=\"foregroundColor\">play_circle_filled</mat-icon>\n    \t</span>\n\n  <div #preloader class=\"windowCentered\" *ngIf=\"isPreLoading && (!(progress > 0) || !isLoading)\">\n    <mat-spinner diameter=\"60\" strokeWidth=\"3\" [ngClass]=\"spinnerStyle\"></mat-spinner>\n  </div>\n\n\n  <div #loader class=\"windowCentered\" *ngIf=\"isLoading\">\n    <mat-spinner diameter=\"60\" strokeWidth=\"3\" mode=\"indeterminate\" [ngClass]=\"spinnerStyle\"></mat-spinner>\n    <div *ngIf=\"progress > 0 && progress <= 1.0\" class=\"load-progress\"\n         [style.color]=\"foregroundColor\">{{ progress * 100 | number: '1.0-0' }}%\n    </div>\n  </div>\n\n\n  <div class=\"play-button\" *ngIf=\"isReady && didFinishLoading && pausedUi\">\n    <mat-icon class=\"mat-icon-128\" [style.color]=\"playPauseButtonColor\">pause_circle_filled</mat-icon>\n  </div>\n\n\n  <a\n    *ngIf=\"isReady && smartVu?.showBranding && !config.disableWatermark && optionalComponentsService.vuframeWatermark$ | async\"\n    href=\"https://www.vuframe.com\" target=\"_blank\"\n    class=\"watermark\">\n    <img src=\"/assets/img/{{ logo }}\"/>\n  </a>\n\n  <div\n    *ngIf=\"isReady && smartVu?.showBranding && config.disableWatermark && optionalComponentsService.vuframeWatermark$ | async\"\n    class=\"watermark-without-interaction\">\n    <img src=\"/assets/img/{{ logo }}\"/>\n  </div>\n\n  <!--  <span class=\"version-number-brand\" *ngIf=\"(isReady && smartVu?.showBranding)\"-->\n  <!--        [style.color]=\"foregroundColor\">v{{ appVersion }}</span>-->\n\n  <!--  <span class=\"version-number\" *ngIf=\"!(isReady && smartVu?.showBranding)\"-->\n  <!--        [style.color]=\"foregroundColor\">v{{ appVersion }}</span>-->\n\n  <vf-sidebar *ngIf=\"smartVu !== null && isReady && config.enableSidebar\" [smartVu]=\"smartVu\"\n              [foregroundColor]=\"foregroundColor\"\n              [forceSidebar]=\"forceSidebar\" [playerBackgroundColor]=\"this.config.backgroundColor\"></vf-sidebar>\n\n  <vf-widget-host *ngIf=\"smartVu !== null && isReady\" [smartVu]=\"smartVu\"\n                  [actions]=\"smartVu.getUIActions()\"></vf-widget-host>\n\n  <vf-back-button [config]=\"config\"\n                  [foregroundColor]=\"foregroundColor\"\n                  [isEgoActive]=\"isEgoActive\"\n                  [showBackButton]=\"optionalComponentsService.backButton$ | async\"\n                  (exitEgoContext)=\"exitEgoContext()\"></vf-back-button>\n\n  <vf-overlay-ui [style.color]=\"foregroundColor\"\n                 *ngIf=\"(config.autoplay || isLoading || isReady) && optionalComponentsService.overlayUiComponent$ | async\"\n                 [ngClass]=\"playerStyle\"\n                 [didFinishLoading]=\"didFinishLoading\"\n                 [isStarted]=\"isStarted\"\n                 [smartVu]=\"smartVu\"\n                 [player]=\"player\"\n                 [config]=\"config\"\n                 [isFullscreen]=\"isFullScreen\"\n                 [controlsMenu]=\"optionalComponentsService.controlsMenu$ | async\"\n                 (emitToggleFullscreen)=\"toggleFullscreen()\"\n  ></vf-overlay-ui>\n  <vf-navigation-ui *ngIf=\"!config.disableNavigation\" [smartVu]=\"smartVu\"></vf-navigation-ui>\n  <vf-debug-ui [player]=\"player\"\n               *ngIf=\"config.enableDebug\"\n               [config]=\"config\"\n               [isReady]=\"isReady\"\n               [didFinishLoading]=\"didFinishLoading\"\n               [version]=\"version\"></vf-debug-ui>\n\n\n  <div *ngIf=\"isReady && didFinishLoading\" id=\"webPlayerFinishedLoading\" data-testid=\"webPlayerFinishedLoading\"></div>\n\n  <!--\t<a class=\"ego-back-button\" *ngIf=\"isEgoActive || isSmartVuOpenedFromAction && config.disableNavigation == false\" (click)=\"exitEgoContext()\" [style.color]=\"foregroundColor\">-->\n  <!--\t\t<mat-icon>chevron_left</mat-icon>-->\n  <!--\t\t<span>Back</span>-->\n  <!--\t</a>-->\n</div>\n", styles: [".with-ar{left:112px}#webPlayerContainer.sidebar-open canvas{right:210px}#webPlayerContainer .spinner-light::ng-deep circle{stroke:#fff}#webPlayerContainer .spinner-dark::ng-deep circle{stroke:#000}#webPlayerContainer canvas{position:relative;will-change:right;transition:right .3s;height:100%;width:100%;right:0;outline:none}#webPlayerContainer .play-button{opacity:.2;transition:opacity .2s;position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);cursor:pointer}#webPlayerContainer .play-button:hover{opacity:.5}#webPlayerContainer .play-button>*{font-size:10em;width:auto!important;height:auto!important}#webPlayerContainer .ego-back-button{opacity:.2;transition:opacity .2s;position:fixed;display:flex;top:20px;left:20px;font-size:1.2em;align-items:center;cursor:pointer}#webPlayerContainer .ego-back-button:hover{opacity:.5}#webPlayerContainer .watermark{opacity:.2;transition:opacity .2s;position:absolute;bottom:5px;right:5px;opacity:20%}#webPlayerContainer .watermark:hover{opacity:.5}#webPlayerContainer .watermark img{width:100px;display:inline-block}#webPlayerContainer .watermark-without-interaction{position:absolute;bottom:5px;right:5px;opacity:20%}#webPlayerContainer .watermark-without-interaction img{width:100px;display:inline-block}#webPlayerContainer .version-number-brand{pointer-events:none;opacity:20%;font-size:9px;position:fixed;right:115px;bottom:8px}#webPlayerContainer .version-number{pointer-events:none;opacity:20%;font-size:9px;position:fixed;right:5px;bottom:3px}.noSelect{-webkit-tap-highlight-color:transparent;-webkit-touch-callout:none;-webkit-user-select:none;user-select:none}.noSelect:focus{outline:none!important}.img-responsive{max-width:100%}.windowCentered{position:fixed;text-align:center;top:50%;left:50%;transform:translate(-50%,-50%)}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: i3$2.MatSpinner, selector: "mat-spinner", inputs: ["color"] }, { type: SidebarComponent, selector: "vf-sidebar", inputs: ["smartVu", "foregroundColor", "playerBackgroundColor", "forceSidebar"] }, { type: WidgetHostComponent, selector: "vf-widget-host", inputs: ["widgets", "actions", "smartVu"] }, { type: BackButtonComponent, selector: "vf-back-button", inputs: ["config", "foregroundColor", "showBackButton", "isEgoActive"], outputs: ["exitEgoContext"] }, { type: OverlayUiComponent, selector: "vf-overlay-ui", inputs: ["config", "player", "didFinishLoading", "isStarted", "smartVu", "isFullscreen", "controlsMenu"], outputs: ["emitToggleFullscreen"] }, { type: NavigationUiComponent, selector: "vf-navigation-ui", inputs: ["smartVu"] }, { type: DebugUiComponent, selector: "vf-debug-ui", inputs: ["isReady", "didFinishLoading", "player", "version", "config"] }], directives: [{ type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "number": i1$1.DecimalPipe, "async": i1$1.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: WebPlayerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-web-player',
                    templateUrl: './web-player.component.html',
                    styleUrls: ['./web-player.component.scss'],
                    providers: [{ provide: Window, useValue: window }, { provide: Document, useValue: document }]
                }]
        }], ctorParameters: function () { return [{ type: VuframeAPIService }, { type: PlayerService }, { type: i1$3.Title }, { type: Document }, { type: Window }, { type: i1.Router }, { type: SmartVuService }, { type: SharedService }, { type: AnalyticsService }, { type: OptionalComponentsService }, { type: PasswordModalService }, { type: i1.ActivatedRoute }, { type: SharedService }, { type: TranslationService }]; }, propDecorators: { canvas: [{
                type: ViewChild,
                args: ['renderCanvas', { static: true }]
            }], shareCode: [{
                type: Input
            }], smartvuToken: [{
                type: Input
            }], config: [{
                type: Input
            }], smartVu: [{
                type: Input
            }], _backgroundColor: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], emitNgAfterContentInit: [{
                type: Output
            }], fullscreenmodes: [{
                type: HostListener,
                args: ['document:fullscreenchange', ['$event']]
            }, {
                type: HostListener,
                args: ['document:webkitfullscreenchange', ['$event']]
            }, {
                type: HostListener,
                args: ['document:mozfullscreenchange', ['$event']]
            }, {
                type: HostListener,
                args: ['document:MSFullscreenChange', ['$event']]
            }] } });

class MaterialElevationDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.defaultElevation = 4;
        this.raisedElevation = 16;
        this.setElevation(this.defaultElevation);
    }
    ngOnChanges(_changes) {
        this.setElevation(this.defaultElevation);
    }
    onMouseEnter() {
        this.setElevation(this.raisedElevation);
    }
    onMouseLeave() {
        this.setElevation(this.defaultElevation);
    }
    setElevation(amount) {
        // remove all elevation classes
        const classesToRemove = Array.from(this.element.nativeElement.classList).filter(c => c.startsWith('mat-elevation-z'));
        classesToRemove.forEach((c) => {
            this.renderer.removeClass(this.element.nativeElement, c);
        });
        // add the given elevation class
        const newClass = `mat-elevation-z${amount}`;
        this.renderer.addClass(this.element.nativeElement, newClass);
    }
}
MaterialElevationDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MaterialElevationDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
MaterialElevationDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: MaterialElevationDirective, selector: "[appMaterialElevation]", inputs: { defaultElevation: "defaultElevation", raisedElevation: "raisedElevation" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MaterialElevationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appMaterialElevation]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { defaultElevation: [{
                type: Input
            }], raisedElevation: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class SmartvuCardComponent {
    constructor() {
    }
    ngOnInit() {
    }
}
SmartvuCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SmartvuCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SmartvuCardComponent, selector: "vf-smartvu-card", inputs: { previewImageURL: "previewImageURL", title: "title", description: "description" }, ngImport: i0, template: "<div class=\"card-container\">\n    <img [src]=\"previewImageURL\" class=\"img-responsive\" appMaterialElevation>\n    <div class=\"title\"><h5>{{title}}</h5></div>\n</div>\n", styles: [".card-container{position:relative;margin:auto;border-radius:10px;cursor:pointer}.card-container img{border-radius:10px;background-color:#ddd;object-fit:contain}.card-container h5{margin-bottom:0}.card-container .title{position:absolute;left:0px;bottom:0px;background-color:#fff;width:100%;padding:10px;border-bottom-left-radius:10px;border-bottom-right-radius:10px}\n"], directives: [{ type: MaterialElevationDirective, selector: "[appMaterialElevation]", inputs: ["defaultElevation", "raisedElevation"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuCardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-smartvu-card',
                    templateUrl: './smartvu-card.component.html',
                    styleUrls: ['./smartvu-card.component.scss']
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { previewImageURL: [{
                type: Input
            }], title: [{
                type: Input
            }], description: [{
                type: Input
            }] } });

class SearchFilterPipe {
    transform(value, args) {
        if (!value)
            return null;
        if (!args)
            return value;
        args = args.toLowerCase();
        return value.filter((smartVu) => smartVu.title.toLowerCase().includes(args));
    }
}
SearchFilterPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SearchFilterPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SearchFilterPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SearchFilterPipe, name: "searchFilter" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SearchFilterPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'searchFilter'
                }]
        }] });

/**
 * The SmartVu Collection component which is used to display a SmartVu from type - SmartVu Collection.
 */
class SmartvuCollectionComponent {
    constructor(vuframeAPIService, smartVuService) {
        this.vuframeAPIService = vuframeAPIService;
        this.smartVuService = smartVuService;
        /**
         * The logo which we have in the footer
         */
        this._logo = 'logo_title_white.svg';
        /**
         * The current version of the viewer
         */
        this.appVersion = libEnv.version;
        /**
         * Array containing all the SmartVus in the SmartVu Collection
         */
        this.smartVuArray = [];
        /**
         * The color mode which can be 'white' or 'black'. Depends on the background
         */
        this.colorMode = 'white';
        /**
         * Helps for the dynamic color mode set up
         */
        this.buttonClass = "btn btn-outline-light mx-2";
        /**
         * Mapping of the different SmartVu configuration fields
         */
        this.FIELD_MAPPING = {
            smartvu_field_id: "smartviews",
            header_image_field_id: "header_image",
            logo_image_field_id: "logo_image",
            background_color_field_id: "background_color",
            collection_title_field_id: "collection_title"
        };
        /**
         * The classes which we dynamically set
         */
        this.CLASSES = {
            background_image_class: 'smartvu-collection-container bg-image',
            background_color_class: 'smartvu-collection-container',
            button_dark_class: "btn btn-outline-dark mx-2",
            button_light_class: "btn btn-outline-light mx-2"
        };
        /**
         * Dark/white mode depending on the color gama which the user has chosen
         */
        this.STYLE_MODES = {
            white_mode: 'white',
            dark_mode: 'black'
        };
    }
    ngOnInit() {
        this.isLoading = true;
        this.collectionSmartVu = this.smartVu;
        this.pushToSmartVuStack(this.collectionSmartVu);
        this.getCollectionSmartVus();
        this.mappedActions = this.collectionSmartVu.mappedActions;
        this.styles = this.setStyles();
    }
    ngAfterContentInit() {
        this.isReady = true;
        setTimeout(() => this.isLoading = false, 1000);
    }
    /**
     * Method which pushes the Collection SmartVu to the SmartVu stack
     */
    pushToSmartVuStack(smartVu) {
        return this.smartVuService.smartVuObjectsArray.some(el => el.feature_type === smartVu.feature_type) ? false : this.smartVuService.fillSmartVuArray(smartVu);
    }
    /**
     * Opens a dialog with the selected SmartVu
     */
    openSmartVu(smartVu) {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('return_to')) {
            window.location.href = window.location.origin + `/${smartVu.shareCode}?${searchParams.toString()}`;
        }
        else {
            window.location.href = window.location.origin + `/${smartVu.shareCode}?return_to=${this.collectionSmartVu.shareCode}&${searchParams.toString()}`;
        }
    }
    /**
     * Returns the Collection SmartVu header image if one is set in the Studio
     */
    getHeaderImage() {
        var _a;
        const identifier = (_a = this.collectionSmartVu.getImageField(this.FIELD_MAPPING.header_image_field_id).json.value[0]) === null || _a === void 0 ? void 0 : _a.file_token;
        if (identifier) {
            return `url(${this.collectionSmartVu.getItem(identifier).downloadURL})`;
        }
    }
    /**
     * Returns the Collection SmartVu logo image if one is set in the Studio
     */
    getLogoImage() {
        var _a;
        const identifier = (_a = this.collectionSmartVu.getImageField(this.FIELD_MAPPING.logo_image_field_id).json.value[0]) === null || _a === void 0 ? void 0 : _a.file_token;
        if (identifier) {
            return this.collectionSmartVu.getItem(identifier).downloadURL;
        }
    }
    /**
     * Returns the Collection SmartVu background color if one is set in the Studio
     */
    getBackgroundColor() {
        var _a, _b;
        // if no background color is set in the design tab, check the extensions
        return ((_a = this.collectionSmartVu.getColorField(this.FIELD_MAPPING.background_color_field_id).json.value[0]) === null || _a === void 0 ? void 0 : _a.value) || ((_b = this.collectionSmartVu.backgroundExtension) === null || _b === void 0 ? void 0 : _b.json.params.color);
    }
    /**
     * Returns the Collection SmartVu collection title if one is set in the Studio
     */
    getCollectionTitle() {
        var _a;
        return (_a = this.collectionSmartVu.getColorField(this.FIELD_MAPPING.collection_title_field_id).json.value[0]) === null || _a === void 0 ? void 0 : _a.value;
    }
    getCollectionSmartVus() {
        const collectionSmartVus = this.collectionSmartVu.getSmartVuField(this.FIELD_MAPPING.smartvu_field_id).json.value;
        if (collectionSmartVus && collectionSmartVus.length !== 0) {
            for (const item of collectionSmartVus) {
                if (item.value_type === 'share_code') {
                    this.vuframeAPIService.getShare(item.value)
                        .pipe(switchMap((data) => this.vuframeAPIService.getSmartVu(data.targetIdentifier, 'collection_smartvu'))).subscribe((smartVu) => {
                        this.smartVuArray.push(smartVu);
                    });
                }
                else if (item.value_type === 'experience_token') {
                    this.vuframeAPIService.getSmartVu(item.value, 'collection_smartvu')
                        .subscribe((smartVu) => this.smartVuArray.push(smartVu));
                }
            }
            //sort the SmartVus so they can be displayed propertly
            setTimeout(() => {
                this.smartVuArray.sort((a, b) => a.title.localeCompare(b.title));
            }, 750);
        }
    }
    /**
     * Sets the page styles
     */
    setStyles() {
        if (this.getHeaderImage()) {
            this.background = {
                'background-image': this.getHeaderImage()
            };
            return this.CLASSES.background_image_class;
        }
        else if (this.getBackgroundColor()) {
            this.background = { 'background-color': this.getBackgroundColor() };
            colorIsDark(this.getBackgroundColor()) ? this.colorMode = this.STYLE_MODES.white_mode : this.colorMode = this.STYLE_MODES.dark_mode;
            colorIsDark(this.getBackgroundColor()) ? this.buttonClass = this.CLASSES.button_light_class : this.buttonClass = this.CLASSES.button_dark_class;
            colorIsDark(this.getBackgroundColor()) ? this._logo = 'logo_title_white.svg' : this._logo = 'logo_title_dark.svg';
            return this.CLASSES.background_color_class;
        }
        else {
            this.colorMode = this.STYLE_MODES.white_mode;
            this.buttonClass = this.CLASSES.button_light_class;
            this.background = { 'background-color': '#000000' };
            return this.CLASSES.background_color_class;
        }
    }
    triggerAction(action) {
        this.smartVu.dispatchAction(action);
    }
    /**
     * Toggles the search/close button on the search bar
     */
    toggle() {
        this.searchValue = null;
        this.bool = !this.bool;
    }
    get logo() {
        return this._logo;
    }
}
SmartvuCollectionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuCollectionComponent, deps: [{ token: VuframeAPIService }, { token: SmartVuService }], target: i0.ɵɵFactoryTarget.Component });
SmartvuCollectionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SmartvuCollectionComponent, selector: "vf-smartvu-collection", inputs: { smartVu: "smartVu", config: "config" }, ngImport: i0, template: "<div [ngClass]=\"styles\" [ngStyle]=\"background\">\n\n    <div class=\"search-bar p-4 d-flex justify-content-end\">\n        <div *ngIf=\"bool\">\n            <div><input type=\"text\" class=\"search-field px-4 text-center\" [(ngModel)]=\"searchValue\" [ngStyle]=\"{'border-bottom-color': colorMode, 'color': colorMode}\" autofocus></div>\n            <small [style.color]=\"colorMode\">Search a SmartVu\u00AE</small></div>\n        <mat-icon [style.color]=\"colorMode\" class=\"custom-icon-size\" (click)=\"toggle()\" *ngIf=\"!bool\" matTooltip=\"Search\" matTooltipPosition=\"below\" style=\"cursor:pointer;\" id=\"search-btn\">search</mat-icon>\n        <mat-icon [style.color]=\"colorMode\" class=\"custom-icon-size\" (click)=\"toggle()\" *ngIf=\"bool\" matTooltip=\"Close\" matTooltipPosition=\"below\" style=\"cursor:pointer;\">cancel_icon</mat-icon>\n    </div>\n    <div class=\"center mb-4\" *ngIf=\"getLogoImage()\">\n        <img [src]=\"getLogoImage()\">\n    </div>\n    <div class=\"collection-title center mb-4\" *ngIf=\"!getLogoImage()\">\n        <h3 [style.color]=\"colorMode\">{{getCollectionTitle()}}</h3>\n    </div>\n\n    <div class=\"actions-container center mb-4\">\n        <div *ngFor=\"let action of mappedActions | keyvalue; let i = index\">\n            <button type=\"button\" id=\"button-{{i}}\" [ngClass]=\"buttonClass\" (click)=\"triggerAction(action.key)\">{{action['value']['json']['name']}}</button>\n        </div>\n    </div>\n\n\n    <div class=\"container\">\n        <div class=\"row\">\n            <ng-container *ngFor=\"let smartvu of smartVuArray | searchFilter: searchValue\">\n                <div class=\"col-sm-12 col-md-6 col-lg-4 p-4\">\n                    <vf-smartvu-card [previewImageURL]=\"smartvu.previewImageUrl\" [description]=\"smartvu.description\" [title]=\"smartvu.title\" (click)=\"openSmartVu(smartvu)\"></vf-smartvu-card>\n                </div>\n            </ng-container>\n        </div>\n    </div>\n\n    <a href=\"https://www.vuframe.com\" target=\"_blank\" class=\"watermark\">\n        <img src=\"/assets/img/{{ logo }}\" class=\"logo-img\"/>\n    </a>\n\n    <small class=\"version-number-brand\" [style.color]=\"colorMode\">v{{ appVersion }}</small>\n\n</div>\n\n\n<div class=\"spinner_overlay\" *ngIf=\"isLoading\">\n    <div class=\"windowCentered\">\n        <mat-spinner diameter=\"60\" strokeWidth=\"3\" mode=\"indeterminate\" class=\"spinner-dark\"></mat-spinner>\n        <p>Loading ...</p>\n    </div>\n</div>\n<vf-widget-host *ngIf=\"smartVu !== null && isReady\" [smartVu]=\"smartVu\" [actions]=\"smartVu.getUIActions()\"></vf-widget-host>\n<vf-message-action></vf-message-action>\n\n", styles: [".smartvu-collection-container{position:absolute;width:100%;min-height:100vh;height:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch}.smartvu-collection-container .container{margin-bottom:40px}.smartvu-collection-container h3{color:#fff}.smartvu-collection-container img{width:150px;height:150px;object-fit:contain}.smartvu-collection-container .logo-image{width:100%;height:100%;object-fit:fill}.smartvu-collection-container .search-bar{height:96.5px}.smartvu-collection-container .search-field{background:transparent;border:none;border-bottom:1px solid;height:24px;-webkit-appearance:none;border-radius:0}.smartvu-collection-container .watermark{opacity:.2;transition:opacity .2s;position:fixed;bottom:5px;right:5px}.smartvu-collection-container .watermark:hover{opacity:.5}.smartvu-collection-container .watermark img{width:100px;height:auto!important;display:inline-block}.smartvu-collection-container .version-number-brand{opacity:.2;transition:opacity .2s;pointer-events:none;font-size:9px;position:fixed;right:115px;bottom:8px}.smartvu-collection-container .version-number-brand:hover{opacity:.5}.bg-image{background-repeat:no-repeat;background-position:center;background-size:cover;height:100vh;width:100vw;position:fixed}.spinner_overlay{position:fixed;left:0;top:0;width:100%;height:100%;background-color:#fff;z-index:1000}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }, { type: SmartvuCardComponent, selector: "vf-smartvu-card", inputs: ["previewImageURL", "title", "description"] }, { type: i3$2.MatSpinner, selector: "mat-spinner", inputs: ["color"] }, { type: WidgetHostComponent, selector: "vf-widget-host", inputs: ["widgets", "actions", "smartVu"] }, { type: MessageActionComponent, selector: "vf-message-action" }], directives: [{ type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i5.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i5.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "keyvalue": i1$1.KeyValuePipe, "searchFilter": SearchFilterPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuCollectionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-smartvu-collection',
                    templateUrl: './smartvu-collection.component.html',
                    styleUrls: ['./smartvu-collection.component.scss']
                }]
        }], ctorParameters: function () { return [{ type: VuframeAPIService }, { type: SmartVuService }]; }, propDecorators: { smartVu: [{
                type: Input
            }], config: [{
                type: Input
            }] } });

/**
 * A dialog popup displaying an error message provided by the applications ErrorService instance. Automatically opens whenever a new error is passed to the ErrorService.
 */
class ErrorDialogComponent {
    constructor(dialog, errorService, router, zone) {
        this.dialog = dialog;
        this.errorService = errorService;
        this.router = router;
        this.zone = zone;
    }
    /**
     * Opens the error dialog for the current error message
     */
    openDialog() {
        const dialogRef = this.dialog.open(ErrorDialog, {
            autoFocus: false,
            panelClass: 'viewer-popup-dialog',
            data: {
                errorMessage: this.error.message,
                showAppLinkButton: this.showAppLinkButton(),
            },
        });
        // redirect to root page after dialog is closed
        dialogRef.afterClosed().subscribe(result => {
            this.router.navigate(['/']);
        });
    }
    /**
     * Unused: Integrate a deep link to the iOS/Android viewer apps into the error popup
     */
    showAppLinkButton() {
        return ((this.error !== null && this.error instanceof PlayerError) ||
            this.error instanceof UnsupportedTypeError);
    }
    ngOnInit() {
        // subscribe to the current error
        this.errorSubscription = this.errorService.error.subscribe((error) => {
            // show dialog if no other error popups are currently open
            if (error &&
                (!this.dialog.openDialogs ||
                    this.dialog.openDialogs.length === 0)) {
                // use ng-zone to ensure correct UI update
                this.zone.run(() => {
                    this.error = error;
                    this.openDialog();
                });
            }
        });
    }
    ngOnDestroy() {
        this.errorSubscription.unsubscribe();
    }
}
ErrorDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorDialogComponent, deps: [{ token: i1$4.MatDialog }, { token: ErrorService }, { token: i1.Router }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
ErrorDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ErrorDialogComponent, selector: "vf-error-dialog", ngImport: i0, template: '<div></div>', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorDialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-error-dialog',
                    template: '<div></div>',
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialog }, { type: ErrorService }, { type: i1.Router }, { type: i0.NgZone }]; } });
/**
 * The dialog opened by ErrorDialogComponent
 */
class ErrorDialog {
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
    }
}
ErrorDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorDialog, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
ErrorDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ErrorDialog, selector: "vf-error-dialog-content", ngImport: i0, template: "<h2 mat-dialog-title>SmartVu\u00AE could not be loaded</h2>\n<div mat-dialog-content>\n\t<p>{{ data.errorMessage }}</p>\n\t<div mat-dialog-actions>\n\t\t<!--<button\n\t\t\t*ngIf=\"data.showAppLinkButton\"\n\t\t\t[mat-dialog-close]=\"true\"\n\t\t\tmat-raised-button\n\t\t\tcolor=\"accent\"\n\t\t>\n\t\t\t<mat-icon>open_in_new</mat-icon>\n\t\t\tView in Vuframe\u00AE App\n\t\t</button>-->\n\n\t\t<a [mat-dialog-close]=\"false\" class=\"btn btn-primary btn-md rounded-lg\">\n\t\t\tTry again\n\t\t</a>\n\t</div>\n</div>\n", styles: [""], directives: [{ type: i1$4.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i1$4.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i1$4.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }, { type: i1$4.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ErrorDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-error-dialog-content',
                    templateUrl: './error-dialog.component.html',
                    styleUrls: ['./error-dialog.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

class MainComponent extends BaseViewerComponent {
    constructor(apiService, smartVuService, sharedService, passwordModalService, analyticsService, zone, errorService) {
        super(apiService, smartVuService, sharedService, passwordModalService, analyticsService);
        this.apiService = apiService;
        this.smartVuService = smartVuService;
        this.sharedService = sharedService;
        this.passwordModalService = passwordModalService;
        this.analyticsService = analyticsService;
        this.zone = zone;
        this.errorService = errorService;
        super.ngOnInit();
    }
    ngOnInit() {
        this.getSmartVu();
        this.applyBrand();
        // subscribe to the current error
        this.errorSubscription = this.errorService.error.subscribe((error) => {
            // show dialog if no other error popups are currently open
            if (error) {
                // use ng-zone to ensure correct UI update
                this.zone.run(() => {
                    this.error = error;
                    this.player.isLoading = false;
                    this.player.isPreLoading = false;
                });
            }
        });
    }
    ngAfterContentInit() {
        setTimeout(() => {
            if (this.player) {
                this.setStyle(this.player);
            }
        }, 100);
    }
    ngOnDestroy() {
        this.errorSubscription.unsubscribe();
    }
}
MainComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MainComponent, deps: [{ token: VuframeAPIService }, { token: SmartVuService }, { token: SharedService }, { token: PasswordModalService }, { token: AnalyticsService }, { token: i0.NgZone }, { token: ErrorService }], target: i0.ɵɵFactoryTarget.Component });
MainComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: MainComponent, selector: "lib-main", inputs: { overlayUIComponent: "overlayUIComponent" }, viewQueries: [{ propertyName: "player", first: true, predicate: ["player"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"smartVu && smartVu.featureType !== 'smartview_collection'\">\n  <vf-web-player #player\n                 [shareCode]=\"shareCode\"\n                 [config]=\"config\"\n                 [smartVu]=\"smartVu\"\n                 [ngClass]=\"playerStyle\"\n                 (emitNgAfterContentInit)=\"ngAfterContentInit()\">\n  </vf-web-player>\n</div>\n\n<vf-smartvu-collection *ngIf=\"smartVu && smartVu.featureType === 'smartview_collection'\" [smartVu]=\"smartVu\"\n                       [config]=\"config\"></vf-smartvu-collection>\n<vf-error-dialog></vf-error-dialog>\n", styles: [""], components: [{ type: WebPlayerComponent, selector: "vf-web-player", inputs: ["shareCode", "smartvuToken", "config", "smartVu", "_backgroundColor", "backgroundColor"], outputs: ["emitNgAfterContentInit"] }, { type: SmartvuCollectionComponent, selector: "vf-smartvu-collection", inputs: ["smartVu", "config"] }, { type: ErrorDialogComponent, selector: "vf-error-dialog" }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: MainComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'lib-main',
                    templateUrl: './main.component.html',
                    styleUrls: ['./main.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: VuframeAPIService }, { type: SmartVuService }, { type: SharedService }, { type: PasswordModalService }, { type: AnalyticsService }, { type: i0.NgZone }, { type: ErrorService }]; }, propDecorators: { overlayUIComponent: [{
                type: Input
            }], player: [{
                type: ViewChild,
                args: ['player']
            }] } });

class VfWebviewerComponent {
    constructor(optionalComponentsService, sharedService, analyticsService) {
        this.optionalComponentsService = optionalComponentsService;
        this.sharedService = sharedService;
        this.analyticsService = analyticsService;
    }
    set collectionSmartVus(collectionSmartVus) {
        if (collectionSmartVus) {
            this.sharedService.collectionSmartVus = collectionSmartVus;
        }
    }
    set smartVuInfo(smartVuInfo) {
        if (smartVuInfo) {
            this.sharedService.smartVuInfo = smartVuInfo;
        }
    }
    set stage(value) {
        this.optionalComponentsService.stage = value;
    }
    set overlayUIComponent(value) {
        this.optionalComponentsService.overlayUiComponent = value;
    }
    ;
    set vuframeWatermark(value) {
        this.optionalComponentsService.vuframeWatermark = value;
    }
    ;
    set controlsMenu(value) {
        this.optionalComponentsService.controlsMenu = value;
    }
    ;
    set backButton(value) {
        this.optionalComponentsService.backButton = value;
    }
    ;
    set inputConfig(value) {
        this.sharedService.inputConfig = value;
        this.analyticsService.registerKeenClient(value.KEEN_PROJECT_ID, value.KEEN_WRITE_KEY);
    }
    set viewerMode(value) {
        switch (true) {
            case value === VFViewerMode.Embed:
                BaseViewerConfig.viewerMode = VFViewerMode.Embed;
                break;
            case value === VFViewerMode.Standalone:
                BaseViewerConfig.viewerMode = VFViewerMode.Standalone;
                break;
        }
    }
    ngOnInit() {
    }
}
VfWebviewerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerComponent, deps: [{ token: OptionalComponentsService }, { token: SharedService }, { token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Component });
VfWebviewerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: VfWebviewerComponent, selector: "vf-webviewer", inputs: { collectionSmartVus: "collectionSmartVus", smartVuInfo: "smartVuInfo", stage: "stage", overlayUIComponent: "overlayUIComponent", vuframeWatermark: "vuframeWatermark", controlsMenu: "controlsMenu", backButton: "backButton", inputConfig: "inputConfig", viewerMode: "viewerMode" }, ngImport: i0, template: "<vf-global-styling></vf-global-styling>\n<lib-main></lib-main>\n\n", styles: [""], components: [{ type: GlobalStylingComponent, selector: "vf-global-styling" }, { type: MainComponent, selector: "lib-main", inputs: ["overlayUIComponent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-webviewer',
                    templateUrl: './vf-webviewer.component.html',
                    styleUrls: ['./vf-webviewer.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: OptionalComponentsService }, { type: SharedService }, { type: AnalyticsService }]; }, propDecorators: { collectionSmartVus: [{
                type: Input
            }], smartVuInfo: [{
                type: Input
            }], stage: [{
                type: Input
            }], overlayUIComponent: [{
                type: Input
            }], vuframeWatermark: [{
                type: Input
            }], controlsMenu: [{
                type: Input
            }], backButton: [{
                type: Input
            }], inputConfig: [{
                type: Input
            }], viewerMode: [{
                type: Input
            }] } });

class AngularMaterialModule {
}
AngularMaterialModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AngularMaterialModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AngularMaterialModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AngularMaterialModule, exports: [MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule] });
AngularMaterialModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AngularMaterialModule, imports: [MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: AngularMaterialModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        MatDialogModule,
                        MatIconModule,
                        MatProgressSpinnerModule,
                        MatSnackBarModule,
                        MatFormFieldModule,
                        MatInputModule,
                        MatButtonModule
                    ],
                }]
        }] });

/**
 * Angular Module containing all different types of SmartVu Element
 */
class SmartVuElementsModule {
}
SmartVuElementsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuElementsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartVuElementsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuElementsModule, declarations: [TextWidgetComponent,
        TitleWidgetComponent,
        DescriptionWidgetComponent,
        HashtagWidgetComponent,
        PreviewImageWidgetComponent,
        ProductLogoWidgetComponent,
        BulletPointsWidgetComponent,
        KeyValueWidgetComponent,
        ActionButtonWidgetComponent,
        CaptionWidgetComponent,
        ProductFactsWidgetComponent,
        ProductPropertiesWidgetComponent,
        ProductUrlWidgetComponent,
        ProductSkuWidgetComponent,
        PriceWidgetComponent,
        MediaWidgetComponent,
        ImagePopupActionComponent,
        PopupDialog,
        ImagePopupDialog,
        PopupActionComponent,
        PdfPopupDialog,
        PdfPopupActionComponent,
        VideoPopupActionComponent,
        VideoPopupDialog,
        SafePipe,
        WebsitePopupActionComponent,
        WebsitePopupDialog,
        PlayAudioActionComponent,
        PlayAudioPopupDialog,
        MessageActionComponent,
        MessageActionDialogComponent,
        ToastMessageComponent], imports: [CommonModule, AngularMaterialModule, MatIconModule, MatButtonModule, PdfJsViewerModule, MatProgressSpinnerModule, MatGridListModule, MatListModule, MatTooltipModule], exports: [MessageActionComponent] });
SmartVuElementsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuElementsModule, imports: [[CommonModule, AngularMaterialModule, MatIconModule, MatButtonModule, PdfJsViewerModule, MatProgressSpinnerModule, MatGridListModule, MatListModule, MatTooltipModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartVuElementsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TextWidgetComponent,
                        TitleWidgetComponent,
                        DescriptionWidgetComponent,
                        HashtagWidgetComponent,
                        PreviewImageWidgetComponent,
                        ProductLogoWidgetComponent,
                        BulletPointsWidgetComponent,
                        KeyValueWidgetComponent,
                        ActionButtonWidgetComponent,
                        CaptionWidgetComponent,
                        ProductFactsWidgetComponent,
                        ProductPropertiesWidgetComponent,
                        ProductUrlWidgetComponent,
                        ProductSkuWidgetComponent,
                        PriceWidgetComponent,
                        MediaWidgetComponent,
                        ImagePopupActionComponent,
                        PopupDialog,
                        ImagePopupDialog,
                        PopupActionComponent,
                        PdfPopupDialog,
                        PdfPopupActionComponent,
                        VideoPopupActionComponent,
                        VideoPopupDialog,
                        SafePipe,
                        WebsitePopupActionComponent,
                        WebsitePopupDialog,
                        PlayAudioActionComponent,
                        PlayAudioPopupDialog,
                        MessageActionComponent,
                        MessageActionDialogComponent,
                        ToastMessageComponent
                    ],
                    imports: [CommonModule, AngularMaterialModule, MatIconModule, MatButtonModule, PdfJsViewerModule, MatProgressSpinnerModule, MatGridListModule, MatListModule, MatTooltipModule],
                    entryComponents: [
                        TextWidgetComponent,
                        TitleWidgetComponent,
                        DescriptionWidgetComponent,
                        HashtagWidgetComponent,
                        PreviewImageWidgetComponent,
                        ProductLogoWidgetComponent,
                        BulletPointsWidgetComponent,
                        KeyValueWidgetComponent,
                    ],
                    exports: [MessageActionComponent],
                }]
        }] });

class SmartvuPopupDialogComponent {
    constructor(dialogRef, data, smartVuService) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.smartVuService = smartVuService;
    }
    onViewClick() {
        this.smartVuService.setTriggerLoadSmartVuEvent(this.data.identifier);
    }
}
SmartvuPopupDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuPopupDialogComponent, deps: [{ token: i1$4.MatDialogRef }, { token: MAT_DIALOG_DATA }, { token: SmartVuService }], target: i0.ɵɵFactoryTarget.Component });
SmartvuPopupDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SmartvuPopupDialogComponent, selector: "vf-smartvu-popup-dialog", ngImport: i0, template: "<div class=\"card-container\">\n    <img [src]=\"data.previewImageURL\">\n    <div>\n        <mat-icon class=\"close-dialog m-2\" matTooltip=\"Close\" matTooltipPosition=\"below\" mat-dialog-close>cancel_icon</mat-icon>\n    </div>\n    <div class=\"p-4\">\n        <div class=\"title center\"><h5>{{data.title}}</h5></div>\n        <div class=\"description\" [innerHTML]=\"data.description\"></div>\n        <button class=\"btn btn-primary btn-md rounded-lg mt-3\" id=\"view\" (click)=\"onViewClick()\" mat-dialog-close style=\"width: 100%\">View</button>\n    </div>\n\n</div>\n", styles: [".card-container{width:100%;position:relative;overflow:hidden}.card-container img{border-top-left-radius:10px;border-top-right-radius:10px;max-width:100%;max-height:100%;background-color:#ddd}.card-container h5{margin-bottom:0}.card-container .title{background-color:#fff;width:100%;padding:10px;border-bottom-left-radius:10px;border-bottom-right-radius:10px}.card-container .description{max-height:300px;overflow:auto}.card-container .close-dialog{top:0px;right:0px;position:absolute;cursor:pointer}.card-container .close-dialog:hover{opacity:.6}@media only screen and (max-height: 768px){.card-container .description{max-height:125px}}\n"], components: [{ type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "fontSet", "fontIcon", "svgIcon"], exportAs: ["matIcon"] }], directives: [{ type: i1$4.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SmartvuPopupDialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-smartvu-popup-dialog',
                    templateUrl: './smartvu-popup-dialog.component.html',
                    styleUrls: ['./smartvu-popup-dialog.component.scss']
                }]
        }], ctorParameters: function () { return [{ type: i1$4.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: SmartVuService }]; } });

/**
 * HTTP interceptor class for automatic error handling for HTTP requests (e.g. for API communication).
 */
class InterceptorService {
    constructor() { }
    /**
     * Provides error handling in case of an HTTP error
     * @error the HTTP error object
     */
    handleError(error) {
        if (error.error instanceof ErrorEvent) {
            //Bugsnag.notify(error);
            // connection or server errors
            Logger.error(`an error occurred while communicating with the vuframe api: ${error.error.message}`);
        }
        else {
            // logical errors such as 404, failed authentication
            //Bugsnag.notify(error);
            Logger.error(`request failed with status code ${error.status}:`, error.error);
        }
        // re-throw the error object for further error handling
        return throwError(new Error("SmartVu® does not exist or is not shared."));
    }
    /**
     * Registers handleError as an error handler in case of HTTP errors
     */
    intercept(req, next) {
        return next.handle(req).pipe(catchError(this.handleError));
    }
}
InterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: InterceptorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
InterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: InterceptorService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: InterceptorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Input field for entering a Vuframe Share code that automatically validates and debounces input.
 */
class ShareCodeInputComponent {
    /**
     * Constructs a new ShareCodeInputComponent
     * @param el: reference to the components HTML input element
     */
    constructor(el, sharedService) {
        this.el = el;
        this.sharedService = sharedService;
        /**
         * Event emitter that is triggered if a valid share code is entered
         */
        this.shareCode = new EventEmitter();
    }
    ngOnInit() {
        // set key event listener
        fromEvent(this.el.nativeElement, 'keyup')
            .pipe(
        // extract share code from input
        map((e) => this.extractShareCode(e.target.value.trim())), 
        // add debounce to prevent unnecessary events
        debounceTime(250), 
        // validate share code
        filter((code) => Share.IsValidShareCode(code.toLowerCase())))
            .subscribe((code) => {
            // notify other components
            this.shareCode.emit(code.toLowerCase());
        });
    }
    /**
     * Extracts share code from an input string. This allows inputs such as https://share.vuframe.com/<share code>
     * @param inputString the input string
     * @returns the share code string
     */
    extractShareCode(inputString) {
        return inputString.replace(this.sharedService.inputConfig.VIEWER_BASE_URL, '');
    }
}
ShareCodeInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ShareCodeInputComponent, deps: [{ token: i0.ElementRef }, { token: SharedService }], target: i0.ɵɵFactoryTarget.Component });
ShareCodeInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ShareCodeInputComponent, selector: "vf-share-code-input", outputs: { shareCode: "shareCode" }, ngImport: i0, template: "<div class=\"share-code-input-container center\">\n  <mat-form-field>\n    <input\n      matInput\n      class=\"share-code-input\"\n      placeholder=\"Enter a share code\"\n      id=\"share-code-input\"\n    />\n  </mat-form-field>\n</div>\n", styles: [".share-code-input{display:inline-block;text-align:center;color:#363c47;width:100%;text-transform:uppercase;letter-spacing:2pt;font-size:150%}.share-code-input-container{color:#363c47;margin-top:20px;margin-bottom:20px}.share-code-input-container>*{width:90%}\n"], components: [{ type: i4$1.MatFormField, selector: "mat-form-field", inputs: ["color", "floatLabel", "appearance", "hideRequiredMarker", "hintLabel"], exportAs: ["matFormField"] }], directives: [{ type: i6.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["id", "disabled", "required", "type", "value", "readonly", "placeholder", "errorStateMatcher"], exportAs: ["matInput"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ShareCodeInputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-share-code-input',
                    templateUrl: './share-code-input.component.html',
                    styleUrls: ['./share-code-input.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: SharedService }]; }, propDecorators: { shareCode: [{
                type: Output
            }] } });

/**
 * Displays preview image, title, etc. of a Share object and adds an open-button for viewing the Share's target SmartVu
 */
class SharePreviewComponent {
    constructor() {
        /**
         * Event emitter that is triggered if the user tries to open the share using the open-button
         */
        this.openShare = new EventEmitter();
    }
    ngOnInit() { }
    /**
     * Callback method that is triggered if the user clicks the open button
     */
    onOpenButtonClicked() {
        this.openShare.emit();
    }
}
SharePreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SharePreviewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SharePreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SharePreviewComponent, selector: "vf-share-preview", inputs: { share: "share" }, outputs: { openShare: "openShare" }, ngImport: i0, template: "<div *ngIf=\"share\">\n\t<h3>{{ share.title }}</h3>\n\t<img src=\"{{ share.previewURL }}\" />\n\t<p>\n\t\t<small innerHTML=\"{{ share.description }}\"></small>\n\t</p>\n\t<button mat-raised-button color=\"primary\" (click)=\"onOpenButtonClicked()\">\n\t\tView\n\t</button>\n</div>\n", styles: ["img{width:250px;height:250px;border-radius:5px}button{width:100%;padding:5px;font-size:16px}\n"], components: [{ type: i1$2.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SharePreviewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-share-preview',
                    templateUrl: './share-preview.component.html',
                    styleUrls: ['./share-preview.component.scss'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { share: [{
                type: Input
            }], openShare: [{
                type: Output
            }] } });

/**
 * Simple footer bar showing the current app version and copyright information
 */
class FooterBarComponent {
    constructor() {
        this.appVersion = libEnv.version;
        this.currentYear = new Date().getFullYear();
    }
    ngOnInit() { }
}
FooterBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FooterBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FooterBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FooterBarComponent, selector: "vf-footer-bar", ngImport: i0, template: "<span class=\"left\"> Copyright \u00A9 {{ currentYear }} Vuframe </span>\n\n<!--<span class=\"right\"> v{{ appVersion }} </span>-->\n", styles: [":host{position:fixed;width:100%;bottom:0;left:0;padding:15px 20px;opacity:.7;color:#666;font-size:.9em}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FooterBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-footer-bar',
                    templateUrl: './footer-bar.component.html',
                    styleUrls: ['./footer-bar.component.scss'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Base component for an UI allowing the user to enter a Vuframe Share Code
 */
class ShareCodeFormComponent {
    /**
     * Constructs a new ShareCodeFormComponent
     * @param router Angular Router object used for redirects within the app
     * @param apiService VuframeAPIService instance provided via dependency injection
     * @param errorService ErrorService instance provided via dependency injection
     * @param zone NgZone instance for enforcing UI update
     */
    constructor(router, apiService, errorService, zone, passwordModalService, sharedService) {
        this.router = router;
        this.apiService = apiService;
        this.errorService = errorService;
        this.zone = zone;
        this.passwordModalService = passwordModalService;
        this.sharedService = sharedService;
    }
    set inputConfig(value) {
        this.sharedService.inputConfig = value;
    }
    ngOnInit() {
        this.share = null;
        this.isLoading = false;
        // set up error subscription
        this.errorSubscription = this.errorService.error.subscribe((error) => {
            this.zone.run(() => {
                if (error) {
                    // display error message if an error is thrown
                    this.share = null;
                    this.isLoading = false;
                    this.errorMessage = error.message;
                }
                else {
                    this.errorMessage = null;
                }
            });
        });
    }
    ngOnDestroy() {
        this.errorSubscription.unsubscribe();
    }
    /**
     * Attempts to load a Share object by a given share code
     * @param code the share code of the share that should be loaded
     */
    loadShare(code) {
        // clear error before new load
        this.errorService.clear();
        this.isLoading = true;
        // set share attribute if api request succeeds
        this.apiService.getShare(code).subscribe(share => {
            this.share = share;
            if (share.targetPassword != null) {
                //check if the SmartVu is password protected and ask for the password
                this.router.navigate([`${this.share.shareCode}`], { queryParams: { version: libEnv.version } });
            }
            else {
                this.isLoading = false;
            }
        });
    }
    /**
     * Opens the 3D view for the current share
     */
    openShare() {
        this.router.navigate([`${this.share.shareCode}`]);
    }
}
ShareCodeFormComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ShareCodeFormComponent, deps: [{ token: i1.Router }, { token: VuframeAPIService }, { token: ErrorService }, { token: i0.NgZone }, { token: PasswordModalService }, { token: SharedService }], target: i0.ɵɵFactoryTarget.Component });
ShareCodeFormComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: ShareCodeFormComponent, selector: "vf-share-code-form", inputs: { inputConfig: "inputConfig" }, ngImport: i0, template: "<div class=\"page-container homepage\">\n\t<form class=\"page-content\">\n\t\t<img src=\"/assets/img/logo.png\" class=\"img-fluid\"/>\n\n\t\t<vf-share-code-input\n\t\t\t(shareCode)=\"loadShare($event)\"\n\t\t></vf-share-code-input>\n\n\t\t<div *ngIf=\"!isLoading\">\n\t\t\t<vf-share-preview\n\t\t\t\t[share]=\"share\"\n\t\t\t\t(openShare)=\"openShare()\"\n\t\t\t></vf-share-preview>\n\t\t</div>\n\t\t<div *ngIf=\"isLoading\" class=\"center\">\n\t\t\t<mat-spinner diameter=\"60\" strokeWidth=\"3\"></mat-spinner>\n\t\t</div>\n\t\t<span class=\"error\" *ngIf=\"errorMessage\">{{ errorMessage }}</span>\n\t</form>\n\t<vf-footer-bar></vf-footer-bar>\n</div>\n<vf-global-styling></vf-global-styling>\n", styles: [""], components: [{ type: ShareCodeInputComponent, selector: "vf-share-code-input", outputs: ["shareCode"] }, { type: SharePreviewComponent, selector: "vf-share-preview", inputs: ["share"], outputs: ["openShare"] }, { type: i3$2.MatSpinner, selector: "mat-spinner", inputs: ["color"] }, { type: FooterBarComponent, selector: "vf-footer-bar" }, { type: GlobalStylingComponent, selector: "vf-global-styling" }], directives: [{ type: i5.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i5.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i5.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ShareCodeFormComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vf-share-code-form',
                    templateUrl: './share-code-form.component.html',
                    styleUrls: ['./share-code-form.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: VuframeAPIService }, { type: ErrorService }, { type: i0.NgZone }, { type: PasswordModalService }, { type: SharedService }]; }, propDecorators: { inputConfig: [{
                type: Input
            }] } });

class GlobalStylingModule {
}
GlobalStylingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GlobalStylingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingModule, declarations: [GlobalStylingComponent], exports: [GlobalStylingComponent] });
GlobalStylingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GlobalStylingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        GlobalStylingComponent
                    ],
                    imports: [],
                    exports: [GlobalStylingComponent]
                }]
        }] });

class EmbedFormModule {
}
EmbedFormModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: EmbedFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EmbedFormModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: EmbedFormModule, declarations: [ShareCodeFormComponent,
        ShareCodeInputComponent,
        SharePreviewComponent,
        FooterBarComponent], imports: [CommonModule, FormsModule, AngularMaterialModule, GlobalStylingModule], exports: [ShareCodeFormComponent] });
EmbedFormModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: EmbedFormModule, imports: [[CommonModule, FormsModule, AngularMaterialModule, GlobalStylingModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: EmbedFormModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ShareCodeFormComponent,
                        ShareCodeInputComponent,
                        SharePreviewComponent,
                        FooterBarComponent
                    ],
                    imports: [CommonModule, FormsModule, AngularMaterialModule, GlobalStylingModule],
                    exports: [ShareCodeFormComponent]
                }]
        }] });

class PasswordModalModule {
}
PasswordModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PasswordModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalModule, declarations: [PasswordModalComponent], imports: [CommonModule,
        MatInputModule,
        MatFormFieldModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        FontAwesomeModule,
        ReactiveFormsModule] });
PasswordModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalModule, imports: [[
            CommonModule,
            MatInputModule,
            MatFormFieldModule,
            FormsModule,
            MatDialogModule,
            MatButtonModule,
            MatIconModule,
            FontAwesomeModule,
            ReactiveFormsModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PasswordModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PasswordModalComponent],
                    imports: [
                        CommonModule,
                        MatInputModule,
                        MatFormFieldModule,
                        FormsModule,
                        MatDialogModule,
                        MatButtonModule,
                        MatIconModule,
                        FontAwesomeModule,
                        ReactiveFormsModule
                    ]
                }]
        }] });

class VfWebviewerModule {
}
VfWebviewerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
VfWebviewerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerModule, declarations: [VfWebviewerComponent,
        WebPlayerComponent,
        BaseViewerComponent,
        SidebarComponent,
        ComponentHostDirective,
        WidgetHostComponent,
        BackButtonComponent,
        SmartvuCollectionComponent,
        SmartvuPopupDialogComponent,
        SmartvuCardComponent,
        SearchFilterPipe,
        MaterialElevationDirective,
        OverlayUiComponent,
        NavigationUiComponent,
        ErrorDialog,
        ErrorDialogComponent,
        DebugUiComponent,
        MainComponent], imports: [CommonModule,
        AngularMaterialModule,
        HttpClientModule,
        SmartVuElementsModule,
        PdfJsViewerModule,
        FormsModule,
        NgbModule,
        EmbedFormModule,
        GlobalStylingModule,
        PasswordModalModule], exports: [VfWebviewerComponent,
        BaseViewerComponent,
        SidebarComponent] });
VfWebviewerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerModule, providers: [
        VuframeAPIService,
        ErrorService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InterceptorService,
            multi: true,
        }
    ], imports: [[
            CommonModule,
            AngularMaterialModule,
            HttpClientModule,
            SmartVuElementsModule,
            PdfJsViewerModule,
            FormsModule,
            NgbModule,
            EmbedFormModule,
            GlobalStylingModule,
            PasswordModalModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: VfWebviewerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        VfWebviewerComponent,
                        WebPlayerComponent,
                        BaseViewerComponent,
                        SidebarComponent,
                        ComponentHostDirective,
                        WidgetHostComponent,
                        BackButtonComponent,
                        SmartvuCollectionComponent,
                        SmartvuPopupDialogComponent,
                        SmartvuCardComponent,
                        SearchFilterPipe,
                        MaterialElevationDirective,
                        OverlayUiComponent,
                        NavigationUiComponent,
                        ErrorDialog,
                        ErrorDialogComponent,
                        DebugUiComponent,
                        MainComponent,
                    ],
                    imports: [
                        CommonModule,
                        AngularMaterialModule,
                        HttpClientModule,
                        SmartVuElementsModule,
                        PdfJsViewerModule,
                        FormsModule,
                        NgbModule,
                        EmbedFormModule,
                        GlobalStylingModule,
                        PasswordModalModule
                    ],
                    exports: [
                        VfWebviewerComponent,
                        BaseViewerComponent,
                        SidebarComponent,
                    ],
                    providers: [
                        VuframeAPIService,
                        ErrorService,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: InterceptorService,
                            multi: true,
                        }
                    ],
                    entryComponents: [ErrorDialog]
                }]
        }] });

/*
 * Public API Surface of vf-webviewer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseViewerComponent, EmbedFormModule, MessageActionComponent, MessageActionDialogComponent, ShareCodeFormComponent, SharedService, SidebarComponent, SmartVuElementsModule, ToastMessageComponent, VfWebviewerComponent, VfWebviewerModule, addURLParams, colorIsDark, detectSafari, getVuframeAuthHash, hexColorFromString, useCloudFrontCDN, version };
